// Import react elements
import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import strich api calls
import {
    getPodDesignProducts,
    getPodDesignProductImages,
    updatePodDesignProducts,
    updatePodDesignProductImages,
    refreshPodDesignProductMockups,
    updatePodDesignProductRoles,
    updatePodDesignProductTranslations,
    calculateOrderEarnings
} from "api/APIStrichCalls";

// Import components
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";
import ToggleSwitch from "global/components/ToggleSwitch";
import { PodProductPreview } from "global/components/PodProductPreview";
import CreateButton from "global/components/CreateButton";
import RichTextInput from "global/components/RichTextInput";
import KeywordTagsInput from "global/components/KeywordTagsInput";
import OpenStoreButton from "global/components/OpenStoreButton";
import ImageCarouselPopup from "global/components/ImageCarouselPopup";
import ImageManager from "../../images/components/ImageManager";
import ProductPriceEditor, {
    buildEarningsPreviewOrderItem,
    getAvailableShippingCountries,
    getPriceConfigFromProductPrices,
    getShippingMethodsForCountry,
    getStoreDefaultCurrency,
} from "./ProductPriceEditor";
import ProductColourwaySelector from "./ProductColourwaySelector";
import ProductSamplePopup from "./ProductSamplePopup";
import { InfoButton } from "global/components/InfoButton";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import hooks
import { UseParentProductFieldUpdater, UseChildProductFieldUpdater } from "../hooks/useProductUpdater";
import { useToast } from "global/hooks/useToast";
import { useCart } from "global/hooks/useCart";
import useIsMobile from "global/hooks/useIsMobile";

// Import the editor
import { ImagePlacementEditor } from "global/components/ImagePlacementEditor/components/ImagePlacementEditor";

// Import utilities
import { S2B, B2S } from "global/utilities/BooleanConverters";
import {
    deepEqual,
    stableStringify
} from "global/utilities/CheckData";
import { ReplacePlaceholdersInObject } from "../utilities/ReplacePlaceholders"
import {
    getAvailableProductColourways,
    getEffectiveProductColourwayCodes,
    normaliseColourwayCodes
} from "../utilities/PodProductColourwayHelpers";
import {
    getAllMockupConfigurations,
    getAvailableMockupConfigurationsForConfiguredRoles,
    getCanonicalProductMockups,
    getFirstConfiguredProductPreviewView,
    getMockupConfigurationKey,
    normaliseProductViewAspects,
    normaliseRoleViewSettings,
    removeIncompatibleMockupImages
} from "../utilities/PodProductViewSettingsHelpers";

// Import icons
import {
    CloseIcon,
    MoveArrows,
    WarningIcon
} from "global/strich_icons/StrichIcons"


const VIEW_ASPECT_OPTIONS = [
    { value: "S", label: "Square" },
    { value: "T", label: "Tall" },
    { value: "W", label: "Wide" },
];

const VIEW_ROTATION_OPTIONS = [
    { value: 0, label: "0°" },
    { value: 90, label: "90°" },
    { value: 180, label: "180°" },
    { value: 270, label: "270°" },
];

// Check whether a design currently gets its products from one or more templates
const designUsesTemplates = (podDesignDetails) => {

    return Array.isArray(podDesignDetails?.template_ids) && podDesignDetails.template_ids.length > 0;

};


// Get the worst active PPI state on one configured parent or child product
const getConfiguredProductPrintQualityStatus = (configuredProduct) => {

    // Inactive products do not currently contribute a PPI warning
    if (!configuredProduct || configuredProduct?.active === "N") { return ""; }

    // Read configured print areas safely
    const configuredRoles = Array.isArray(configuredProduct?.roles)
        ? configuredProduct.roles
        : [];

    // Start with no warning
    let printQualityStatus = "";

    // Check each active print area
    for (const configuredRole of configuredRoles) {

        // Ignore disabled print areas
        if (configuredRole?.active === "N") { continue; }

        // Read the calculated status
        const roleStatus =
            String(configuredRole?.print_quality?.status || "").toLowerCase();

        // Failure is the worst possible state
        if (roleStatus === "fail") {
            return "fail";
        }

        // Keep a warning unless a later role fails
        if (roleStatus === "warning") {
            printQualityStatus = "warning";
        }

    }

    return printQualityStatus;

};

const normalisePreviewString = (value) => {

    // Return an empty string for nullish values
    if (value === null || value === undefined) {

        return "";

    }

    // Return the trimmed string value
    return String(value).trim();

};

// Check whether a design role currently contains usable image data
const designRoleHasUsableImageData = (designRole) => {

    // Missing design roles cannot produce live product previews
    if (!designRole || typeof designRole !== "object") {
        return false;
    }

    // Accept every image-data shape currently used by the design editor
    return Boolean(
        designRole.image_url ||
        designRole.thumbnail_url ||
        designRole.uploadedImage ||
        designRole.imageDetails?.image_url ||
        designRole.asset_id ||
        designRole.image_id
    );

};

const getPreviewViewStyle = (view) => {

    // Return the normalised view style
    return normalisePreviewString(view?.style);

};



const getPreviewViewName = (view) => {

    // Return the normalised view name
    return normalisePreviewString(view?.name);

};



const getPreviewViewPriority = (view) => {

    // Put explicit reference views first
    if (normalisePreviewString(view?.reference).toUpperCase() === "Y") {

        return 0;

    }

    // Put default views next
    if (getPreviewViewStyle(view).toLowerCase() === "default") {

        return 1;

    }

    // Put everything else after that
    return 2;

};




const getPreviewViewRotation = (view) => {

    // Convert the view rotation to a number
    const rotation = Number(view?.rotation ?? view?.rotate ?? 0);

    // Return zero if the rotation is invalid
    if (!Number.isFinite(rotation)) {

        return 0;

    }

    // Normalise into a positive 0-359 rotation
    const normalisedRotation = rotation % 360;

    // Return the normalised rotation
    return normalisedRotation < 0 ? normalisedRotation + 360 : normalisedRotation;

};



const roleHasEditedPlacement = (roleData) => {

    // Return whether this role has its own edited placement data
    return String(roleData?.edited || "N").trim().toUpperCase() === "Y";

};



const getProductPricePreviewKey = ({
    storeSku,
    podProductSku,
}) => {

    // Return a stable key for matching mock order values back to the product row
    return `${String(storeSku || "").trim()}::${String(podProductSku || "").trim()}`;

};



const getResolvedVatRateFromPriceRows = (prices = []) => {

    // Stop when prices is not an array
    if (!Array.isArray(prices)) {

        return 0;

    }

    // Find the first active price row
    const priceRow = prices.find((priceItem) => priceItem?.blocked !== "Y") || null;

    // Return the tax rate as a percentage
    return Number(priceRow?.tax_rate || 0) * 100;

};



const getCurrentPlanPreviewFromPreviewObject = ({
    earningPreviews,
    currentPlanLevel,
    subscriptionLevels,
}) => {

    // Read preview entries safely
    const previewEntries = Object.entries(earningPreviews || {});

    // Read the current plan value as text, as it may be a level number or a code
    const currentPlanText = String(currentPlanLevel || "").trim().toLowerCase();

    // Match directly by preview key or preview subscription code first
    const directCodePreviewEntry = previewEntries.find(([previewCode, preview]) => {

        // Read the preview code
        const safePreviewCode = String(previewCode || "").trim().toLowerCase();

        // Read the preview subscription code
        const previewSubscriptionCode = String(preview?.subscription_level?.code || "").trim().toLowerCase();

        // Return whether this preview matches the current plan code
        return currentPlanText !== "" && (safePreviewCode === currentPlanText || previewSubscriptionCode === currentPlanText);

    });

    // Return the direct code match when found
    if (directCodePreviewEntry) {

        return directCodePreviewEntry[1];

    }

    // Read subscription levels safely
    let safeSubscriptionLevels = [];

    // Use subscription levels directly when they are an array
    if (Array.isArray(subscriptionLevels)) {

        safeSubscriptionLevels = subscriptionLevels;

    }

    // Use subscription level results when wrapped in an API response
    if (!Array.isArray(subscriptionLevels) && Array.isArray(subscriptionLevels?.results)) {

        safeSubscriptionLevels = subscriptionLevels.results;

    }

    // Find the current subscription level row
    const currentSubscriptionLevel = safeSubscriptionLevels.find((levelRow) => {
        return Number(levelRow?.level) === Number(currentPlanLevel);
    }) || null;

    // Read the current subscription code
    const currentSubscriptionCode = String(currentSubscriptionLevel?.code || "").trim();

    // Match by subscription code first
    if (currentSubscriptionCode !== "") {

        const matchedCodePreviewEntry = previewEntries.find(([previewCode, preview]) => {

            // Read the preview subscription code
            const previewSubscriptionCode = String(preview?.subscription_level?.code || previewCode || "").trim();

            // Return whether this preview matches the current subscription code
            return previewSubscriptionCode === currentSubscriptionCode;

        });

        // Return the matched preview
        if (matchedCodePreviewEntry) {

            return matchedCodePreviewEntry[1];

        }

    }

    // Match by numeric subscription level
    const matchedLevelPreviewEntry = previewEntries.find(([, preview]) => {
        return Number(preview?.subscription_level?.level) === Number(currentPlanLevel);
    });

    // Return the matched preview
    if (matchedLevelPreviewEntry) {

        return matchedLevelPreviewEntry[1];

    }

    // Return null when there is no match
    return null;

};



const getMockOrderItemDisplayValues = ({
    orderItem,
    orderResults,
    currentPlanLevel,
    subscriptionLevels,
    currencyCode,
}) => {

    // Read line-level earning previews from the order item
    const lineEarningPreviews =
        orderItem?.earning_previews ||
        orderItem?.earnings_previews ||
        orderItem?.earning_preview ||
        orderItem?.earnings_preview ||
        orderItem?.earningPreview ||
        null;

    // Get the current plan preview from the line
    const linePlanPreview = getCurrentPlanPreviewFromPreviewObject({
        earningPreviews: lineEarningPreviews,
        currentPlanLevel,
        subscriptionLevels,
    });

    // Read response order items safely
    const responseOrderItems = Array.isArray(orderResults?.orderitems) ? orderResults.orderitems : [];

    // Use order-level previews only when this mock order has exactly one product line
    const orderLevelEarningPreviews = responseOrderItems.length === 1
        ? orderResults?.earning_previews || orderResults?.earnings_previews || null
        : null;

    // Get the current plan preview from the order when safe
    const orderLevelPlanPreview = getCurrentPlanPreviewFromPreviewObject({
        earningPreviews: orderLevelEarningPreviews,
        currentPlanLevel,
        subscriptionLevels,
    });

    // Return only values actually calculated by the mock order API
    return {
        storePrice:
            orderItem?.itempriceinctax ??
            orderItem?.item_price_inc_tax ??
            orderItem?.price_inc_tax ??
            orderItem?.itemprice ??
            null,

        productEarning:
            linePlanPreview?.product_earning ??
            orderItem?.product_earning ??
            orderItem?.earning?.product_earning ??
            orderLevelPlanPreview?.product_earning ??
            null,

        totalEarning:
            linePlanPreview?.total_earning ??
            orderItem?.total_earning ??
            orderItem?.earning?.total_earning ??
            orderLevelPlanPreview?.total_earning ??
            null,

        currencyCode,
    };

};



const previewViewIsAllowedForConfiguredRole = (view, configuredRole) => {

    // Hide the view when this configured product does not have the matching role
    if (!configuredRole) {

        return false;

    }

    // Hide the view when the matching configured role has been disabled
    if (normalisePreviewString(configuredRole?.active || "Y").toUpperCase() === "N") {

        return false;

    }

    // Get the configured role view settings
    const viewSettings = normaliseRoleViewSettings(configuredRole?.view_settings);

    // Get every design aspect this real product view can display
    const viewAspects = normaliseProductViewAspects(view);

    // Get the view rotation
    const viewRotation = getPreviewViewRotation(view);

    // Check if the view and configured role share at least one aspect
    const aspectAllowed = viewSettings.aspects.some((aspect) => viewAspects.includes(aspect));

    // Check if the view rotation is allowed
    const rotationAllowed = viewSettings.rotations.includes(viewRotation);

    // Return whether this view should be shown
    return aspectAllowed && rotationAllowed;

};



const getProductPreviewViews = (customProduct) => {

    // Safely store product surfaces
    const surfaces = Array.isArray(customProduct?.surfaces) ? customProduct.surfaces : [];

    // Store all preview views
    const previewViews = [];

    // Loop through surfaces
    surfaces.forEach((surface) => {

        // Safely store print areas
        const printAreas = Array.isArray(surface?.print_areas) ? surface.print_areas : [];

        // Loop through print areas
        printAreas.forEach((printArea) => {

            // Safely store print area views
            const views = Array.isArray(printArea?.views) ? printArea.views : [];

            // Loop through views
            views.forEach((view) => {

                // Safely store assets
                const assets = Array.isArray(view?.assets) ? view.assets : [];

                // Check whether this view has a preview asset
                const hasPreviewAsset = assets.some((asset) => normalisePreviewString(asset?.type).toLowerCase() === "preview");

                // Skip views that cannot render as live previews
                if (!hasPreviewAsset) {

                    return;

                }

                // Add the preview view with surface and print area context attached
                previewViews.push({
                    ...view,
                    surface_code: view?.surface_code || surface?.code || "default",
                    print_area_code: view?.print_area_code || printArea?.code || "default",
                    role: view?.role || printArea?.role || printArea?.internal_id || "",
                });

            });

        });

    });

    // Sort the views in the same rough order as the store page
    return previewViews.sort((a, b) => getPreviewViewPriority(a) - getPreviewViewPriority(b));

};



const getProductPreviewEntries = (previewViews, galleryImages) => {

    // Build live view entries
    const viewEntries = previewViews.map((view, index) => ({
        type: "view",
        index: index,
        view: view,
    }));

    // Build saved/generated image entries
    const imageEntries = galleryImages.map((image, index) => ({
        type: "image",
        index: previewViews.length + index,
        imageIndex: index,
        img: image,
    }));

    // Return combined entries
    return viewEntries.concat(imageEntries);

};


// Helper to check if a canonical product has a matching role to the one passed in
const hasMatchingRole = (canonicalParentOrChildProduct, roleToMatch) => {

    // Get the surfaces safely from the canonical product
    const safeSurfaces = canonicalParentOrChildProduct?.custom_product?.surfaces || [];

    const productHasMatchingRole = (safeSurfaces || []).some((surface) => {

        // Get print areas safely (always an array)
        const printAreas = surface?.print_areas || [];

        // Map through the canonical product print areas
        return printAreas.some((printArea) =>

            // Only match if there exists a role whose role key equals this roleItem role
            roleToMatch?.role === printArea?.role

        );

    });

    return productHasMatchingRole;

}


// Find one configured parent or child product by its configured id
const getConfiguredProductById = (products, configuredProductId) => {

    // Normalise the configured id
    const safeConfiguredProductId = String(configuredProductId || "");

    // Stop when there is no configured id
    if (!safeConfiguredProductId) { return null; }

    // Read parent products safely
    const safeProducts = Array.isArray(products) ? products : [];

    // Check each parent and its children
    for (const productItem of safeProducts) {

        // Return a matching parent product
        if (String(productItem?.id || productItem?.client_configured_product_id || "") === safeConfiguredProductId) {
            return productItem;
        }

        // Read child products safely
        const childProducts = Array.isArray(productItem?.children) ? productItem.children : [];

        // Find a matching child product
        const matchingChild = childProducts.find((childItem) => {
            return String(childItem?.id || childItem?.client_configured_product_id || "") === safeConfiguredProductId;
        }) || null;

        // Return the matching child
        if (matchingChild) { return matchingChild; }

    }

    // No matching configured product was found
    return null;

};



// Build the saved automatic mockup state for one configured product family
const getConfiguredProductAutoMockupCounts = (configuredProduct) => {

    // Store effective counts and inheritance state by configured product id
    const counts = {};

    // Add one configured product and resolve any child inheritance from its effective parent value
    const addConfiguredProduct = (productItem, parentAutoMockupCount = null) => {

        // Skip invalid products
        if (!productItem || typeof productItem !== "object") { return; }

        // Read the configured product identity
        const configuredProductId =
            String(
                productItem?.id ||
                productItem?.client_configured_product_id ||
                ""
            );

        // Read whether this child inherits its automatic mockup count
        const inheritsAutoMockupCount =
            parentAutoMockupCount !== null &&
            String(productItem?.auto_mockup_count_inherited || "N").trim().toUpperCase() === "Y";

        // Start with the configured default for top-level or historical invalid values
        let effectiveAutoMockupCount = 3;

        // Inherited children always use the current effective parent count
        if (inheritsAutoMockupCount) {

            effectiveAutoMockupCount = parentAutoMockupCount;

        } else {

            // Read the explicit configured value
            const configuredAutoMockupCount =
                Number.parseInt(
                    productItem?.auto_mockup_count ?? 3,
                    10
                );

            // Keep valid whole-number values, including zero for manual selection
            if (Number.isInteger(configuredAutoMockupCount) && configuredAutoMockupCount >= 0) {
                effectiveAutoMockupCount = configuredAutoMockupCount;
            }

        }

        // Store both effective behaviour and whether it is inherited
        if (configuredProductId) {

            counts[configuredProductId] = {
                effective: effectiveAutoMockupCount,
                inherited: inheritsAutoMockupCount ? "Y" : "N",
            };

        }

        // Include configured children using this product's effective value as their parent default
        const children =
            Array.isArray(productItem?.children)
                ? productItem.children
                : [];

        children.forEach((childItem) => {
            addConfiguredProduct(childItem, effectiveAutoMockupCount);
        });

    };

    // Build the complete family lookup
    addConfiguredProduct(configuredProduct);

    return counts;

};


// Find configured products whose effective automatic mockup count changed since the last save
const getChangedAutoMockupProductIds = (
    savedCounts,
    currentConfiguredProduct
) => {

    // Build the current count lookup
    const currentCounts =
        getConfiguredProductAutoMockupCounts(
            currentConfiguredProduct
        );

    // Return only configured products whose effective count is different
    return Object.keys(currentCounts).filter(
        (configuredProductId) => {

            return (
                currentCounts[configuredProductId]?.effective !==
                savedCounts?.[configuredProductId]?.effective
            );

        }
    );

};

// Build the effective mockup colourway state for one configured product family
const getConfiguredProductMockupColourways = (configuredProduct) => {

    // Store effective colourway lists by configured product id
    const colourways = {};

    // Add one configured product and resolve automatic inheritance from its parent
    const addConfiguredProduct = (productItem, parentMockupColourways = null) => {

        // Skip invalid products
        if (!productItem || typeof productItem !== "object") { return; }

        // Read the configured product identity
        const configuredProductId =
            String(
                productItem?.id ||
                productItem?.client_configured_product_id ||
                ""
            );

        // Read the product's available colourways
        const availableColourways =
            normaliseColourwayCodes(
                productItem?.colourways
            );

        // Start with no effective mockup colourways
        let effectiveMockupColourways = [];

        // Products without colourways never create colourway-specific mockups
        if (availableColourways.length > 0) {

            // Read whether this product has an explicit mockup colourway selection
            const explicitMockupColourways =
                String(
                    productItem?.mockup_colourways_explicit || "N"
                ).trim().toUpperCase() === "Y";

            // Automatic children only inherit mockup colours while their default also comes from the parent
            const inheritsParentMockupColourways =
                Array.isArray(parentMockupColourways) &&
                String(
                    productItem?.default_colourway_inherited || "N"
                ).trim().toUpperCase() === "Y";

            if (explicitMockupColourways) {

                // Read the explicit selection
                effectiveMockupColourways =
                    normaliseColourwayCodes(
                        productItem?.mockup_colourways
                    );

                // Keep the effective default first, matching API save normalisation
                const defaultColourway =
                    String(
                        productItem?.default_colourway || ""
                    ).trim();

                if (defaultColourway) {

                    effectiveMockupColourways =
                        effectiveMockupColourways.filter(
                            (colourwayCode) => {
                                return colourwayCode !== defaultColourway;
                            }
                        );

                    effectiveMockupColourways.unshift(
                        defaultColourway
                    );

                }

            } else if (inheritsParentMockupColourways) {

                // Automatic children with an inherited default also inherit the parent's complete mockup selection
                effectiveMockupColourways = [
                    ...parentMockupColourways,
                ];

            } else {

                // Automatic parents and children with their own default follow that effective default
                const defaultColourway =
                    String(
                        productItem?.default_colourway || ""
                    ).trim();

                if (defaultColourway) {
                    effectiveMockupColourways.push(
                        defaultColourway
                    );
                }

            }

        }

        // Store the effective ordered selection
        if (configuredProductId) {
            colourways[configuredProductId] =
                effectiveMockupColourways;
        }

        // Resolve configured children against this effective parent list
        const children =
            Array.isArray(productItem?.children)
                ? productItem.children
                : [];

        children.forEach((childItem) => {
            addConfiguredProduct(
                childItem,
                effectiveMockupColourways
            );
        });

    };

    // Build the complete family state
    addConfiguredProduct(configuredProduct);

    return colourways;

};


// Find configured products whose effective mockup colourways changed since the last save
const getChangedMockupColourwayProductIds = (
    savedColourways,
    currentConfiguredProduct
) => {

    // Build the current effective colourway lookup
    const currentColourways =
        getConfiguredProductMockupColourways(
            currentConfiguredProduct
        );

    // Return only products whose ordered effective selection changed
    return Object.keys(currentColourways).filter(
        (configuredProductId) => {

            const previousColourways =
                savedColourways?.[configuredProductId] || [];

            return !deepEqual(
                currentColourways[configuredProductId],
                previousColourways
            );

        }
    );

};

// Replace the automatic mockup count on one configured parent or child product
const replaceConfiguredProductAutoMockupCount = (products, configuredProductId, autoMockupCount) => {

    // Normalise the target id and input array
    const safeConfiguredProductId = String(configuredProductId || "");
    const safeProducts = Array.isArray(products) ? products : [];

    // Update only the matching configured parent or child
    return safeProducts.map((productItem) => {

        // Update a matching parent product
        if (String(productItem?.id || productItem?.client_configured_product_id || "") === safeConfiguredProductId) {

            // Keep inherited children showing the parent's new effective value without creating child overrides
            const nextChildren =
                Array.isArray(productItem?.children)
                    ? productItem.children.map((childItem) => {

                        // Explicit child overrides remain unchanged
                        if (String(childItem?.auto_mockup_count_inherited || "N").trim().toUpperCase() !== "Y") {
                            return childItem;
                        }

                        // Inherited children follow the new effective parent value
                        return {
                            ...childItem,
                            auto_mockup_count: autoMockupCount,
                        };

                    })
                    : productItem?.children;

            return {
                ...productItem,
                auto_mockup_count: autoMockupCount,
                auto_mockup_count_inherited: "N",
                children: nextChildren,
            };

        }

        // Update a matching child product
        const childProducts = Array.isArray(productItem?.children) ? productItem.children : [];

        const nextChildren = childProducts.map((childItem) => {

            // Keep unrelated children unchanged
            if (String(childItem?.id || childItem?.client_configured_product_id || "") !== safeConfiguredProductId) {
                return childItem;
            }

            // Set the matching child automatic mockup count
            return {
                ...childItem,
                auto_mockup_count: autoMockupCount,
                auto_mockup_count_inherited: "N",
            };

        });

        // Return the parent with its current children
        return {
            ...productItem,
            children: nextChildren,
        };

    });

};


// Replace the image rows on one configured parent or child product
const replaceConfiguredProductImages = (products, configuredProductId, images) => {

    // Normalise the target id and input arrays
    const safeConfiguredProductId = String(configuredProductId || "");
    const safeProducts = Array.isArray(products) ? products : [];
    const safeImages = Array.isArray(images) ? images : [];

    // Update only the matching configured parent or child
    return safeProducts.map((productItem) => {

        // Update a matching parent product
        if (String(productItem?.id || productItem?.client_configured_product_id || "") === safeConfiguredProductId) {
            return {
                ...productItem,
                images: safeImages,
            };
        }

        // Update a matching child product
        const childProducts = Array.isArray(productItem?.children) ? productItem.children : [];
        const nextChildren = childProducts.map((childItem) => {

            // Keep unrelated children unchanged
            if (String(childItem?.id || childItem?.client_configured_product_id || "") !== safeConfiguredProductId) {
                return childItem;
            }

            // Replace the matching child image rows
            return {
                ...childItem,
                images: safeImages,
            };

        });

        // Return the parent with its current children
        return {
            ...productItem,
            children: nextChildren,
        };

    });

};


// Replace the roles and image rows on one configured parent or child product
const replaceConfiguredProductRolesAndImages = (products, configuredProductId, roles, images) => {

    // Normalise the target id and input arrays
    const safeConfiguredProductId = String(configuredProductId || "");
    const safeProducts = Array.isArray(products) ? products : [];
    const safeRoles = Array.isArray(roles) ? roles : [];
    const safeImages = Array.isArray(images) ? images : [];

    // Update only the matching configured parent or child
    return safeProducts.map((productItem) => {

        // Update a matching parent product
        if (String(productItem?.id || productItem?.client_configured_product_id || "") === safeConfiguredProductId) {
            return {
                ...productItem,
                roles: safeRoles,
                images: safeImages,
            };
        }

        // Update a matching child product
        const childProducts = Array.isArray(productItem?.children) ? productItem.children : [];

        const nextChildren = childProducts.map((childItem) => {

            // Keep unrelated children unchanged
            if (String(childItem?.id || childItem?.client_configured_product_id || "") !== safeConfiguredProductId) {
                return childItem;
            }

            // Replace the matching child roles and image rows
            return {
                ...childItem,
                roles: safeRoles,
                images: safeImages,
            };

        });

        // Return the parent with its current children
        return {
            ...productItem,
            children: nextChildren,
        };

    });

};


// Normalise one complete configured product family for the popup Save button
const normaliseConfiguredProductForEditedCheck = (
    configuredProduct
) => {

    // Stop when there is no configured product
    if (
        !configuredProduct ||
        typeof configuredProduct !== "object"
    ) {
        return null;
    }

    // Roles and images are saved immediately through their dedicated endpoints
    const normalisedProduct = {
        ...configuredProduct,
        roles: [],
        images: [],
    };

    // Canonical product data is display-only and does not belong to this save
    delete normalisedProduct.pod_product;

    // Automatic mockup counts and inheritance use their own saved baseline because Manage Images can save manual mode immediately
    delete normalisedProduct.auto_mockup_count;
    delete normalisedProduct.auto_mockup_count_inherited;

    // Normalise every configured child recursively
    if (Array.isArray(configuredProduct.children)) {

        normalisedProduct.children =
            configuredProduct.children.map(
                (configuredChild) => {

                    return normaliseConfiguredProductForEditedCheck(
                        configuredChild
                    );

                }
            );

    }

    // Return only fields controlled by the product Save button
    return normalisedProduct;

};



// Build one configured product family for the normal product endpoint
const buildConfiguredProductFamilySavePayload = (
    configuredProduct
) => {

    // Copy the product so live state is never mutated
    const productPayload = {
        ...configuredProduct,
    };

    // These fields are saved or supplied elsewhere
    delete productPayload.pod_product;
    delete productPayload.roles;
    delete productPayload.images;
    delete productPayload.translations;
    delete productPayload.client_created_at;

    // Send colourway codes rather than complete colourway objects
    if (Array.isArray(productPayload.colourways)) {

        productPayload.colourways =
            normaliseColourwayCodes(
                productPayload.colourways
            );

    }

    // Clean every configured child in the same family
    if (Array.isArray(configuredProduct?.children)) {

        productPayload.children =
            configuredProduct.children.map(
                (configuredChild) => {

                    return buildConfiguredProductFamilySavePayload(
                        configuredChild
                    );

                }
            );

    }

    // Return the product endpoint payload
    return productPayload;

};



// Find one configured parent product family by its public id
const getConfiguredProductFamilyById = (
    products,
    configuredProductId
) => {

    // Read the configured product list safely
    let safeProducts = [];

    if (Array.isArray(products)) {
        safeProducts = products;
    }

    // Normalise the requested configured id
    const safeConfiguredProductId =
        String(configuredProductId || "");

    // Stop when there is no usable configured id
    if (!safeConfiguredProductId) {
        return null;
    }

    // Return the exact configured parent family
    return safeProducts.find((productItem) => {

        return (
            String(productItem?.id || "") ===
            safeConfiguredProductId
        );

    }) || null;

};



// Throw a useful error when an API request failed
const requireSuccessfulProductResponse = (
    response,
    fallbackMessage
) => {

    // Successful responses need no further handling
    if (response?.success) {
        return;
    }

    // Start with the action-specific fallback message
    let errorMessage = fallbackMessage;

    // Include the API error when one was returned
    if (response?.error_message) {
        errorMessage += ": " + response.error_message;
    }

    // Send the error to the shared handler
    throw new Error(errorMessage);

};







// Check whether a configured product still has mockup rows waiting for a generated image
const configuredProductHasPendingMockups = (configuredProduct) => {

    // Read image rows safely
    const images = Array.isArray(configuredProduct?.images) ? configuredProduct.images : [];

    // Return whether any mockup row is still missing its image id
    return images.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id);

};



// Build the complete configured role payload used by the replacement endpoint
const buildConfiguredProductRolesPayload = (roles) => {

    // Read roles safely
    const safeRoles = Array.isArray(roles) ? roles : [];

    // Build the complete role payload
    return safeRoles.map((roleItem) => {

        // Read whether this role has its own edited placement
        const roleIsEdited = String(roleItem?.edited || "N").toUpperCase() === "Y";

        // Start with fields required for every configured role
        const rolePayload = {
            role: roleItem?.role || null,
            active: roleItem?.active || "N",
            initial_view_rotation: roleItem?.initial_view_rotation !== null && roleItem?.initial_view_rotation !== undefined
                ? Number(roleItem.initial_view_rotation)
                : 0,
            view_settings: normaliseRoleViewSettings(roleItem?.view_settings),
        };

        // Unedited roles must not overwrite inherited placement data
        if (!roleIsEdited) { return rolePayload; }

        // Add the complete edited placement data
        return {
            ...rolePayload,
            background_colour: roleItem?.background_colour || null,
            edited_background: roleItem?.edited_background || null,
            centre_x: roleItem?.centre_x !== null && roleItem?.centre_x !== undefined ? String(roleItem.centre_x) : null,
            centre_y: roleItem?.centre_y !== null && roleItem?.centre_y !== undefined ? String(roleItem.centre_y) : null,
            scale_x: roleItem?.scale_x !== null && roleItem?.scale_x !== undefined ? String(roleItem.scale_x) : null,
            scale_y: roleItem?.scale_y !== null && roleItem?.scale_y !== undefined ? String(roleItem.scale_y) : null,
            rotation: roleItem?.rotation !== null && roleItem?.rotation !== undefined ? String(roleItem.rotation) : null,
            fit_fill: roleItem?.fit_fill || null,
            position: roleItem?.position || null,
            edited: "Y",
        };

    });

};



// Build the dedicated image endpoint payload, including pending background jobs
const buildProductImagesApiPayload = (
    images,
    preserveGeneratedColourways = true
) => {

    // Read images safely
    const safeImages = Array.isArray(images) ? images : [];

    // Build the ordered image payload
    return safeImages.map((imageRow) => {

        // Start a clean image row
        const cleanImage = {};

        // Default generated mockups and user bucket images to the user's bucket
        cleanImage.source = "U";

        // Preserve Strich ownership for canonical product images
        if (!imageRow?.mockup_id && imageRow?.source === "S") { cleanImage.source = "S"; }

        // Preserve a completed image id
        if (imageRow?.image_id) { cleanImage.image_id = Number(imageRow.image_id); }

        // Preserve the exact mockup job configuration, with or without an image id
        if (imageRow?.mockup_id) {
            cleanImage.mockup_id = Number(imageRow.mockup_id);
            cleanImage.aspect = String(imageRow?.aspect || "").trim().toUpperCase();
            cleanImage.rotation = Number(imageRow.rotation);
        }

        // Manual images always retain an optional colourway; exact generated retries can retain theirs too
        if (
            imageRow?.colourway &&
            (
                !imageRow?.mockup_id ||
                preserveGeneratedColourways
            )
        ) {
            cleanImage.colourway =
                String(imageRow.colourway);
        }

        // Return the clean image row
        return cleanImage;

    }).filter((imageRow) => imageRow.image_id || imageRow.mockup_id);

};

// Build a stable identity and ordering signature for one product image selection
const getProductImageSelectionSignature = (images) => {

    // Read image rows without Strich product images, which do not affect mockup selection mode
    const safeImages = Array.isArray(images)
        ? images.filter((imageRow) => imageRow?.source !== "S")
        : [];

    // Keep only the source identity and exact display order
    return stableStringify(
        safeImages.map((imageRow) => {

            // Generated mockups are identified by exact configuration
            if (imageRow?.mockup_id) {
                return {
                    source: "mockup",
                    mockup_id: Number(imageRow.mockup_id),
                    aspect: String(imageRow?.aspect || "").trim().toUpperCase(),
                    rotation: Number(imageRow.rotation),
                };
            }

            // Manual bucket images are identified by their saved bucket image id
            return {
                source: "image",
                image_id: Number(imageRow?.image_id || 0),
            };

        })
    );

};


// Turn selected generated mockup rows back into pending background jobs
const buildPendingMockupImageRows = (images) => {

    // Read image rows safely
    const safeImages = Array.isArray(images) ? images : [];

    // Clear generated image data from mockup rows while preserving manual bucket images
    return safeImages.map((imageRow) => {

        // Manual bucket images do not need background generation
        if (!imageRow?.mockup_id) { return imageRow; }

        // Preserve the exact mockup configuration but remove its generated result
        return {
            ...imageRow,
            image_id: null,
            image_url: null,
            thumbnail_url: null,
            thumbUrl: null,
            mockup_image_url: null,
            mockup_thumbnail_url: null,
        };

    });

};


// Product images popup - manages product image rows from mockups and bucket images
const ProductImagesPopup = ({

    // Controls whether the popup is open
    isOpen,

    // Popup close handler
    onClose,

    // Popup title
    title = "Manage Product Images",

    // Canonical parent or child product used to resolve exact mockup configurations
    canonicalProduct = null,

    // Configured product roles used to determine compatible mockups
    configuredRoles = [],

    // Design roles used to identify print areas which currently have images
    designRoles = [],

    // Current ordered product image rows
    images = [],

    // Save handler
    onSave,

    // Disabled state
    disabled = false,

}) => {

    // Store selected image rows locally until Save is clicked
    const [selectedImages, setSelectedImages] = useState([]);

    // Store the selected image row currently being dragged
    const [draggedSelectedImageIndex, setDraggedSelectedImageIndex] = useState(null);

    // Store the active source tab
    const [activeSourceTab, setActiveSourceTab] = useState("mockups");

    // Store whether the mockup gallery shows role matches or every mockup
    const [mockupFilterMode, setMockupFilterMode] = useState("matching");

    // Normalise the canonical mockups used for labels and previews
    const safeAvailableMockups = useMemo(() => {

        // Read unique mockups from the canonical product locations
        return getCanonicalProductMockups(canonicalProduct);

    }, [canonicalProduct]);

        // Keep only configured roles whose corresponding design role has an image
    const configuredRolesWithImages = useMemo(() => {

        // Read the supplied configured roles safely
        let safeConfiguredRoles = [];

        if (Array.isArray(configuredRoles)) {
            safeConfiguredRoles = configuredRoles;
        }

        // Read the design roles safely
        let safeDesignRoles = [];

        if (Array.isArray(designRoles)) {
            safeDesignRoles = designRoles;
        }

        // Store the role codes which currently contain usable image data
        const populatedDesignRoleKeys = new Set();

        safeDesignRoles.forEach((designRole) => {

            // Ignore design roles without image data
            if (!designRoleHasUsableImageData(designRole)) {
                return;
            }

            // Read the role code
            const roleCode =
                normalisePreviewString(designRole.role);

            // Store the populated role
            if (roleCode) {
                populatedDesignRoleKeys.add(roleCode);
            }

        });

        // Return only configured roles backed by a populated design role
        return safeConfiguredRoles.filter((configuredRole) => {

            // Read the configured role code
            const roleCode =
                normalisePreviewString(configuredRole?.role);

            // Keep this role only when its design image exists
            return populatedDesignRoleKeys.has(roleCode);

        });

    }, [
        configuredRoles,
        designRoles,
    ]);

    // Normalise the canonical product images available for manual selection
    const safeAvailableProductImages = useMemo(() => {

        // Read the canonical product venues safely
        const venues = Array.isArray(canonicalProduct?.venues) ? canonicalProduct.venues : [];
        const productImages = [];
        const addedImageIds = new Set();

        // Go through each venue on the canonical product
        venues.forEach((venue) => {

            // Read this venue's ordinary product images safely
            const venueImages = Array.isArray(venue?.images) ? venue.images : [];

            // Go through each ordinary product image
            venueImages.forEach((productImage) => {

                const imageId = Number(productImage?.image_id);

                // Ignore invalid images or images already added from another venue
                if (!imageId || addedImageIds.has(imageId)) { return; }

                // Add this canonical product image once
                addedImageIds.add(imageId);
                productImages.push({
                    ...productImage,
                    image_id: imageId,
                });

            });

        });

        // Return the available canonical product images
        return productImages;

    }, [canonicalProduct]);

    // Normalise current image rows when the popup opens or image data changes
    useEffect(() => {

        // If the popup is closed, do nothing
        if (!isOpen) { return; }

        // Resolve selected rows without removing mockups hidden by the role filter
        const resolvedImages = removeIncompatibleMockupImages(
            images,
            canonicalProduct,
            configuredRolesWithImages
        );

        // Build the selected image list
        const nextSelectedImages = resolvedImages
            .map((imageRow) => {

                // Read image id safely
                const imageId = imageRow?.image_id ? Number(imageRow.image_id) : null;

                // Read mockup id safely
                const mockupId = imageRow?.mockup_id ? Number(imageRow.mockup_id) : null;

                // Skip invalid rows
                if (!imageId && !mockupId) { return null; }

                // Read exact mockup configuration values
                const aspect = mockupId ? String(imageRow?.aspect || "").trim().toUpperCase() : null;
                const rotation = mockupId && imageRow?.rotation !== null && imageRow?.rotation !== undefined && imageRow?.rotation !== ""
                    ? Number(imageRow.rotation)
                    : null;

                // Return a clean image row
                return {
                    image_id: imageId,
                    source: imageRow?.source === "S" ? "S" : "U",
                    mockup_id: mockupId,
                    aspect: aspect,
                    rotation: rotation,
                    image_url: imageRow?.image_url || null,
                    thumbnail_url: imageRow?.thumbnail_url || null,
                };

            })
            .filter((imageRow) => imageRow);

        // Store the selected image rows only when their contents have actually changed
        setSelectedImages((previousImages) => {

            // Keep the existing state when both image arrays contain the same data
            if (deepEqual(previousImages, nextSelectedImages)) {

                // Returning the same reference prevents an unnecessary render
                return previousImages;

            }

            // Store the genuinely changed image rows
            return nextSelectedImages;

        });

    }, [
        isOpen,
        images,
        canonicalProduct,
        configuredRolesWithImages,
    ]);



    // Reset popup controls whenever the Manage Images popup opens
    useEffect(() => {

        // Do nothing while the popup is closed
        if (!isOpen) {

            // Stop here
            return;

        }

        // Start on the mockups source tab
        setActiveSourceTab("mockups");

        // Start with role-matching mockups
        setMockupFilterMode("matching");

    }, [
        isOpen,
    ]);


    // Build a lookup map for available mockups
    const availableMockupMap = useMemo(() => {

        // Create the map
        const map = {};

        // Add each available mockup to the map
        safeAvailableMockups.forEach((mockup) => {

            // Skip mockups without an id
            if (!mockup?.id) { return; }

            // Add this mockup to the map
            map[String(mockup.id)] = mockup;

        });

        // Return the map
        return map;

    }, [safeAvailableMockups]);

    // Get every mockup filtered by the active role view settings
    const allMockupConfigurations = useMemo(() => {

        // Include every mockup without requiring a matching location
        return getAllMockupConfigurations(
            canonicalProduct,
            configuredRolesWithImages
        );

    }, [
        canonicalProduct,
        configuredRolesWithImages,
    ]);

    // Get exact configurations matching one or more active configured roles
    const matchingMockupConfigurations = useMemo(() => {

        // Return role matches in canonical mockup and product view order
        return getAvailableMockupConfigurationsForConfiguredRoles(
            canonicalProduct,
            configuredRolesWithImages
        );

    }, [
        canonicalProduct,
        configuredRolesWithImages,
    ]);

    // Choose which mockup configurations the gallery should display
    let displayedMockupConfigurations = matchingMockupConfigurations;

    if (mockupFilterMode === "all") {
        displayedMockupConfigurations = allMockupConfigurations;
    }

    // Build the empty gallery message for the selected filter
    let emptyMockupGalleryMessage =
        "No mockups match the active print areas and selected view settings.";

    if (mockupFilterMode === "all") {
        emptyMockupGalleryMessage = "No mockups are available for this product.";
    }

    // Check whether an exact mockup configuration is already selected
    const isMockupConfigurationSelected = (configuration) => {

        // Return whether this exact mockup, aspect and rotation row exists
        return selectedImages.some((imageRow) => {

            // Build the selected row key
            const selectedKey = getMockupConfigurationKey(imageRow?.mockup_id, imageRow?.aspect, imageRow?.rotation);

            // Compare the exact configuration key
            return selectedKey && selectedKey === configuration?.key;

        });

    };

    // Count how many currently displayed mockup configurations are already selected
    const selectedDisplayedMockupCount = displayedMockupConfigurations.filter((configuration) => {
        return isMockupConfigurationSelected(configuration);
    }).length;

    // Work out which bulk actions are currently useful
    const canAddAllDisplayedMockups =
        displayedMockupConfigurations.length > selectedDisplayedMockupCount;

    const canRemoveAllDisplayedMockups =
        selectedDisplayedMockupCount > 0;

    // Add every currently displayed mockup configuration which is not already selected
    const handleAddAllDisplayedMockups = () => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Add missing mockup rows to the end of the current image list
        setSelectedImages((previousImages) => {

            // Build a lookup of exact mockup configurations already selected
            const selectedConfigurationKeys = new Set();

            previousImages.forEach((imageRow) => {

                // Manual bucket images do not have a mockup configuration key
                if (!imageRow?.mockup_id) { return; }

                // Store the exact selected mockup configuration
                const selectedKey = getMockupConfigurationKey(
                    imageRow.mockup_id,
                    imageRow.aspect,
                    imageRow.rotation
                );

                if (selectedKey) {
                    selectedConfigurationKeys.add(selectedKey);
                }

            });

            // Build new rows in the same order they are currently displayed
            const rowsToAdd = [];

            displayedMockupConfigurations.forEach((configuration) => {

                // Read the exact configuration key
                const configurationKey = String(configuration?.key || "");

                // Ignore invalid or already-selected configurations
                if (!configurationKey || selectedConfigurationKeys.has(configurationKey)) { return; }

                // Prevent duplicates within the displayed configuration list itself
                selectedConfigurationKeys.add(configurationKey);

                // Add the exact mockup configuration as a queued image source row
                rowsToAdd.push({
                    image_id: null,
                    mockup_id: Number(configuration.mockup_id),
                    aspect: configuration.aspect,
                    rotation: Number(configuration.rotation),
                    image_url: null,
                    thumbnail_url: null,
                });

            });

            // Keep the existing state when there is nothing new to add
            if (rowsToAdd.length === 0) { return previousImages; }

            // Append newly selected mockups in gallery order
            return [
                ...previousImages,
                ...rowsToAdd,
            ];

        });

    };

    // Remove every currently displayed mockup configuration from the selected image list
    const handleRemoveAllDisplayedMockups = () => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Build a lookup of the exact configurations currently displayed
        const displayedConfigurationKeys = new Set();

        displayedMockupConfigurations.forEach((configuration) => {

            // Store valid configuration keys
            const configurationKey = String(configuration?.key || "");

            if (configurationKey) {
                displayedConfigurationKeys.add(configurationKey);
            }

        });

        // Nothing to remove if the gallery is empty
        if (displayedConfigurationKeys.size === 0) { return; }

        // Remove only displayed mockup rows
        setSelectedImages((previousImages) => {

            const nextImages = previousImages.filter((imageRow) => {

                // Never remove manual bucket images through the mockup bulk action
                if (!imageRow?.mockup_id) { return true; }

                // Build the exact selected mockup configuration key
                const selectedKey = getMockupConfigurationKey(
                    imageRow.mockup_id,
                    imageRow.aspect,
                    imageRow.rotation
                );

                // Keep mockups which are not part of the currently displayed set
                return !selectedKey || !displayedConfigurationKeys.has(selectedKey);

            });

            // Keep the existing state reference when nothing changed
            if (nextImages.length === previousImages.length) { return previousImages; }

            // Store the reduced image list
            return nextImages;

        });

    };

    // Toggle an exact mockup configuration source on or off
    const handleToggleMockupConfiguration = (configuration) => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Read the exact configuration key
        const configurationKey = String(configuration?.key || "");

        // Ignore invalid configurations
        if (!configurationKey) { return; }

        // Update the selected image rows
        setSelectedImages((previousImages) => {

            // Check whether the exact configuration is already selected
            const configurationIsSelected = previousImages.some((imageRow) => {
                return getMockupConfigurationKey(imageRow?.mockup_id, imageRow?.aspect, imageRow?.rotation) === configurationKey;
            });

            // Remove only this exact configuration when it is already selected
            if (configurationIsSelected) {
                return previousImages.filter((imageRow) => {
                    return getMockupConfigurationKey(imageRow?.mockup_id, imageRow?.aspect, imageRow?.rotation) !== configurationKey;
                });
            }

            // Add the exact mockup configuration as a queued image source row
            return [
                ...previousImages,
                {
                    image_id: null,
                    mockup_id: Number(configuration.mockup_id),
                    aspect: configuration.aspect,
                    rotation: Number(configuration.rotation),
                    image_url: null,
                    thumbnail_url: null,
                },
            ];

        });

    };

    // Check whether a canonical product image is already selected
    const isProductImageSelected = (productImage) => {

        // Check the selected image rows for this exact Strich image
        return selectedImages.some((imageRow) => {
            return (
                imageRow?.source === "S" &&
                imageRow?.image_id === productImage?.image_id &&
                !imageRow?.mockup_id
            );
        });

    };

    // Toggle a canonical product image source on or off
    const handleToggleProductImage = (productImage) => {

        // Do nothing if the popup is disabled
        if (disabled) { return; }

        // Ignore product images without an image id
        if (!productImage?.image_id) { return; }

        // Update the selected product image rows
        setSelectedImages((previousImages) => {

            // Check whether this canonical product image is already selected
            const productImageIsSelected = previousImages.some((imageRow) => {
                return (
                    imageRow?.source === "S" &&
                    imageRow?.image_id === productImage.image_id &&
                    !imageRow?.mockup_id
                );
            });

            // Remove the canonical product image when it is already selected
            if (productImageIsSelected) {

                // Remove only this Strich-owned image row
                return previousImages.filter((imageRow) => {
                    return !(
                        imageRow?.source === "S" &&
                        imageRow?.image_id === productImage.image_id &&
                        !imageRow?.mockup_id
                    );
                });

            }

            // Add the canonical product image to the end of the selected image list
            return [
                ...previousImages,
                {
                    image_id: productImage.image_id,
                    source: "S",
                    mockup_id: null,
                    image_url: productImage?.image_url || null,
                    thumbnail_url: productImage?.thumbnail_url || null,
                },
            ];

        });

    };


    // Toggle a bucket image source on or off
    const handleToggleBucketImage = useCallback((bucketImage) => {

        // Do nothing if the popup is disabled
        if (disabled) {

            // Stop here
            return;

        }

        // Normalise the selected bucket image id
        const safeImageId = Number(bucketImage?.id);

        // Ignore invalid image ids
        if (!safeImageId) {

            // Stop here
            return;

        }

        // Update the selected product image rows
        setSelectedImages((previousImages) => {

            // Check whether this bucket image is already selected
            const bucketImageIsSelected = previousImages.some((imageRow) => {

                // Match manual bucket images only
                return (
                    Number(imageRow?.image_id) === safeImageId &&
                    imageRow?.source !== "S" &&
                    !imageRow?.mockup_id
                );

            });

            // Remove the bucket image when it is already selected
            if (bucketImageIsSelected) {

                // Return the list without this bucket image
                return previousImages.filter((imageRow) => {

                    // Keep every row except the matching manual bucket image
                    return !(
                        Number(imageRow?.image_id) === safeImageId &&
                        imageRow?.source !== "S" &&
                        !imageRow?.mockup_id
                    );

                });

            }

            // Add the new bucket image to the end of the selected image list
            return [

                // Keep the existing selected images
                ...previousImages,

                // Add the selected bucket image
                {
                    image_id: safeImageId,
                    source: "U",
                    mockup_id: null,
                    image_url: bucketImage?.image_url || null,
                    thumbnail_url:
                        bucketImage?.thumbnail_url ||
                        bucketImage?.thumbnail ||
                        null,
                },

            ];

        });

    }, [
        disabled,
    ]);

    // Remove a selected image row
    const handleRemoveImageRow = (indexToRemove) => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Remove the image row
        setSelectedImages((previousImages) => previousImages.filter((_, index) => index !== indexToRemove));

    };

    // Start dragging a selected image row
    const handleSelectedImageDragStart = (indexToMove) => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Store the index being dragged
        setDraggedSelectedImageIndex(indexToMove);

    };

    // Allow selected image rows to receive drops
    const handleSelectedImageDragOver = (event) => {

        // Prevent the browser default so dropping is allowed
        event.preventDefault();

    };

    // Drop a selected image row into a new position
    const handleSelectedImageDrop = (event, dropIndex) => {

        // Prevent the browser default
        event.preventDefault();

        // Do nothing if disabled
        if (disabled) { return; }

        // Do nothing if there is no dragged row
        if (draggedSelectedImageIndex === null) { return; }

        // Do nothing if the row was dropped on itself
        if (draggedSelectedImageIndex === dropIndex) {
            setDraggedSelectedImageIndex(null);
            return;
        }

        // Reorder the selected image rows
        setSelectedImages((previousImages) => {

            // Copy the previous image rows
            const nextImages = [...previousImages];

            // Remove the dragged row
            const movedRows = nextImages.splice(draggedSelectedImageIndex, 1);

            // If there was no moved row, keep the previous state
            if (movedRows.length === 0) { return previousImages; }

            // Insert the dragged row at the drop position
            nextImages.splice(dropIndex, 0, movedRows[0]);

            // Return the reordered rows
            return nextImages;

        });

        // Clear the drag index
        setDraggedSelectedImageIndex(null);

    };

    // End dragging a selected image row
    const handleSelectedImageDragEnd = () => {

        // Clear the drag index
        setDraggedSelectedImageIndex(null);

    };

    // Save the selected image rows
    const handleSave = async () => {

        // Do nothing if disabled
        if (disabled) { return; }

        // Check whether the user manually changed the selected sources or their order
        const manualSelectionChanged =
            getProductImageSelectionSignature(selectedImages) !==
            getProductImageSelectionSignature(images);

        // Save the selected images in display order
        await onSave?.(selectedImages, {
            manualSelectionChanged: manualSelectionChanged,
        });

    };

    // If the popup is closed, render nothing
    if (!isOpen) { return null; }

    // Render the popup
    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--medium productImagesPopup">

                <div className="popup__header">

                    <div className="popup__headerLeft">

                        <h2 className="popup__title">{title}</h2>

                        <p className="popup__subtitle">
                            Choose the image sources for this configured product.
                        </p>

                    </div>

                    <div className="popup__headerRight">

                        <button type="button" className="popup__close" onClick={onClose} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content productImagesPopup__content">

                    <div className="productImagesPopup__section">

                        <div className="productImagesPopup__sectionHeader">

                            <h3 className="productImagesPopup__sectionTitle">Product image list</h3>

                            <span className="hintText">
                                These rows will be saved in this order.
                            </span>

                        </div>

                        <div className="productImagesPopup__selectedStrip">

                            {selectedImages.length === 0 ? (

                                <div className="productImagesPopup__empty">
                                    No image rows selected.
                                </div>

                            ) : (

                                selectedImages.map((imageRow, index) => {

                                    // Get the mockup for this row if there is one
                                    const mockup = imageRow.mockup_id ? availableMockupMap[String(imageRow.mockup_id)] : null;

                                    // Work out the best preview image
                                    const previewUrl =
                                        imageRow.thumbnail_url ||
                                        imageRow.image_url ||
                                        mockup?.mockup_thumbnail_url ||
                                        mockup?.mockup_image_url ||
                                        mockup?.image_url ||
                                        "";

                                    // Read the display label for the exact aspect
                                    const aspectLabel = VIEW_ASPECT_OPTIONS.find((option) => option.value === imageRow?.aspect)?.label || imageRow?.aspect || "";

                                    // Work out the label
                                    const label = imageRow.mockup_id
                                        ? `${mockup?.name || "Mockup " + imageRow.mockup_id} · ${aspectLabel} · ${imageRow.rotation}°`
                                        : imageRow?.source === "S"
                                            ? "Product image"
                                            : "Bucket image";

                                    // Build a stable row key
                                    const imageRowKey = imageRow.mockup_id
                                        ? getMockupConfigurationKey(imageRow.mockup_id, imageRow.aspect, imageRow.rotation)
                                        : `image|${imageRow?.source === "S" ? "S" : "U"}|${imageRow.image_id || index}`;

                                    // Render the selected image row
                                    return (

                                        <div
                                            className={draggedSelectedImageIndex === index ? "productImagesPopup__selectedTile productImagesPopup__selectedTile--dragging" : "productImagesPopup__selectedTile"}
                                            key={imageRowKey || index}
                                            title={label}
                                            draggable={!disabled}
                                            onDragStart={() => handleSelectedImageDragStart(index)}
                                            onDragOver={handleSelectedImageDragOver}
                                            onDrop={(event) => handleSelectedImageDrop(event, index)}
                                            onDragEnd={handleSelectedImageDragEnd}
                                        >

                                            {previewUrl ? (

                                                <img
                                                    className="productImagesPopup__tileImage"
                                                    src={previewUrl}
                                                    alt={label}
                                                />

                                            ) : (

                                                <div className="productImagesPopup__emptyTile">
                                                    Pending
                                                </div>

                                            )}

                                            {imageRow.mockup_id &&

                                                <span className="productImagesPopup__sourceBadge">
                                                    M
                                                </span>

                                            }

                                            {!imageRow.mockup_id && imageRow.image_id &&

                                                <span className="productImagesPopup__sourceBadge">
                                                    {imageRow?.source === "S" ? "P" : "I"}
                                                </span>

                                            }

                                            <button
                                                type="button"
                                                className="productImagesPopup__removeButton"
                                                onClick={() => handleRemoveImageRow(index)}
                                                disabled={disabled}
                                                title="Remove image row"
                                            >
                                                ×
                                            </button>

                                        </div>

                                    );

                                })

                            )}

                        </div>

                    </div>

                    <div className="productImagesPopup__section productImagesPopup__section--fill">

                        <div className="productImagesPopup__sectionHeader">

                            <h3 className="productImagesPopup__sectionTitle">Image sources</h3>

                            <div className="productImagesPopup__tabs">

                                <button
                                    type="button"
                                    className={activeSourceTab === "mockups" ? "productImagesPopup__tab productImagesPopup__tab--active" : "productImagesPopup__tab"}
                                    onClick={() => setActiveSourceTab("mockups")}
                                >
                                    Mockups
                                </button>

                                <button
                                    type="button"
                                    className={activeSourceTab === "product" ? "productImagesPopup__tab productImagesPopup__tab--active" : "productImagesPopup__tab"}
                                    onClick={() => setActiveSourceTab("product")}
                                >
                                    Product Images
                                </button>

                                <button
                                    type="button"
                                    className={activeSourceTab === "bucket" ? "productImagesPopup__tab productImagesPopup__tab--active" : "productImagesPopup__tab"}
                                    onClick={() => setActiveSourceTab("bucket")}
                                >
                                    User Bucket
                                </button>

                            </div>

                        </div>

                        {activeSourceTab === "mockups" &&

                            <>

                                <div className="productImagesPopup__sectionHeader">

                                    <div className="productImagesPopup__tabs">

                                        <button
                                            type="button"
                                            className={mockupFilterMode === "matching" ? "productImagesPopup__tab productImagesPopup__tab--active" : "productImagesPopup__tab"}
                                            onClick={() => setMockupFilterMode("matching")}
                                        >
                                            Matching roles
                                        </button>

                                        <button
                                            type="button"
                                            className={mockupFilterMode === "all" ? "productImagesPopup__tab productImagesPopup__tab--active" : "productImagesPopup__tab"}
                                            onClick={() => setMockupFilterMode("all")}
                                        >
                                            All mockups
                                        </button>

                                    </div>

                                    <div className="horizontal-align-content-center">

                                        <CreateButton
                                            type="button"
                                            title="Add All"
                                            size="s"
                                            onClick={handleAddAllDisplayedMockups}
                                            disabled={disabled || !canAddAllDisplayedMockups}
                                        />

                                        <CreateButton
                                            type="button"
                                            title="Remove All"
                                            size="s"
                                            invert={true}
                                            onClick={handleRemoveAllDisplayedMockups}
                                            disabled={disabled || !canRemoveAllDisplayedMockups}
                                        />

                                    </div>

                                </div>

                                <div className="productImagesPopup__gallery">

                                    {displayedMockupConfigurations.length < 1 &&

                                        <div className="productImagesPopup__emptyGallery">
                                            {emptyMockupGalleryMessage}
                                        </div>

                                    }

                                    {displayedMockupConfigurations.map((configuration) => {

                                        // Read the canonical mockup for this exact configuration
                                        const mockup = configuration.mockup;

                                        // Check if this exact mockup configuration is selected
                                        const isSelected = isMockupConfigurationSelected(configuration);

                                        // Read the friendly aspect label
                                        const aspectLabel = VIEW_ASPECT_OPTIONS.find((option) => option.value === configuration.aspect)?.label || configuration.aspect;

                                        // Build the exact configuration label
                                        const configurationLabel = `${mockup?.name || "Mockup " + configuration.mockup_id} · ${aspectLabel} · ${configuration.rotation}°`;

                                        // Render the compatible exact mockup configuration tile
                                        return (

                                            <button
                                                key={configuration.key}
                                                type="button"
                                                className={isSelected ? "productImagesPopup__galleryTile productImagesPopup__galleryTile--selected" : "productImagesPopup__galleryTile"}
                                                onClick={() => handleToggleMockupConfiguration(configuration)}
                                                disabled={disabled}
                                                title={configurationLabel}
                                            >

                                                <img
                                                    className="productImagesPopup__tileImage"
                                                    src={mockup?.mockup_thumbnail_url || mockup?.mockup_image_url || mockup?.image_url}
                                                    alt={configurationLabel}
                                                />

                                                <span className="productImagesPopup__tileName">
                                                    {configurationLabel}
                                                </span>

                                            </button>

                                        );

                                    })}

                                </div>

                            </>

                        }

                        {activeSourceTab === "product" &&

                            <div className="productImagesPopup__gallery">

                                {safeAvailableProductImages.length < 1 &&

                                    <div className="productImagesPopup__emptyGallery">
                                        No product images are available for this product.
                                    </div>

                                }

                                {/* Show each ordinary image available from the canonical product */}
                                {safeAvailableProductImages.map((productImage, imageIndex) => {

                                    // Check whether this canonical product image is selected
                                    const isSelected = isProductImageSelected(productImage);

                                    // Build the product image label and preview
                                    const label = `Product image ${imageIndex + 1}`;
                                    const previewUrl = productImage?.thumbnail_url || productImage?.image_url || "";

                                    // Render this canonical product image tile
                                    return (

                                        <button
                                            key={`product-image-${productImage.image_id}`}
                                            type="button"
                                            className={isSelected ? "productImagesPopup__galleryTile productImagesPopup__galleryTile--selected" : "productImagesPopup__galleryTile"}
                                            onClick={() => handleToggleProductImage(productImage)}
                                            disabled={disabled}
                                            title={label}
                                        >

                                            {previewUrl ? (

                                                <img
                                                    className="productImagesPopup__tileImage"
                                                    src={previewUrl}
                                                    alt={label}
                                                />

                                            ) : (

                                                <div className="productImagesPopup__emptyTile">
                                                    Unavailable
                                                </div>

                                            )}

                                            <span className="productImagesPopup__tileName">
                                                {label}
                                            </span>

                                        </button>

                                    );

                                })}

                            </div>

                        }

                        {activeSourceTab === "bucket" &&

                            <div className="productImagesPopup__bucketManager">

                                <ImageManager
                                    view="popup"
                                    setSelectedBucketId={handleToggleBucketImage}
                                />

                            </div>

                        }

                    </div>

                </div>

                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                    <div></div>

                    <div className="horizontal-align-content-center">

                        <CreateButton type="button" title="Save Images" size="m" onClick={handleSave} disabled={disabled} />

                        <CreateButton type="button" title="Cancel" invert isCancel={true} size="m" onClick={onClose} />

                    </div>

                </div>

            </div>

        </div>

    );

};





// Product details image carousel
const ProductDetailsImageCarousel = ({

    // This is the images array
    images,

    // This controls how many placeholders we render
    numberOfPlaceHolders = 0,

    // This is the product role we are rendering for (parent or child)
    parentOrChildProduct,

    // This is the configured parent product, used by child previews so they can inherit parent placement
    parentConfiguredProduct = null,

    // Canonical Parent Product
    parentProduct,

    // Canonical Child Product
    childProduct = null,

    // This tells us if we are dealing with a child product
    isChild = false,

    // This stores the design roles for live view previews
    designRoles = [],

}) => {

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    const [imagePreviewPopupOpen, setImagePreviewPopupOpen] = useState(false);
    const [selectedImageToZoom, setSelectedImageToZoom] = useState(false);

    // This tracks the active image index
    const [activeIndex, setActiveIndex] = useState(0);

    // This ensures we always have an array
    const safeImages = useMemo(() => {

        // This returns an array or an empty array safely
        return Array.isArray(images) ? images : [];

    }, [images]);

    // This builds a fixed-length array of carousel "slots" using live views first, then saved/generated images
    const imageSlots = useMemo(() => {

        // This ensures the placeholder count is a safe non-negative integer
        const safePlaceholders = Math.max(0, Number(numberOfPlaceHolders) || 0);

        // Choose the canonical custom product for live previews
        const customProduct = isChild ? childProduct?.custom_product : parentProduct?.custom_product;

        // Store configured roles for this product or child
        const configuredRolesForPreview = Array.isArray(parentOrChildProduct?.roles) ? parentOrChildProduct.roles : [];

        // Store configured parent roles so child previews can inherit the same role
        const parentConfiguredRolesForPreview = isChild && Array.isArray(parentConfiguredProduct?.roles) ? parentConfiguredProduct.roles : [];

        // Store the role codes which currently contain usable design images
        const populatedDesignRoleKeys = new Set();

        if (Array.isArray(designRoles)) {

            designRoles.forEach((designRole) => {

                // Ignore design roles without usable image data
                if (!designRoleHasUsableImageData(designRole)) {
                    return;
                }

                // Read the role code
                const roleCode =
                    normalisePreviewString(designRole?.role);

                // Store the populated role code
                if (roleCode) {
                    populatedDesignRoleKeys.add(roleCode);
                }

            });

        }

        // Get the configured role for a live preview view
        const getConfiguredRoleForPreviewView = (view) => {

            // Get the safe role key
            const safeRole = normalisePreviewString(view?.role);

            // Find the exact role on this product or child
            const configuredRole = configuredRolesForPreview.find((roleItem) => normalisePreviewString(roleItem?.role) === safeRole) || null;

            // Use the exact child role first, otherwise inherit the exact same role from the parent
            return configuredRole || parentConfiguredRolesForPreview.find((roleItem) => normalisePreviewString(roleItem?.role) === safeRole) || null;

        };

        // Get live preview views for populated design roles
        const previewViews = getProductPreviewViews(customProduct).filter((view) => {

            // Read the design role belonging to this view
            const roleCode =
                normalisePreviewString(view?.role);

            // Do not display views for design roles without an image
            if (!populatedDesignRoleKeys.has(roleCode)) {
                return false;
            }

            // Get the configured role for this view
            const configuredRole =
                getConfiguredRoleForPreviewView(view);

            // Return whether this populated role allows the view
            return previewViewIsAllowedForConfiguredRole(
                view,
                configuredRole
            );

        });

        // Build combined live view and image entries
        const previewEntries = getProductPreviewEntries(previewViews, safeImages);

        // This decides how many slots we want in total
        const totalSlots = Math.max(safePlaceholders, previewEntries.length);

        // This creates an array of slots with either preview data or placeholder metadata
        return Array.from({ length: totalSlots }, (_, idx) => {

            // This grabs the preview entry at this index if it exists
            const previewEntry = previewEntries[idx];

            // If there is a preview entry here, return it
            if (previewEntry) {

                return {
                    ...previewEntry,
                    index: idx,
                };

            }

            // Otherwise return a placeholder slot
            return {

                // This marks the slot as a placeholder
                type: "placeholder",

                // This stores the index
                index: idx,

                // This provides a stable placeholder key
                placeholderKey: `placeholder-${idx}`,

            };

        });

    }, [
        safeImages,
        numberOfPlaceHolders,
        isChild,
        childProduct,
        parentProduct,
        parentOrChildProduct,
        parentConfiguredProduct,
        designRoles,
    ]);

    // This prevents activeIndex pointing at nothing if inputs shrink
    useEffect(() => {

        // This clamps the active index to the valid range
        setActiveIndex((prev) => {

            // This calculates the max valid index
            const maxIndex = Math.max(0, imageSlots.length - 1);

            // This clamps prev into range
            return Math.min(Math.max(0, prev), maxIndex);

        });

    }, [imageSlots.length]);

    // This reads the active slot safely
    const activeSlot = imageSlots[activeIndex];

    // This reads the active image if the slot is a real image
    const activeImage = activeSlot?.type === "image" ? activeSlot.img : null;

    // This reads the active view if the slot is a live view
    const activeView = activeSlot?.type === "view" ? activeSlot.view : null;

    // This chooses the canonical custom product safely
    const canonicalCustomProduct = isChild ? childProduct?.custom_product : parentProduct?.custom_product;

    // This stores the configured parent role rows
    const safeParentConfiguredRoles = Array.isArray((isChild ? parentConfiguredProduct : parentOrChildProduct)?.roles)
        ? (isChild ? parentConfiguredProduct : parentOrChildProduct).roles
        : [];

    // This stores the configured child role rows
    const safeChildConfiguredRoles = isChild && Array.isArray(parentOrChildProduct?.roles) ? parentOrChildProduct.roles : [];

    // This stores the design role rows
    const safeDesignRoles = Array.isArray(designRoles) ? designRoles : [];

    // This gets the configured parent role for a live preview view
    const getParentConfiguredRoleForView = (view) => {

        // Get the safe role key
        const safeRole = normalisePreviewString(view?.role);

        // Return only the matching configured parent role
        return safeParentConfiguredRoles.find((roleItem) => normalisePreviewString(roleItem?.role) === safeRole) || null;

    };

    // This gets the configured child role for a live preview view
    const getChildConfiguredRoleForView = (view) => {

        // Get the safe role key
        const safeRole = normalisePreviewString(view?.role);

        // Return only the matching configured child role
        return safeChildConfiguredRoles.find((roleItem) => normalisePreviewString(roleItem?.role) === safeRole) || null;

    };

    // This gets the configured role used for settings/fallback display
    const getConfiguredRoleForView = (view) => {

        // Return child role first for settings, then parent role
        return getChildConfiguredRoleForView(view) || getParentConfiguredRoleForView(view);

    };

    // This gets the child role data to pass to the preview module
    const getPreviewChildRoleForView = (view) => {

        // Get the configured child role
        const childRole = getChildConfiguredRoleForView(view);

        // Only pass child role data when the child has its own edited placement
        return roleHasEditedPlacement(childRole) ? childRole : null;

    };

    // This gets the parent role data to pass to the preview module
    const getPreviewParentRoleForView = (view) => {

        // Always pass the configured parent role so unedited children inherit parent placement and scale
        return getParentConfiguredRoleForView(view);

    };

    // This gets the effective role for fit/fill display options
    const getPreviewFitFillRoleForView = (view) => {

        // Use edited child role first, otherwise use parent role
        return getPreviewChildRoleForView(view) || getPreviewParentRoleForView(view) || getConfiguredRoleForView(view);

    };

    // This gets the populated design role for a live preview view
    const getDesignRoleForView = (view) => {

        // Get the safe role key
        const safeRole =
            normalisePreviewString(view?.role);

        // Find only the exact corresponding design role
        const matchingDesignRole =
            safeDesignRoles.find((roleItem) => {
                return (
                    normalisePreviewString(roleItem?.role) ===
                    safeRole
                );
            });

        // Never pass configured placement data as though it were a design image
        if (!designRoleHasUsableImageData(matchingDesignRole)) {
            return null;
        }

        // Return the populated design role
        return matchingDesignRole;

    };

    // Handle click to zoom on main carousel image
    const openImagePreviewPopup = (mainImageUrl) => {

        // Set the selected image to the one we've passed in (clicked on)
        setSelectedImageToZoom(mainImageUrl);

        // Open the large image preview pop up
        setImagePreviewPopupOpen(true);

    };



    // This returns the carousel UI
    return (

        <>

            {/* This wraps the carousel */}
            <div className="imageCarousel">

                {/* This renders the main image */}
                <div className="imageCarousel__main">

                    {/* This renders the main display box */}
                    <div
                        className="imageCarousel__img"
                        role="img"
                        aria-label={activeImage ? "Product image" : "Placeholder image"}
                    >

                        {// Check if the selected entry is a live view
                        activeView ? (

                            <div className="imageCarousel__liveView">

                                <PodProductPreview
                                    thedesign={getDesignRoleForView(activeView)}
                                    product={canonicalCustomProduct}
                                    child={isChild ? canonicalCustomProduct : null}
                                    currentProductRoleData={getPreviewParentRoleForView(activeView)}
                                    currentProductChildRoleData={isChild ? getPreviewChildRoleForView(activeView) : null}
                                    mockup_height={isMobile ? 200 : 466}
                                    options={{
                                        display_mode: "product",
                                        product_fit_fill: getPreviewFitFillRoleForView(activeView)?.fit_fill || null,
                                        fit_or_fill: getPreviewFitFillRoleForView(activeView)?.fit_fill || null,
                                        surface_code: normalisePreviewString(activeView?.surface_code) || "default",
                                        print_area_code: normalisePreviewString(activeView?.print_area_code) || "default",
                                        view_style: getPreviewViewStyle(activeView) || "default",
                                        view_name: getPreviewViewName(activeView),
                                        selected_colourway_code: parentOrChildProduct?.default_colourway || "",
                                    }}
                                />

                            </div>

                        ) :

                        // Check if there are no images or live views
                        imageSlots.length === 0 ?

                        <div>

                            Currently no images or live views to display, click "Manage Images" below to add images.

                        </div>

                        :

                        (

                            <>

                                {/* Check if we have a mockup_image_url - if we do then there is no design roles data we just want to show the blank mockups */}
                                {activeImage?.mockup_image_url ?

                                    // Display the blank mockup image when its URL is available
                                    activeImage?.mockup_image_url ? (

                                        // This renders the actual image
                                        <img
                                            className="imageCarousel__realImg"
                                            src={activeImage.mockup_image_url}
                                            alt={activeImage?.label || `Image ${activeIndex + 1}`}
                                            title="🔍 Click to Zoom"
                                            onClick={() => openImagePreviewPopup(activeImage.mockup_image_url)}
                                        />

                                    ) : (

                                        // Otherwise render placeholder text / label
                                        <span className="imageCarousel__imgText">
                                            <LoadingSpinner />
                                        </span>

                                    )

                                    :

                                    // Display the generated image when its URL is available
                                    activeImage?.image_url ? (

                                        // This renders the actual image
                                        <img
                                            className="imageCarousel__realImg"
                                            src={activeImage?.image_url}
                                            alt={activeImage?.label || `Image ${activeIndex + 1}`}
                                            title="🔍 Click to Zoom"
                                            onClick={() => openImagePreviewPopup(activeImage?.image_url)}
                                        />

                                    ) : (

                                        // Otherwise render placeholder text / label
                                        <span className="imageCarousel__imgText">
                                            <LoadingSpinner />
                                        </span>

                                    )

                                }

                            </>

                        )}

                    </div>

                </div>

                {/* This renders the thumbnail row */}
                <div className="imageCarousel__thumbs">

                    {/* This maps each slot to a thumb button */}
                    {imageSlots.map((slot) => {

                        // This calculates if the slot is active
                        const isActive = slot.index === activeIndex;

                        // This reads the image if it exists
                        const slotImage = slot.type === "image" ? slot.img : null;

                        // This builds a stable key
                        const key = slotImage?.id ?? slot.placeholderKey ?? `slot-${slot.index}`;

                        // This returns the thumbnail button
                        return (

                            // This is the thumbnail button
                            <button
                                key={key}
                                type="button"
                                className={
                                    isActive
                                        ? "imageCarousel__thumb imageCarousel__thumb--active"
                                        : "imageCarousel__thumb"
                                }
                                onClick={() => setActiveIndex(slot.index)}
                                aria-label={`Select image ${slot.index + 1}`}
                            >

                                {/* This is the thumbnail preview box */}
                                <span className="imageCarousel__thumbBox">

                                    {slot.type === "view" ? (

                                        <span className="imageCarousel__viewThumb">

                                            <PodProductPreview
                                                thedesign={getDesignRoleForView(slot.view)}
                                                product={canonicalCustomProduct}
                                                child={isChild ? canonicalCustomProduct : null}
                                                currentProductRoleData={getPreviewParentRoleForView(slot.view)}
                                                currentProductChildRoleData={isChild ? getPreviewChildRoleForView(slot.view) : null}
                                                mockup_height={isMobile ? 60 : 136}
                                                options={{
                                                    display_mode: "product",
                                                    product_fit_fill: getPreviewFitFillRoleForView(slot.view)?.fit_fill || null,
                                                    fit_or_fill: getPreviewFitFillRoleForView(slot.view)?.fit_fill || null,
                                                    surface_code: normalisePreviewString(slot.view?.surface_code) || "default",
                                                    print_area_code: normalisePreviewString(slot.view?.print_area_code) || "default",
                                                    view_style: getPreviewViewStyle(slot.view) || "default",
                                                    view_name: getPreviewViewName(slot.view),
                                                    selected_colourway_code: parentOrChildProduct?.default_colourway || "",
                                                }}
                                            />

                                        </span>

                                    ) :

                                    /* Check if we have a mockup_image_url - if we do then there is no design roles data we just want to show the blank mockups */
                                    slotImage?.mockup_image_url ?

                                        // Display the blank mockup thumbnail when its URL is available
                                        slotImage?.mockup_image_url ? (

                                            // This renders the thumbnail image
                                            <img
                                                className="imageCarousel__thumbImg"
                                                src={slotImage.mockup_thumbnail_url || slotImage.mockup_image_url}
                                                alt={slotImage?.short || `Thumbnail ${slot.index + 1}`}
                                            />

                                        ) : (

                                            // Otherwise show a placeholder number (or short label if you want)
                                            <span className="imageCarousel__thumbText">

                                                {/* This renders the placeholder number 
                                        {slotImage?.short || `#${slot.index + 1}`}
                                        */}
                                                <LoadingDotsAnimation />

                                            </span>

                                        )

                                        :

                                        // Display the generated thumbnail when its URL is available
                                        (slotImage?.thumbUrl || slotImage?.image_url) ? (

                                            // This renders the thumbnail image
                                            <img
                                                className="imageCarousel__thumbImg"
                                                src={slotImage.thumbUrl || slotImage.image_url}
                                                alt={slotImage?.short || `Thumbnail ${slot.index + 1}`}
                                            />

                                        ) : (

                                            // Otherwise show a placeholder number (or short label if you want)
                                            <span className="imageCarousel__thumbText">

                                                {/* This renders the placeholder number 
                                        {slotImage?.short || `#${slot.index + 1}`}
                                        */}
                                                <LoadingDotsAnimation />

                                            </span>

                                        )

                                    }

                                </span>

                            </button>

                        );

                    })}

                </div>

            </div>

            {/* Pop up to show carousel of print area images */}
            {imagePreviewPopupOpen && (

                <ImageCarouselPopup
                    images={images}
                    initialImage={selectedImageToZoom}
                    onClose={() => setImagePreviewPopupOpen(false)}
                />

            )}

        </>

    );

};





// Pop up for use th eimage placemnet editor
const ImagePlacementPopup = ({
    title,
    subTitle,
    onClose,
    onSave,
    product,
    child,
    isChild,
    role,
    //currentChildRole,
    currentProductRole,
    //currentDesignRole, 
    latestTransform,
    setLatestTransform,
    currentProduct,
    podDesignDetails,
    saveTransformDataToProductRole,

    // Whether the role and replacement image rows are currently being saved
    saving = false,
}) => {

    // Store the placement editor reset handler so the footer can trigger it
    const resetPlacementHandlerRef = useRef(null);

    // Store the rotation of the product view used by the placement editor
    const placementViewRotationRef = useRef(0);

    // This receives the reset handler from the placement editor
    const handleRegisterPlacementReset = useCallback((handler) => {

        // Store the latest reset handler
        resetPlacementHandlerRef.current = typeof handler === "function" ? handler : null;

    }, []);



    // This handles image placement editor updates
    const handleImagePlacementTransformChange = useCallback((payload) => {

        // This forces a safe object even if payload is null/undefined
        const safePayload = payload ?? {};

        // This forces the correct sku every time
        const safeProductSku = String(product?.sku || "");

        // This forces the correct child sku every time
        const safeChildSku = String(child?.sku || "");

        // This forces the correct print-area role every time
        const safeRoleKey = String(role?.role || "");

        // This stores the latest transform only if it actually changed
        setLatestTransform((previousValue) => {

            // This builds the next value
            const nextValue = {

                // This keeps any transform fields
                ...safePayload,

                // This guarantees sku is correct
                product_sku: safeProductSku,

                // This guarantees child sku is correct
                child_sku: safeChildSku,

                // This guarantees role is correct
                role: safeRoleKey,

                // This records the product view rotation used to establish the placement
                initial_view_rotation: placementViewRotationRef.current,

            };

            // This prevents pointless rerenders
            if (JSON.stringify(previousValue || {}) === JSON.stringify(nextValue || {})) {

                // This keeps previous state
                return previousValue;

            }

            // This stores the new value
            return nextValue;

        });

    }, [
        product?.sku,
        child?.sku,
        role?.role,
        setLatestTransform,
    ]);

    // Get the safe parent product sku
    const safeSku = product?.sku || null;

    // Get the safe child sku
    const safeChildSku = child?.sku || null;

    // Get the safe role (aka exterior_front_main)
    const safeRole = role?.role || null;

    // Set where we want to get the asset data from (the canonical parent or the canonical child)
    const childOrParentProduct = isChild ? child : product;

    // Declare the variables you need
    let surface, print_area;

    // Create a safe surfaces array (prevents crashes)
    const safeSurfaces = childOrParentProduct.custom_product?.surfaces || [];

    // If there are no surfaces, return early (nothing we can do)
    if (safeSurfaces.length === 0) return null;

    // Loop through surfaces to find the first surface that contains a matching print area
    for (let s = 0; s < safeSurfaces.length; s++) {

        // Set the surface for this loop
        const currentSurface = safeSurfaces[s];

        // Create a safe print areas array for this surface
        const currentPrintAreas = currentSurface?.print_areas || [];

        // Skip this surface if it has no print areas
        if (currentPrintAreas.length === 0) continue;

        // Try to find a matching print area by role
        const matchedPrintArea = currentPrintAreas.find((pa) => pa?.role === role?.role);

        // If we found a match, store them and break early
        if (matchedPrintArea) {

            // Set the chosen surface
            surface = currentSurface;

            // Set the chosen print area
            print_area = matchedPrintArea;

            // Break out of the loop early
            break;

        }


        // If no matching print area was found anywhere, fallback to first surface and first print area
        if (!print_area) {

            // Fallback surface
            surface = safeSurfaces[0];

            // Create a safe print areas array for the fallback surface
            const fallbackPrintAreas = surface?.print_areas || [];

            // If fallback surface has no print areas, return early
            if (fallbackPrintAreas.length === 0) return null;

            // Fallback print area
            print_area = fallbackPrintAreas[0];

        }

    }

    // Ensure views is an array
    const views = Array.isArray(print_area?.views) ? print_area.views : [];

    // Find the configured child product when editing a child
    const placementChildProduct = isChild && Array.isArray(currentProduct?.children)
        ? currentProduct.children.find((childItem) => String(childItem?.pod_product_sku || childItem?.sku || "") === String(child?.sku || "")) || null
        : null;

    // Find the configured role whose view settings control this placement editor
    const placementConfiguredRole = isChild
        ? placementChildProduct?.roles?.find((roleItem) => String(roleItem?.role || "") === String(role?.role || "")) || currentProductRole
        : currentProductRole;

    // Read the allowed view settings for this configured role
    const placementViewSettings = normaliseRoleViewSettings(placementConfiguredRole?.view_settings);

    // Keep only actual active views allowed by the configured role
    const compatibleViews = views.filter((viewItem) => {

        // Skip inactive views
        if (String(viewItem?.active || "Y").toUpperCase() === "N") { return false; }

        // Read the supported view aspects and exact view rotation
        const viewAspects = normaliseProductViewAspects(viewItem);
        const viewRotation = getPreviewViewRotation(viewItem);

        // Check whether this real view shares an aspect with the configured role
        const aspectAllowed = placementViewSettings.aspects.some((aspect) => viewAspects.includes(aspect));

        // Return whether this view is allowed
        return aspectAllowed && placementViewSettings.rotations.includes(viewRotation);

    });

    // Use the same configured-view order as the product image carousel
    const orderedCompatibleViews = [...compatibleViews].sort((firstView, secondView) => {
        return getPreviewViewPriority(firstView) - getPreviewViewPriority(secondView);
    });

    // Use the first view actually available to this configured design product role
    const view = orderedCompatibleViews[0];

    // Guard if view missing
    if (!view) {

        return null;

    };

    // Store the rotation of the product view used by the placement editor
    placementViewRotationRef.current = getPreviewViewRotation(view);

    // Ensure assets is an array
    const assets = Array.isArray(view?.assets) ? view.assets : [];

    // Find preview asset
    const previewAsset = assets.find((a) => a?.type === "preview");

    // This finds the current design role
    const currentDesignRole =
        Array.isArray(podDesignDetails?.roles)
            ? podDesignDetails.roles.find((roleItem) => String(roleItem?.role || "") === String(role?.role || ""))
            : null;

    // This finds the current child object
    const currentChildProduct =
        Array.isArray(currentProduct?.children)
            ? currentProduct.children.find((childItem) => String(childItem?.pod_product_sku || childItem?.sku || "") === String(child?.sku || ""))
            : null;

    // This finds the child role data
    const currentChildRole =
        Array.isArray(currentChildProduct?.roles)
            ? currentChildProduct.roles.find((roleItem) => String(roleItem?.role || "") === String(role?.role || ""))
            : null;

    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--medium">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            {title}

                        </h2>


                        {/* This is the parent subtitle */}
                        {subTitle &&
                            <p className="popup__subtitle">

                                {subTitle}

                            </p>
                        }

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button
                            type="button"
                            className="popup__close"
                            onClick={() => {

                                // Do not close while the placement and image jobs are being saved
                                if (saving) { return; }

                                // Close the placement editor
                                onClose?.();

                            }}
                            disabled={saving}
                            title="Close"
                        >
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content" style={{ height: "calc(100% - 80px)" }}>

                    <div className="image-placement-editor" style={{ padding: "10px", height: "100%", width: "100%" }}>

                        <ImagePlacementEditor

                            // This passes parent product sku
                            productSku={safeSku}

                            // This passes child sku when relevant
                            childSku={safeChildSku}

                            // This passes the role key
                            role={safeRole}

                            // This passes active state
                            active={currentProductRole?.active ? currentProductRole?.active : "Y"}

                            // This passes the preview asset
                            asset={previewAsset}

                            // This passes the print area settings
                            printAreaSettings={print_area.settings || []}

                            // This passes the design image
                            userImageUrl={currentDesignRole?.image_url || ""}

                            // This passes natural dimensions
                            naturalWidth={currentDesignRole?.design_width}
                            naturalHeight={currentDesignRole?.design_height}

                            // This passes fit fill from design role only
                            fit_fill={currentDesignRole?.fit_fill || "fit"}

                            // This passes child role data only when editing a child
                            childRoleData={isChild ? currentChildRole : null}

                            // This passes parent product role data
                            parentRoleData={currentProductRole || null}

                            // This passes design role data
                            designRoleData={currentDesignRole || null}

                            // This uses the memoised callback
                            onTransformChange={handleImagePlacementTransformChange}

                            // This lets the popup footer trigger reset
                            onResetPlacementReady={handleRegisterPlacementReset}

                        />

                    </div>

                </div>

                {/* Footer actions */}
                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                    <div></div>

                    <div className="horizontal-align-content-center">

                        <CreateButton
                            type="button"
                            title="Reset"
                            size="m"
                            invert={true}
                            disabled={saving}
                            onClick={() => resetPlacementHandlerRef.current?.()}
                        />

                        <CreateButton
                            type="button"
                            title={saving ? "Saving..." : "Save"}
                            size="m"
                            disabled={saving}
                            onClick={async () => {

                                // Save the transform into the single live product state
                                const saveResult = saveTransformDataToProductRole(latestTransform);

                                // Save the configured role and queue replacement mockup images
                                await onSave(saveResult);

                            }}
                        />

                        <CreateButton
                            type="button"
                            isCancel={true}
                            invert={true}
                            title="Cancel"
                            size="m"
                            disabled={saving}
                            onClick={() => onClose()}
                        />

                    </div>

                </div>

            </div>

        </div>

    );

};





// This renders the view settings controls for a print area role
const RoleViewSettingsControls = ({
    roleData,
    disabled = false,
    onChange,
}) => {

    // Get safe role view settings
    const viewSettings = normaliseRoleViewSettings(roleData?.view_settings);

    // Count selected aspects

    // Count selected rotations

    // Toggle one settings value
    const toggleSettingValue = (settingKey, selectedValue) => {

        // Create a set from the current setting array
        const selectedValuesSet = new Set(viewSettings[settingKey]);

        // Check if the value is already selected
        const valueIsSelected = selectedValuesSet.has(selectedValue);

        // Remove the value when it is already selected
        if (valueIsSelected) {

            // Delete the selected value
            selectedValuesSet.delete(selectedValue);

        } else {

            // Add the selected value
            selectedValuesSet.add(selectedValue);

        }

        // Send updated settings to the parent
        onChange?.({
            aspects: settingKey === "aspects" ? Array.from(selectedValuesSet) : viewSettings.aspects,
            rotations: settingKey === "rotations" ? Array.from(selectedValuesSet) : viewSettings.rotations,
        });

    };

    // Render one option group
    const renderOptionGroup = ({ title, description, settingKey, options }) => {

        // Build a stable info popover key
        const infoKey = `view-settings-${settingKey}-${roleData?.role || "role"}`;

        // Return the option group UI
        return (

            // This is one compact setting row
            <div className="roleViewSettings__group">

                {/* This is the group label */}
                <div className="roleViewSettings__groupHeader">

                    {/* This is the setting title */}
                    <span className="roleViewSettings__groupTitle">{title}</span>

                    {/* This is the info button */}
                    <InfoButton
                        settingCode={infoKey}
                        shortLabel={`${title} view setting`}
                        description={description}
                    />

                </div>

                {/* This is the chip list */}
                <div className="selectable__chips roleViewSettings__chips">

                    {options.map((option) => {

                        // Check if this option is selected
                        const isSelected = viewSettings[settingKey].includes(option.value);

                        // Return one selectable chip
                        return (

                            // This is one chip button
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled}
                                className={`selectable__chip ${isSelected ? "selectable__chip--selected" : ""}`}
                                onClick={() => toggleSettingValue(settingKey, option.value)}
                                title={isSelected ? `Remove ${option.label}` : `Allow ${option.label}`}
                            >

                                {/* This is the visual tick */}
                                <span className="selectable__chipTick">{isSelected ? "✓" : "+"}</span>

                                {/* This is the option label */}
                                <span className="selectable__chipLabel">{option.label}</span>

                            </button>

                        );

                    })}

                </div>

            </div>

        );

    };




    // Return the full control
    return (

        <div className="roleRow__views">

            {/* This is the view settings title */}
            <div className="roleViewSettings__header">

                <span className="roleViewSettings__title">Views</span>

            </div>

            {/* This renders aspect options */}
            {renderOptionGroup({
                title: "Aspect",
                description: "Choose which product view shapes can be shown as live product views for this print area.",
                settingKey: "aspects",
                options: VIEW_ASPECT_OPTIONS,
            })}

            {/* This renders rotation options  */}
            {renderOptionGroup({
                title: "Rotation",
                description: "Choose which rotated versions can be shown as live product views for this print area.",
                settingKey: "rotations",
                options: VIEW_ROTATION_OPTIONS,
            })}

        </div>

    );

};
         




// This is a reusable panel to show role toggles + edit placement button
const PrintAreasPanel = ({

    // Design parent product's roles
    productRoles,

    // Design child products's roles
    childProductRoles = [],

    // The selected (chosen) roles in the design set
    selectedRoles,

    // Panel card
    title,

    // All global roles
    printAreaRoles,

    // Canonical Product
    product,

    // Canonical Child
    child = null,

    // Is child flag
    isChild = false,

    // Pod design details and function to update
    podDesignDetails,

    // This is the current design product
    currentProduct,

    // Configured parent product id
    configuredProductId = "",

    // NEW: parent handler to open the placement editor overlay
    handleOpenImagePlacementEditor,

    // Save role view settings and their compatible image rows
    onRoleViewSettingsChange,

    // Configured product currently saving role view settings
    savingRoleViewSettingsTargetId = "",

    // Reset one edited print area placement
    onResetPlacement,

    // Whether a placement is currently being saved
    savingPlacement = false,

}) => {

    // This ensures roles is always an array
    const safeProductRoles = Array.isArray(productRoles) ? productRoles : [];

    // This ensures roles is always an array
    const safeChildProductRoles = Array.isArray(childProductRoles) ? childProductRoles : [];

    // Ensure the design roles is always an array
    const safeDesignRoles =
        Array.isArray(podDesignDetails?.roles)
            ? podDesignDetails.roles
            : [];

    // Ensure the printable-area selector state is always an array
    const safeSelectedRoles =
        Array.isArray(selectedRoles)
            ? selectedRoles
            : [];

    // Choose the canonical parent or child whose print area order is being displayed
    let previewCanonicalProduct = product;
    if (isChild) { previewCanonicalProduct = child; }

    // Read this product's canonical surfaces in the order supplied by the API
    let canonicalSurfaces = [];
    if (Array.isArray(previewCanonicalProduct?.custom_product?.surfaces)) {
        canonicalSurfaces = previewCanonicalProduct.custom_product.surfaces;
    }

    // Build a lookup containing only printable areas selected on this design
    const selectedRoleKeys = new Set();

    safeSelectedRoles.forEach((selectedRole) => {

        // Ignore unselected printable areas
        if (!selectedRole?.selected) { return; }

        // Store the selected role key
        const selectedRoleKey = String(selectedRole?.role || "");

        if (selectedRoleKey) {
            selectedRoleKeys.add(selectedRoleKey);
        }

    });

    // Build populated design roles in this custom product's surface and print area order
    const orderedDesignRoles = [];
    const addedRoleKeys = new Set();

    canonicalSurfaces.forEach((surface) => {

        // Read this surface's print areas in API order
        let printAreas = [];

        if (Array.isArray(surface?.print_areas)) {
            printAreas = surface.print_areas;
        }

        printAreas.forEach((printArea) => {

            // Read the canonical print area role key
            const roleKey = String(printArea?.role || "");

            // Skip print areas without a role, unselected roles, or duplicate role rows
            if (!roleKey || !selectedRoleKeys.has(roleKey) || addedRoleKeys.has(roleKey)) { return; }

            // Find the populated design role for this canonical print area
            const matchingDesignRole = safeDesignRoles.find((designRole) => String(designRole?.role || "") === roleKey);

            // Do not create rows for empty or removed printable areas
            if (!matchingDesignRole) { return; }

            // Add this role in the custom product's print area order
            orderedDesignRoles.push(matchingDesignRole);
            addedRoleKeys.add(roleKey);

        });

    });

    // This stores the exact configured parent or child identity used by the API endpoints
    const exactConfiguredProductId = String(currentProduct?.id || currentProduct?.client_configured_product_id || configuredProductId || "");



    // This renders the panel
    return (

        // This is the card
        <div className="card">

            {/* This is the header */}
            <div className="card__header">

                {/* This is the title */}
                <h3 className="card__title">{title}</h3>

                {/* This is a hint */}
                <span className="hintText">{designUsesTemplates(podDesignDetails) ? "Remove all templates to edit print areas" : "Toggle areas or edit design placement"}</span>

            </div>

            {/* This is the role list */}
            <div className="roleList">

                {/* Render populated roles in the normal printable-area order */}
                {orderedDesignRoles.map((roleItem) => {

                    // This is the design's role
                    const designRole = roleItem;

                    // This is the design parent product's role
                    const currentProductRole = safeProductRoles.find((r) => r?.role === roleItem?.role) || null;

                    // This is the design child product's role
                    const currentProductChildRole = safeChildProductRoles.find((r) => r?.role === roleItem?.role) || null;

                    // Only use child placement data when this child role has been edited directly
                    const currentProductChildPreviewRole = isChild && roleHasEditedPlacement(currentProductChildRole) ? currentProductChildRole : null;

                    // Use child fit/fill only when this child role has been edited directly, otherwise inherit from the parent
                    const currentProductPreviewRole = currentProductChildPreviewRole || currentProductRole;

                    // Set the safe role key (aka exterior_front_main)
                    const safeRoleKey = roleItem?.role || null;

                    // Use the child role only while rendering a child product
                    const childOrParentRole = isChild ? currentProductChildRole : currentProductRole;

                    // Check whether this exact parent or child print area has an edited placement
                    const currentRoleIsEdited = String(childOrParentRole?.edited || "N").toUpperCase() === "Y";

                    // Get the API-calculated print quality for this exact configured role
                    const currentPrintQuality = childOrParentRole?.print_quality || null;

                    // Normalise the print quality status for display styling
                    const currentPrintQualityStatus = String(currentPrintQuality?.status || "").toLowerCase();

                    // Grouping parents are not purchasable and therefore do not need PPI warnings
                    const currentProductIsPurchasable =
                        isChild ||
                        !Array.isArray(product?.children) ||
                        product.children.length === 0;

                    // Check whether this purchasable product's active print area has a PPI problem
                    const currentPrintQualityHasIssue =
                        currentProductIsPurchasable &&
                        childOrParentRole?.active !== "N" &&
                        (currentPrintQualityStatus === "warning" || currentPrintQualityStatus === "fail");

                    // Find the canonical print area assigned to this role
                    let currentPrintArea = null;

                    for (const canonicalSurface of canonicalSurfaces) {

                        // Read this surface's print areas safely
                        const canonicalPrintAreas = Array.isArray(canonicalSurface?.print_areas)
                            ? canonicalSurface.print_areas
                            : [];

                        // Find the print area assigned to this role
                        currentPrintArea = canonicalPrintAreas.find((printArea) => printArea?.role === safeRoleKey) || null;

                        // Stop once the assigned print area has been found
                        if (currentPrintArea) { break; }

                    }

                    // Get the recommended image resolution for this print area
                    const currentRecommendedPpi = currentPrintArea?.settings?.recommended_ppi || null;

                    // Build the print quality explanation shown from the warning triangle
                    let currentPrintQualityMessage = "";

                    if (currentPrintQualityHasIssue) {

                        currentPrintQualityMessage = `The calculated PPI for this print area is ${currentPrintQuality?.ppi}.`;

                        if (currentRecommendedPpi) {
                            currentPrintQualityMessage += ` It should be ${currentRecommendedPpi} or higher. Consider using the AI upscale tool on the design to improve this.`;
                        }

                    }

                    // Get the first live view allowed by this configured design product role
                    const firstConfiguredView = getFirstConfiguredProductPreviewView(
                        previewCanonicalProduct,
                        childOrParentRole,
                        safeRoleKey
                    );

                    // Check if this product has a matching role
                    const displayRole = hasMatchingRole(isChild ? child : product, roleItem);

                    // Return ealy if there's no matching product role 
                    if (!displayRole) {

                        return null;

                    }

                    // This reads the role title safely (aka Front, Back)
                    const roleName = printAreaRoles?.find((r) => r.role === roleItem.role)?.name || roleItem.role;

                    // This renders one row
                    return (

                        // This is one role row
                        <div className="roleRow" key={roleItem.role}>

                            {/* This is the role identity block */}
                            <div className="roleRow__identity">

                                {/* This is the role image */}
                                <div className="roleRow__image">

                                {firstConfiguredView &&

                                    <PodProductPreview
                                        thedesign={designRole}
                                        product={product.custom_product}
                                        child={child?.custom_product}
                                        currentProductRoleData={currentProductRole}
                                        currentProductChildRoleData={currentProductChildPreviewRole}
                                        mockup_height={60}
                                        options={{
                                            display_mode: "product",
                                            product_fit_fill: currentProductPreviewRole?.fit_fill ? currentProductPreviewRole.fit_fill : null,
                                            fit_or_fill: currentProductPreviewRole?.fit_fill ? currentProductPreviewRole.fit_fill : null,
                                            surface_code: firstConfiguredView.surface_code || "default",
                                            print_area_code: firstConfiguredView.print_area_code || "default",
                                            view_style: firstConfiguredView.style || "default",
                                            view_name: firstConfiguredView.name || "",
                                            selected_colourway_code: currentProduct?.default_colourway || "",
                                        }}
                                    />

                                }

                                </div>

                            </div>

                            {/* This is the role label */}
                            <div className="roleRow__label">

                                <div className="roleRow__nameLine">

                                    {/* Explain warning or failed image resolution without cluttering the row */}
                                    {currentPrintQualityHasIssue &&

                                        <InfoButton
                                            buttonIcon={
                                                <WarningIcon
                                                    className={`printQualityWarningIcon printQualityWarningIcon--${currentPrintQualityStatus}`}
                                                />
                                            }
                                            shortLabel="Print quality warning"
                                            description={currentPrintQualityMessage}
                                        />

                                    }

                                    <span className="roleRow__name">{roleName}</span>

                                </div>

                                {/* <span className="roleRow__key">{roleItem.role}</span> */}

                            </div>

                            {/* This shows when the print area has edited design placement */}
                            <div className="roleRow__label">

                                {currentRoleIsEdited &&

                                    <button
                                        type="button"
                                        className="pod-pricing-page__plan-badge pod-design__edited-badge"
                                        title="This print area has edited design placement. Click to reset it."
                                        aria-label={`Reset ${roleName} placement`}
                                        disabled={Boolean(designUsesTemplates(podDesignDetails)) || savingPlacement}
                                        onClick={() => {

                                            // Reset only this exact parent or child print area
                                            onResetPlacement?.({
                                                product: product,
                                                child: child,
                                                isChild: isChild,
                                                role: safeRoleKey,
                                                roleName: roleName,
                                                currentRole: childOrParentRole,
                                            });

                                        }}
                                    >
                                        E
                                    </button>

                                }

                            </div>

                            {/* This is the edit placement button */}
                            <div className="roleRow__actions">

                                {!designUsesTemplates(podDesignDetails) &&

                                    <CreateButton
                                        //title="Edit ✥"
                                        title={
                                            <div className="horizontal-align-content-center">
                                                Edit
                                                <MoveArrows
                                                    size={24}
                                                    fillColour="#fff"
                                                    className="global-icon global-icon--light sidebar__item-icon"
                                                />
                                            </div>
                                        }
                                        size="s"
                                        type="button"
                                        onClick={() => {

                                            // This calls the parent to open the placement editor overlay
                                            handleOpenImagePlacementEditor?.({

                                                // This is the display title
                                                title: "Design Placement Editor",

                                                // This is the subtitle
                                                subTitle: `${roleName}  •  ${isChild ? child.sku : product.sku}`,

                                                // These tell the popup what it is editing
                                                product: product,
                                                child: child,
                                                isChild: isChild,

                                                // These provide role data
                                                role: childOrParentRole || designRole,
                                                currentChildRole: currentProductChildRole,
                                                currentProductRole: currentProductRole,
                                                currentDesignRole: designRole,

                                            });

                                        }}
                                    />

                                }

                            </div>
                          
                            {/* This is the toggle */}
                            <div className="roleRow__toggle">

                                <ToggleSwitch
                                    title={

                                        isChild && currentProductChildRole?.active ?

                                            S2B(currentProductChildRole.active) ? "Disable Print Area"
                                                :
                                                "Enable Print Area"

                                            : currentProductRole?.active ?

                                                S2B(currentProductRole.active) ? "Disable Print Area"
                                                    :
                                                    "Enable Print Area"

                                                : ""


                                    }
                                    value={isChild ? currentProductChildRole?.active ? S2B(currentProductChildRole.active) : true : currentProductRole?.active ? S2B(currentProductRole.active) : true}
                                    size="s"
                                    label=""
                                    disabled={
                                        designUsesTemplates(podDesignDetails)
                                            ? true
                                            : Boolean(savingRoleViewSettingsTargetId)
                                    }
                                    onChange={(event) => {

                                        // Save the active state and queue replacement mockups for this exact parent or child
                                        onRoleViewSettingsChange?.({
                                            configuredProductId: exactConfiguredProductId,
                                            canonicalProduct: isChild ? child : product,
                                            targetRole: safeRoleKey,
                                            nextActive: B2S(event.target.value),
                                            regenerateMockupImages: true,
                                        });

                                    }}
                                />

                            </div>


                            {/* This controls which views are available for this configured product role */}
                            <RoleViewSettingsControls
                                roleData={childOrParentRole}
                                disabled={
                                    designUsesTemplates(podDesignDetails)
                                        ? true
                                        : Boolean(savingRoleViewSettingsTargetId)
                                }
                                onChange={(nextViewSettings) => {

                                    // Save the complete role set and compatible image rows for this exact parent or child
                                    onRoleViewSettingsChange?.({
                                        configuredProductId: exactConfiguredProductId,
                                        canonicalProduct: isChild ? child : product,
                                        targetRole: safeRoleKey,
                                        nextViewSettings: nextViewSettings,
                                    });

                                }}
                            />

                        </div>

                    );

                })}

                {/* If there are no design product roles (then there's no image uploaded to a role for the design) then we just want to show all the blank product roles here */}
                {safeDesignRoles.length === 0 &&

                    // Map through all the global roles 
                    printAreaRoles.map((roleItem) => {

                        // Find the configured parent and child role rows
                        const currentProductRole = safeProductRoles.find((roleData) => roleData?.role === roleItem?.role) || null;
                        const currentProductChildRole = safeChildProductRoles.find((roleData) => roleData?.role === roleItem?.role) || null;

                        // Choose the configured role whose view settings belong to this exact product
                        let configuredRoleForView = currentProductRole;

                        if (isChild && currentProductChildRole) {
                            configuredRoleForView = currentProductChildRole;
                        }

                        // Choose the canonical parent or child whose views are being displayed
                        let previewCanonicalProduct = product;

                        if (isChild) {
                            previewCanonicalProduct = child;
                        }

                        // Get the first live view allowed by this configured design product role
                        const firstConfiguredView = getFirstConfiguredProductPreviewView(
                            previewCanonicalProduct,
                            configuredRoleForView,
                            roleItem?.role
                        );

                        // Check if this product has a matching role
                        const displayRole = hasMatchingRole(isChild ? child : product, roleItem);

                        // Check if we need to display the role row
                        if (displayRole) {

                            // We do so return it
                            return (

                                <div className="roleRow" key={roleItem.role}>

                                    {/* This is the role identity block */}
                                    <div className="roleRow__identity">

                                        {/* This is the role image */}
                                        <div className="roleRow__image">

                                            {firstConfiguredView &&

                                                <PodProductPreview

                                                    // There is no uploaded design image in this fallback section
                                                    thedesign={{ roleItem }}

                                                    // Use the canonical parent product
                                                    product={product.custom_product}

                                                    // Use the canonical child product when rendering a child
                                                    child={child?.custom_product}

                                                    // Pass the configured parent role
                                                    currentProductRoleData={currentProductRole}

                                                    // Pass the configured child role when rendering a child
                                                    currentProductChildRoleData={currentProductChildRole}

                                                    // Set the preview height
                                                    mockup_height={76}

                                                    // Use the first view available to this configured product role
                                                    options={{
                                                        display_mode: "product",
                                                        surface_code: firstConfiguredView.surface_code || "default",
                                                        print_area_code: firstConfiguredView.print_area_code || "default",
                                                        view_style: firstConfiguredView.style || "default",
                                                        view_name: firstConfiguredView.name || "",
                                                        selected_colourway_code: currentProduct?.default_colourway || "",
                                                    }}
                                                />

                                            }

                                        </div>

                                        {/* This is the role label */}
                                        <div className="roleRow__label">

                                            <span className="roleRow__name">{roleItem.name}</span>

                                            {/* <span className="roleRow__key">{roleItem.role}</span> */}

                                        </div>

                                    </div>

                                    {/* This is the toggle */}
                                    <div className="roleRow__toggle">

                                    </div>

                                    {/* This is the lock symbol */}
                                    <div className="roleRow__label">

                                    </div>

                                    {/* This is the edit placement button */}
                                    <div className="roleRow__actions">

                                    </div>

                                </div>

                            )

                        } else {

                            // We don't so return null
                            return null;

                        }

                    })}

            </div>

        </div>

    );

};





// This is the parent product details aka price, title, description
const ProductDetailsData = ({ currentProduct, parentConfiguredProduct = null, setCurrentProducts, product, sku, configuredProductId = "", isChild = false, child = null, chosenLanguage, designRoles, placeholderData, storeHomeCountry = "", podDesignDetails, productPricePreviewValues = {}, productPricePreviewStatus = "idle" }) => {

    // Get user's account data 
    const {
        userData,
        subscriptionLevels,
        rateCard,
        fulfilmentCountries,
        currencies,
        countries,
        serviceCodes,
    } = useContext(UserContext);

    // Define toast options
    const { toastError } = useToast();

    // Use the shared cart hook so sample orders can go straight into the store cart.
    const { loadCart, addItem } = useCart();

    // Store whether the sample product selector popup is open.
    const [sampleProductPopupOpen, setSampleProductPopupOpen] = useState(false);

    // Get user account currency
    const accountDefaultCurrency = userData?.currency;

    // Use the main portal store alias for the product module sample popup.
    const storeAlias = userData?.portal_connection_alias || "";

    // This creates the parent updater (always called)
    const updateParentProductField = UseParentProductFieldUpdater(setCurrentProducts);

    // This creates the child updater (always called)
    const updateChildProductField = UseChildProductFieldUpdater(setCurrentProducts);

    // This picks which updater to use (no hooks here, just choosing a function)
    const updateProductField = isChild ? updateChildProductField : updateParentProductField;

    // Get the translations based on parent or child
    const translations = isChild ?

        child.venues?.[0]?.translations?.find((trans) => trans?.language === chosenLanguage)

        :

        product.venues?.[0]?.translations?.find((trans) => trans?.language === chosenLanguage);

    // Helper to replace placeholders #x.y# - this creates a new object with placeholders replaced
    const formattedTranslations = ReplacePlaceholdersInObject(translations, placeholderData);

    // This chooses the best sku value consistently
    const safeChildSku = String(child?.pod_product_sku ?? child?.sku ?? "");

    // This stores the configured parent product identity for exact parent/child data updates
    const safeConfiguredProductId = String(configuredProductId || currentProduct?.id || currentProduct?.client_configured_product_id || "");

    // Get the current product/child's translations for our chosen language
    const currentProductTranslations = currentProduct?.translations?.find((trans) => trans.language === chosenLanguage);

    // Read available colourways from the matching canonical product shape.
    const availableColourways = getAvailableProductColourways(isChild ? child : product);

    // Read the configured parent effective default for child inheritance controls
    const parentDefaultColourwayCode = isChild
        ? String(parentConfiguredProduct?.default_colourway || "").trim()
        : "";

    // Read the configured parent's effective mockup colourways for child inheritance display
    const parentMockupColourways = isChild
        ? normaliseColourwayCodes(parentConfiguredProduct?.mockup_colourways)
        : [];

    // Parent default options must be available on every colourway-capable configured child
    const disabledParentDefaultColourwayCodes = !isChild
        ? normaliseColourwayCodes(availableColourways).filter((colourwayCode) => {

            // Read configured and canonical children
            const configuredChildren = Array.isArray(currentProduct?.children) ? currentProduct.children : [];
            const canonicalChildren = Array.isArray(product?.children) ? product.children : [];

            // Disable this parent default when any colourway-capable child does not expose it
            return configuredChildren.some((configuredChild) => {

                // Match the canonical child by POD product sku
                const configuredChildSku = String(configuredChild?.pod_product_sku || "").trim();
                const canonicalChild = canonicalChildren.find((candidate) => String(candidate?.sku || "").trim() === configuredChildSku) || null;

                // Ignore children without colourways
                const childColourwayCodes = getEffectiveProductColourwayCodes(configuredChild, canonicalChild);
                if (childColourwayCodes.length === 0) return false;

                // The parent code is unavailable when the child does not contain it
                return !childColourwayCodes.includes(colourwayCode);

            });

        })
        : [];

    // Parent mockup colourways must also be available on every child which inherits the parent mockup selection
    const disabledParentMockupColourwayCodes = !isChild
        ? normaliseColourwayCodes(availableColourways).filter((colourwayCode) => {

            // Read configured and canonical children
            const configuredChildren = Array.isArray(currentProduct?.children) ? currentProduct.children : [];
            const canonicalChildren = Array.isArray(product?.children) ? product.children : [];

            // Disable this parent mockup when any inheriting colourway-capable child cannot use it
            return configuredChildren.some((configuredChild) => {

                // Children with their own default or explicit mockup list do not inherit the parent mockup selection
                const childInheritsDefault =
                    String(configuredChild?.default_colourway_inherited || "N").trim().toUpperCase() === "Y";

                const childHasExplicitMockups =
                    String(configuredChild?.mockup_colourways_explicit || "N").trim().toUpperCase() === "Y";

                if (!childInheritsDefault || childHasExplicitMockups) return false;

                // Match the canonical child by POD product sku
                const configuredChildSku = String(configuredChild?.pod_product_sku || "").trim();

                const canonicalChild =
                    canonicalChildren.find(
                        (candidate) =>
                            String(candidate?.sku || "").trim() === configuredChildSku
                    ) || null;

                // Ignore children without colourways
                const childColourwayCodes =
                    getEffectiveProductColourwayCodes(
                        configuredChild,
                        canonicalChild
                    );

                if (childColourwayCodes.length === 0) return false;

                // The parent mockup code is unavailable when the inheriting child does not contain it
                return !childColourwayCodes.includes(colourwayCode);

            });

        })
        : [];

    // Work out if this sample still needs variant or colourway selection first.
    const sampleNeedsProductModule =
        (!isChild && Array.isArray(product?.children) && product.children.length > 0)
        || availableColourways.length > 0;

    // Set the keywords from the product otherwise from the translations
    const keywords = currentProductTranslations?.keywords ? currentProductTranslations?.keywords : translations.keywords || "";

    // This converts newlines to "" and added a bottom margin to rich text editor - NB: converting newlines to <br /> so the "Size:" lines display nicely in the description makes the whole thing too big and gappy
    const htmlDescriptionWithLineBreaks = (currentProductTranslations?.description ? currentProductTranslations?.description : formattedTranslations.description || "").replace(/\n/g, "");

    // Get the list of role names for the pricing - this will be an array like ["exterior_front_main", "exterior_back_main"]
    const printAreasToIncludeInPricing = (() => {

        // Get the current product roles array safely
        const currentProductRoles = currentProduct?.roles || [];

        // Build a quick lookup of roles that are explicitly disabled on the current product
        const inactiveRoleSet = new Set(
            currentProductRoles
                .filter((productRole) => productRole.active === "N")
                .map((productRole) => productRole.role)
        );

        // Use the design roles but exclude anything the current product has explicitly disabled
        return (designRoles || [])
            .filter((designRole) => !inactiveRoleSet.has(designRole.role))
            .map((designRole) => designRole.role);

    })();



    // Create keyword list array
    const keywordList = (

        // Check keywords exist and trim them
        keywords.trim()

            // They do, so get the currect keywords
            ? keywords

                // Split each keyword/phrase by comma
                .split(',')

                // Map the keywords and trim any trailing spaces
                .map(keyword => keyword.trim())

                // Filter only real values (with a length greater than 0)
                .filter(keyword => keyword.length > 0)

            // Else create an empyty array
            : []

    );



    // Handle keywords changes (adding / removing)
    const handleKeywordsChange = (newKeywordsArray) => {

        // Join keywords into a comma-separated string, or empty string if cleared
        const keywordsString = newKeywordsArray.length > 0

            // Join each keyword/phrase with a comma and space
            ? newKeywordsArray.join(', ')

            // Need to save as space when removing all keywords
            : " ";

        // This updates title
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "keywords",
            value: keywordsString,
            target: "translation",
            language: chosenLanguage,
        });

    };



    // Handle colourway selection changes for parent and child products
    const handleProductColourwaysChange = (colourwayPayload) => {

        // This stores the selected colourway codes on the design/template product
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "colourways",
            value: colourwayPayload.colourways,
        });

        // This stores the effective default colourway code on the design/template product
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "default_colourway",
            value: colourwayPayload.default_colourway,
        });

        // This tells the API whether the effective value is inherited from the configured parent
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "default_colourway_inherited",
            value: colourwayPayload.default_colourway_inherited || "N",
        });

        // Store the effective mockup colourway list for local display and change detection
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "mockup_colourways",
            value: colourwayPayload.mockup_colourways || [],
        });

        // Tell the API whether the mockup colourways are a deliberate stored selection
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "mockup_colourways_explicit",
            value: colourwayPayload.mockup_colourways_explicit || "N",
        });

        // Keep the effective mockup inheritance state visible in local product data
        updateProductField({
            configuredProductId: safeConfiguredProductId,
            sku: sku,
            childSku: isChild ? safeChildSku : "",
            key: "mockup_colourways_inherited",
            value: colourwayPayload.mockup_colourways_inherited || "N",
        });

        // Keep inherited child effective values current immediately when the parent default changes
        if (!isChild) {
            setCurrentProducts((previousDetails) => {

                // Copy the product list safely
                const previousProducts = Array.isArray(previousDetails?.products) ? previousDetails.products : [];
                const nextProducts = previousProducts.map((configuredProduct) => {

                    // Only update the active configured parent product
                    const configuredId = String(configuredProduct?.id || configuredProduct?.client_configured_product_id || "");
                    if (configuredId !== safeConfiguredProductId) return configuredProduct;

                    // Update effective values on children inheriting the parent default
                    const nextChildren = Array.isArray(configuredProduct?.children)
                        ? configuredProduct.children.map((configuredChild) => {

                            // Leave explicitly overridden children unchanged
                            if (String(configuredChild?.default_colourway_inherited || "N").trim().toUpperCase() !== "Y") return configuredChild;

                            // Explicit child mockup selections remain independent of the parent mockup list
                            const childHasExplicitMockups =
                                String(configuredChild?.mockup_colourways_explicit || "N").trim().toUpperCase() === "Y";

                            // Return the inherited child with the new effective parent colourway state
                            return {
                                ...configuredChild,
                                default_colourway: colourwayPayload.default_colourway,
                                mockup_colourways: childHasExplicitMockups
                                    ? configuredChild.mockup_colourways
                                    : colourwayPayload.mockup_colourways,
                                mockup_colourways_inherited: childHasExplicitMockups ? "N" : "Y",
                            };

                        })
                        : configuredProduct.children;

                    // Return the parent with updated inherited children
                    return {
                        ...configuredProduct,
                        children: nextChildren,
                    };

                });

                // Return the updated design/template state
                return {
                    ...previousDetails,
                    products: nextProducts,
                };

            });
        }

    };



    const handleCreateSampleOrder = async () => {

        // Use the most specific SKU for the active row.
        const sampleSku = String(
            currentProduct?.sku
            || (isChild ? child?.sku : product?.sku)
            || ""
        ).trim();

        // Stop when the current product row does not have a usable SKU.
        if (!sampleSku) {
            toastError({ title: "Error", message: "Could not add this sample because the product SKU is missing.", autoCloseMs: 3000 });
            return;
        }

        // Reuse the first saved image so the cart module has product artwork available for checkout.
        const primaryImage =
            currentProduct?.images?.[0]?.image_url
            || currentProduct?.images?.[0]?.thumbnail_url
            || "";

        // Use the current translated title when it exists so the cart row reads nicely.
        const sampleTitle =
            currentProductTranslations?.title
            || formattedTranslations.title
            || sampleSku;

        // Use the shared product module when the sample still needs option selection.
        if (sampleNeedsProductModule) {
            setSampleProductPopupOpen(true);
            return;
        }

        try {

            // Make sure the shared cart UI is mounted before adding an item.
            await loadCart();

            // Add the sample product to the shared cart.
            addItem({
                sku: sampleSku,
                quantity: 1,
                title: sampleTitle,
                image_url: primaryImage,
            });

        } catch (error) {

            toastError({
                title: "Error",
                message: error?.message || "Could not add this product to the cart.",
                autoCloseMs: 3000,
            });

        }

    };



    return (

        // This is the card
        <div className="card">

            {/* This is the header */}
            <div className="card__header">

                {/* This is the title */}
                <h3 className="card__title">{isChild ? "Child " : ""} Product Details</h3>

                {(product?.Active === "N" || child?.Active === "N" ) &&
                
                    <div className="warning-message">
                        This {isChild ? "child" : "product"} is currently inactive.
                    </div>

                }
                
                <div className="horizontal-align-content-center">

                     {/* This is a hint */}
                    <span className="hintText">

                        {isChild && <span> {child.sku}</span>}

                    </span>

                    <span>

                    </span>

                    {/* Only show the 'order sample' button if this product has images */}
                    {currentProduct?.images?.length > 0 && 

                        <CreateButton
                            title="Order Sample"
                            size="s"
                            tooltip="Order a Sample"
                            onClick={handleCreateSampleOrder}
                        />

                    }

                </div>

            </div>

            <div className="card__content">

                {/* This is the title and pricing row */}
                {/* Do NOT put the pricing row above the title. It doesn't match the children and looks shit - Ben */}
                {/* They can't always be on the same line because it's unusable on thin screens - Elle 
                <div className={(isChild || (!product.children || product.children?.length === 0)) ? "productDetailsTitlePriceRow" : "productDetailsTitlePriceRow productDetailsTitlePriceRow--titleOnly"}>
*/}
                    {/* This is the title field */}
                    <div className="fieldRow productDetailsTitlePriceRow__title">
                        <label className="fieldRow__label">Title</label>
                        <input
                            className="fieldRow__textarea"
                            value={currentProductTranslations?.title ? currentProductTranslations?.title : formattedTranslations.title}
                            placeholder="Title"
                            onChange={(e) => {

                                // This updates title
                                updateProductField({
                                    configuredProductId: safeConfiguredProductId,
                                    sku: sku,
                                    childSku: isChild ? safeChildSku : "",
                                    key: "title",
                                    value: e.target.value,
                                    target: "translation",
                                    language: chosenLanguage,
                                });

                            }}
                            disabled={designUsesTemplates(podDesignDetails) ? true : false}
                        />
                    </div>

                    {(isChild || (!product.children || product.children?.length === 0)) &&

                        <div className="fieldRow productDetailsTitlePriceRow__title">
                        <label className="fieldRow__label">Price</label>

                        <div className="productDetailsTitlePriceRow__price">

                            <div className="productDetailsTitlePriceRow__priceControl">

                                <ProductPriceEditor

                                    // Pass the canonical parent SKU
                                    sku={sku}

                                    // Pass the child SKU when this editor belongs to a child
                                    childSku={isChild ? safeChildSku : null}

                                    // Use this configured product or child store SKU
                                    storeSku={currentProduct?.sku || ""}

                                    // Pass the user's subscription level
                                    currentPlanLevel={userData.subscription_level}

                                    // Pass all subscription levels
                                    subscriptionLevels={subscriptionLevels}

                                    // Pass the canonical home-country pricing
                                    defaultPrices={product?.homeCountryPricing?.prices || []}

                                    // Pass the locally saved pricing override
                                    productPrices={currentProduct?.prices || []}

                                    // Pass currency reference data
                                    coreCurrencies={currencies}

                                    // Pass country reference data
                                    coreCountries={countries}

                                    // Pass the active print areas
                                    selectedPrintAreas={printAreasToIncludeInPricing}

                                    // Pass available fulfilment destinations
                                    shippingDestinations={fulfilmentCountries}

                                    // Pass the store's home country
                                    storeHomeCountry={storeHomeCountry}

                                    // Pass the account's default currency
                                    accountDefaultCurrency={accountDefaultCurrency}

                                    // Pass whether the merchant handles payment
                                    initialUserTakesPayment={userData.paypal_merchant_id ? true : false}

                                    // Pass the rate card
                                    rateCard={rateCard}

                                    // Handle pricing changes
                                    onPriceConfigChange={(nextPrices) => {

                                        // Update the correct parent or child product
                                        updateProductField({
                                            configuredProductId: safeConfiguredProductId,
                                            sku: sku,
                                            childSku: isChild ? safeChildSku : "",
                                            key: "prices",
                                            value: nextPrices,
                                        });

                                    }}

                                    // Hide additional inline information
                                    displayInfo={false}

                                    // Disable pricing when controlled by a template
                                    disablePriceEditor={designUsesTemplates(podDesignDetails) ? true : false}

                                    // Pass design details
                                    podDesignDetails={podDesignDetails}

                                    // Pass the configured default colourway
                                    defaultColourwayCode={currentProduct?.default_colourway || ""}

                                    // Pass fulfilment service codes
                                    serviceCodes={serviceCodes}

                                    // Pass the API-calculated display values
                                    apiDisplayValues={
                                        productPricePreviewValues[
                                            getProductPricePreviewKey({
                                                storeSku: currentProduct?.sku || "",
                                                podProductSku: isChild
                                                    ? safeChildSku
                                                    : currentProduct?.pod_product_sku || sku,
                                            })
                                        ] || null
                                    }

                                    // Pass the API preview status
                                    apiDisplayStatus={productPricePreviewStatus}

                                />



                            </div>

                        </div>

                        </div>

                    }

                    {/* Parent products with children are priced individually on their child products. */}
                    {!isChild && product.children?.length > 0 &&

                        <div className="fieldRow productDetailsTitlePriceRow__title">

                            <label className="fieldRow__label">Price</label>

                            <div className="productDetailsTitlePriceRow__parentPriceNote">

                                Pricing is set on the product children shown in the left panel.

                            </div>

                        </div>

                    }

{/*
                </div>
*/}
                {/* This is the parent description row  */}
                <div className="fieldRow productModal__descriptionRow">
                    <label className="fieldRow__label">Description</label>
                    <RichTextInput
                        value={currentProductTranslations?.description ? currentProductTranslations?.description : htmlDescriptionWithLineBreaks}
                        onChange={(inputValue) => {

                            // This updates description
                            updateProductField({
                                configuredProductId: safeConfiguredProductId,
                                sku: sku,
                                childSku: isChild ? safeChildSku : "",
                                key: "description",
                                value: inputValue,
                                target: "translation",
                                language: chosenLanguage,
                            });

                        }}
                        disabled={designUsesTemplates(podDesignDetails) ? true : false}
                    />
                </div>

                <div className="bullets-keywords-container">

                    {/* This is the parent bullets row  */}
                    <div className="fieldRow fieldRow--bullets">

                        {/* This is the label */}
                        <label className="fieldRow__label fieldRow__label--centered">
                            Bullets
                        </label>

                        {/* This is the right side control area */}
                        <div className="fieldRow__control fieldRow__control--stacked">

                            <input
                                className="fieldRow__textarea"
                                value={currentProductTranslations?.bullet1 ? currentProductTranslations?.bullet1 : formattedTranslations.bullet1}
                                placeholder="Bullet 1"
                                onChange={(e) => {

                                    // This updates title
                                    updateProductField({
                                        configuredProductId: safeConfiguredProductId,
                                        sku: sku,
                                        childSku: isChild ? safeChildSku : "",
                                        key: "bullet1",
                                        value: e.target.value,
                                        target: "translation",
                                        language: chosenLanguage,
                                    });

                                }}
                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                            />
                            <input
                                className="fieldRow__textarea"
                                value={currentProductTranslations?.bullet2 ? currentProductTranslations?.bullet2 : formattedTranslations.bullet2}
                                placeholder="Bullet 2"
                                onChange={(e) => {

                                    // This updates title
                                    updateProductField({
                                        configuredProductId: safeConfiguredProductId,
                                        sku: sku,
                                        childSku: isChild ? safeChildSku : "",
                                        key: "bullet2",
                                        value: e.target.value,
                                        target: "translation",
                                        language: chosenLanguage,
                                    });

                                }}
                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                            />
                            <input
                                className="fieldRow__textarea"
                                value={currentProductTranslations?.bullet3 ? currentProductTranslations?.bullet3 : formattedTranslations.bullet3}
                                placeholder="Bullet 3"
                                onChange={(e) => {

                                    // This updates title
                                    updateProductField({
                                        configuredProductId: safeConfiguredProductId,
                                        sku: sku,
                                        childSku: isChild ? safeChildSku : "",
                                        key: "bullet3",
                                        value: e.target.value,
                                        target: "translation",
                                        language: chosenLanguage,
                                    });

                                }}
                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                            />
                            <input
                                className="fieldRow__textarea"
                                value={currentProductTranslations?.bullet4 ? currentProductTranslations?.bullet4 : formattedTranslations.bullet4}
                                placeholder="Bullet 4"
                                onChange={(e) => {

                                    // This updates title
                                    updateProductField({
                                        configuredProductId: safeConfiguredProductId,
                                        sku: sku,
                                        childSku: isChild ? safeChildSku : "",
                                        key: "bullet4",
                                        value: e.target.value,
                                        target: "translation",
                                        language: chosenLanguage,
                                    });

                                }}
                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                            />
                            <input
                                className="fieldRow__textarea"
                                value={currentProductTranslations?.bullet5 ? currentProductTranslations?.bullet5 : formattedTranslations.bullet5}
                                placeholder="Bullet 5"
                                onChange={(e) => {

                                    // This updates title
                                    updateProductField({
                                        configuredProductId: safeConfiguredProductId,
                                        sku: sku,
                                        childSku: isChild ? safeChildSku : "",
                                        key: "bullet5",
                                        value: e.target.value,
                                        target: "translation",
                                        language: chosenLanguage,
                                    });

                                }}
                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                            />

                        </div>

                    </div>

                    {/* This is the parent keywords row  */}
                    <div className="fieldRow">

                        <label className="fieldRow__label">Keywords</label>

                        <KeywordTagsInput
                            value={keywordList}
                            onChange={handleKeywordsChange}
                            inputClassName={"fieldRow__textarea"}
                            disabled={designUsesTemplates(podDesignDetails) ? true : false}
                        />

                    </div>

                </div>

                {/* This is the colourway selector shown at the bottom of the product details panel */}
                <ProductColourwaySelector
                    canonicalProduct={isChild ? child : product}
                    product={currentProduct}
                    disabled={designUsesTemplates(podDesignDetails) ? true : false}
                    allowInheritedDefault={isChild}
                    parentDefaultColourwayCode={parentDefaultColourwayCode}
                    parentMockupColourways={parentMockupColourways}
                    disabledDefaultColourwayCodes={disabledParentDefaultColourwayCodes}
                    disabledMockupColourwayCodes={disabledParentMockupColourwayCodes}
                    onChange={handleProductColourwaysChange}
                />

            </div>

            <ProductSamplePopup
                isOpen={sampleProductPopupOpen}
                onClose={() => setSampleProductPopupOpen(false)}
                storeAlias={storeAlias}
                sku={String(currentProduct?.sku || (isChild ? child?.sku : product?.sku) || "").trim()}
                showPrice={false}
            />

        </div>

    );

};





// This is the child details drawer shown when you click “Details”
const ChildDetailsDrawer = ({

    // Canonical child
    child,
    // Current design product
    currentProduct,
    // Configured parent product id
    configuredProductId,
    // Function to update currentProducts (this is the same as setPodDesignDetails)
    setCurrentProducts,
    // Canonical product
    product,
    // Parent product sku
    sku,
    // Chosen language
    chosenLanguage,
    // All global print area roles
    printAreaRoles,
    // Selected design set roles
    selectedRoles,
    // Pod deisgn details
    podDesignDetails,
    // Open the placement editor
    handleOpenImagePlacementEditor,

    // Save role view settings and their compatible image rows
    onRoleViewSettingsChange,

    // Configured product currently saving role view settings
    savingRoleViewSettingsTargetId = "",

    // Reset one edited child print area placement
    onResetPlacement,

    // Whether a placement is currently being saved
    savingPlacement = false,

    // Refresh the automatic mockup selection
    handleRefreshProductMockups,

    // Update the automatic mockup count
    handleAutoMockupCountChange,

    // Store home country used by product pricing
    storeHomeCountry = "",

    placeholderData,

    // API-calculated product price display values
    productPricePreviewValues = {},

    // API-calculated product price display status
    productPricePreviewStatus = "idle",

    // Open product images popup
    setProductImagesPopupTarget,

}) => {

    // This creates a ref for focus management
    const closeButtonRef = useRef(null);

    // This focuses the close button when the drawer opens
    useEffect(() => {

        // This focuses the close button for accessibility
        closeButtonRef.current?.focus();

    }, []);

    // Current design product child
    const currentChild = currentProduct?.children?.find((c) => c.pod_product_sku === child.sku);

    // Get the configured child image rows
    const safeMockups = Array.isArray(currentChild?.images) ? currentChild.images : [];

    // Read the effective automatic mockup count for this child
    let currentChildAutoMockupCount = Number.parseInt(
        currentChild?.auto_mockup_count ?? currentProduct?.auto_mockup_count ?? 3,
        10
    );

    if (!Number.isInteger(currentChildAutoMockupCount) || currentChildAutoMockupCount < 0) {
        currentChildAutoMockupCount = 3;
    }

    // Automatic mockups are enabled whenever the effective count is above zero
    const childAutoMockupsEnabled = currentChildAutoMockupCount > 0;

    // Inherited children follow the parent Auto Mockups state and count
    const childAutoMockupsInherited =
        String(currentChild?.auto_mockup_count_inherited || "N").trim().toUpperCase() === "Y";

    // This returns the drawer UI
    return (

        // This is the drawer overlay area
        <div className="childDrawer" role="dialog" aria-modal="true" aria-label="Child product details">

            {/* This is the drawer body */}
            <div className="childDrawer__body">

                {/* This is a two-column layout */}
                <div className="childDrawer__grid">

                    <div className="childDrawer__imagesPanel">

                        <div className="card productModal__imagesPanel">

                            <div className="card__header">

                                <h3 className="card__title">Images</h3>

                                <span className="hintText">Live views and product images</span>

                            </div>

                            <div className="card__content productModal__imagesPanelContent">

                                {/* Image carousel of mock up images */}
                                <ProductDetailsImageCarousel
                                    images={safeMockups}
                                    numberOfPlaceHolders={0}
                                    parentOrChildProduct={currentChild}
                                    parentConfiguredProduct={currentProduct}
                                    parentProduct={product}
                                    childProduct={child}
                                    isChild={true}
                                    designRoles={podDesignDetails?.roles}
                                />

                                <CreateButton
                                    type="button"
                                    title="Manage Images"
                                    size="s"
                                    fullWidth={true}
                                    disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                    onClick={() => {

                                        // Open the product images popup for this child product
                                        setProductImagesPopupTarget({
                                            configuredProductId: currentChild?.id || currentChild?.client_configured_product_id || "",
                                            title: "Manage Child Product Images",
                                            canonicalProduct: child,
                                            configuredRoles: Array.isArray(currentChild?.roles) ? currentChild.roles : [],
                                            images: Array.isArray(currentChild?.images) ? currentChild.images : [],
                                        });

                                    }}
                                />

                                <div className="productModal__imageControls">

                                    <div className="productModal__autoMockups">

                                        <label>Auto Mockups</label>

                                        <ToggleSwitch
                                            name={"auto_mockups_" + String(currentChild?.id || currentChild?.client_configured_product_id || child?.sku || "child")}
                                            value={childAutoMockupsEnabled}
                                            onChange={(event) => {

                                                // Turning Auto Mockups off stores zero; turning it back on restores the normal default count
                                                handleAutoMockupCountChange?.({
                                                    configuredProductId: currentProduct?.id || currentProduct?.client_configured_product_id || "",
                                                    childSku: child?.sku || "",
                                                    value: event.target.value ? 3 : 0,
                                                    inherited: false,
                                                });

                                            }}
                                            label=""
                                            disabled={
                                                designUsesTemplates(podDesignDetails)
                                                || childAutoMockupsInherited
                                            }
                                            title={childAutoMockupsEnabled ? "Turn off automatic mockups" : "Turn on automatic mockups"}
                                            size="s"
                                        />

                                        {childAutoMockupsEnabled &&

                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={currentChildAutoMockupCount}
                                                onChange={(event) => {

                                                    // Editing a child count creates an explicit child override
                                                    handleAutoMockupCountChange?.({
                                                        configuredProductId: currentProduct?.id || currentProduct?.client_configured_product_id || "",
                                                        childSku: child?.sku || "",
                                                        value: event.target.value,
                                                        inherited: false,
                                                    });

                                                }}
                                                disabled={
                                                    designUsesTemplates(podDesignDetails)
                                                    || childAutoMockupsInherited
                                                }
                                            />

                                        }

                                        <ToggleSwitch
                                            name={"inherit_auto_mockups_" + String(currentChild?.id || currentChild?.client_configured_product_id || child?.sku || "child")}
                                            value={childAutoMockupsInherited}
                                            onChange={(event) => {

                                                // Switch between the effective parent count and an explicit child override
                                                handleAutoMockupCountChange?.({
                                                    configuredProductId: currentProduct?.id || currentProduct?.client_configured_product_id || "",
                                                    childSku: child?.sku || "",
                                                    value: currentProduct?.auto_mockup_count ?? 3,
                                                    inherited: event.target.value,
                                                });

                                            }}
                                            label="Use parent"
                                            labelPosition="left"
                                            disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                            title="Use the configured parent product's Auto Mockups value"
                                            size="s"
                                        />

                                        <span className="hintText">
                                            {childAutoMockupsInherited
                                                ? "Inherited from parent"
                                                : childAutoMockupsEnabled
                                                    ? "per rotation"
                                                    : "Uses all selected images"}
                                        </span>

                                    </div>

                                    <CreateButton
                                        type="button"
                                        title="Refresh"
                                        tooltip="Saves any Auto Mockups changes, then selects and regenerates a fresh random set."
                                        size="s"
                                        disabled={
                                            designUsesTemplates(podDesignDetails)
                                            || !/^\d+$/.test(String(currentChild?.id || ""))
                                        }
                                        onClick={() => {

                                            // Select a fresh automatic mockup set and queue it for generation
                                            handleRefreshProductMockups?.({
                                                configuredProductId: currentChild?.id || ""
                                            });

                                        }}
                                    />

                                </div>

                            </div>

                        </div>

                    </div>

                    {/* This is the child details */}
                    <ProductDetailsData
                        // Current child data (the stuff assigned to the design)
                        currentProduct={currentChild}
                        // Configured parent data used for default colourway inheritance
                        parentConfiguredProduct={currentProduct}
                        // setState function to update data
                        setCurrentProducts={setCurrentProducts}
                        // Canonical parent Product
                        product={product}
                        // Canonical child Product
                        child={child}
                        // Parent product sku 
                        sku={sku}
                        configuredProductId={configuredProductId}
                        // Set isChild true so it adds the childSku when updating fields
                        isChild={true}
                        // Chosen language
                        chosenLanguage={chosenLanguage}
                        // Design roles (incase there are no current product roles - needed for realistic costing)
                        designRoles={podDesignDetails.roles}

                        placeholderData={placeholderData}

                        podDesignDetails={podDesignDetails}
                        storeHomeCountry={storeHomeCountry}
                        productPricePreviewValues={productPricePreviewValues}
                        productPricePreviewStatus={productPricePreviewStatus}
                    />

                    <div className="childDrawer__printAreas">

                        {/* Table of roles - can be toggled and edited (image placement editor)  */}
                        <PrintAreasPanel
                            productRoles={currentProduct?.roles}
                            childProductRoles={currentChild?.roles}
                            selectedRoles={selectedRoles}
                            title={"Print Areas"}
                            printAreaRoles={printAreaRoles}
                            product={product}
                            child={child}
                            podDesignDetails={podDesignDetails}
                            currentProduct={currentChild}
                            configuredProductId={configuredProductId}
                            isChild={true}
                            handleOpenImagePlacementEditor={handleOpenImagePlacementEditor}
                            onRoleViewSettingsChange={onRoleViewSettingsChange}
                            savingRoleViewSettingsTargetId={savingRoleViewSettingsTargetId}
                            onResetPlacement={onResetPlacement}
                            savingPlacement={savingPlacement}
                        />

                    </div>

                </div>

            </div>

        </div>

    );

};





// Product details pop up for parent and children
const ProductDetailsPopUp = ({
    isOpen,
    onClose,
    // Canonical product
    product,
    podDesignDetails,
    setCurrentProducts,
    printAreaRoles,
    selectedRoles,
    latestTransform,
    setLatestTransform,
    currentProduct,
    saveTransformDataToProductRole,
    formValues,
    productTitleWithoutPlaceholders,
    storeHomeCountry,
    designOrTemplate,
    onProductFamilyCommitted,
}) => {

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Get user's account data 
    const {
        connectionsData,
        userData,
        rateCard,
        subscriptionLevels,
        fulfilmentCountries,
        currencies,
        countries
    } = useContext(UserContext);

    // Define toast options
    const { toastError } = useToast();

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    // This tracks which child is expanded into the drawer
    const [activeChildSku, setActiveChildSku] = useState(product.children?.length > 0 ? "parent" : null);

    // State for showing children quick edit table
    const [viewChildrenQuickEditTable, setViewChildrenQuickEditTable] = useState(false);

    // This tracks the product image manager popup target
    const [productImagesPopupTarget, setProductImagesPopupTarget] = useState(null);

    // This tracks the active configured product whose background mockups are being polled
    const [productImagesPollTargetId, setProductImagesPollTargetId] = useState("");

    // This tracks the configured product currently saving role view settings
    const [savingRoleViewSettingsTargetId, setSavingRoleViewSettingsTargetId] = useState("");

    // This tracks whether a placement and its replacement image jobs are being saved
    const [savingPlacement, setSavingPlacement] = useState(false);

    // This tracks the normal product Save or Cancel request
    const [savingProduct, setSavingProduct] = useState(false);

    // This tracks the product close pop up request
    const [closingProduct, setClosingProduct] = useState(false);

    // This stores API-calculated product price display values by product row
    const [productPricePreviewValues, setProductPricePreviewValues] = useState({});

    // This stores the API-calculated product price display status
    const [productPricePreviewStatus, setProductPricePreviewStatus] = useState("idle");

    // Set chosen language
    const chosenLanguage = "EN";

    // This finds the active child object
    const activeChild = useMemo(() => {

        // Get the children products
        const childProducts = product.children ? product.children : [];

        // This returns the child matching the active id
        return childProducts.find((c) => c.sku === activeChildSku) || null;

    }, [activeChildSku, product]);

    // This tracks which placement editor is open (null means closed)
    const [placementEditorTarget, setPlacementEditorTarget] = useState(null);

    // This opens the image placement editor overlay
    const handleOpenImagePlacementEditor = (payload) => {

        // This stores the payload in state
        setPlacementEditorTarget(payload);

    };

    // This is a small “badge/pill” component for quick tags
    const Pill = ({ children }) => {

        // This returns a styled pill
        return (

            // This renders the pill element
            <span className="pill">

                {/* This renders the pill content */}
                {children}

            </span>

        );

    };

    // This toggles active state for all children in one click
    const handleToggleAllChildren = (nextActive, product) => {

        // Check that children is an array
        if (Array.isArray(product?.children) && product.children.length > 0) {

            // Loop through each canonical child and update its active state
            product.children.forEach((child) => {

                // This updates the child product's active state
                updateChildProductField({
                    configuredProductId: configuredProductId,
                    childSku: child.sku,
                    key: "active",
                    value: B2S(nextActive),
                });

            });

        }

    };

    // This creates bound parent and child product updaters
    const updateParentProductField = UseParentProductFieldUpdater(setCurrentProducts);
    const updateChildProductField = UseChildProductFieldUpdater(setCurrentProducts);

    // Update the automatic mockup count on the selected configured parent or child
    const handleAutoMockupCountChange = ({ configuredProductId = "", childSku = "", value, inherited = false }) => {

        // Update one child independently when a child sku is supplied
        if (childSku) {

            // Store the effective child value
            updateChildProductField({
                configuredProductId: configuredProductId,
                childSku: childSku,
                key: "auto_mockup_count",
                value: value,
            });

            // Store whether the child should keep inheriting future parent changes
            updateChildProductField({
                configuredProductId: configuredProductId,
                childSku: childSku,
                key: "auto_mockup_count_inherited",
                value: inherited ? "Y" : "N",
            });

            return;

        }

        // Store the explicit parent value
        updateParentProductField({
            configuredProductId: configuredProductId,
            key: "auto_mockup_count",
            value: value,
        });

        // Keep inherited child effective values current without creating child overrides
        const safeConfiguredProductId = String(configuredProductId || "");

        setCurrentProducts((previousDetails) => {

            // Read the current product list safely
            const safePreviousDetails = previousDetails ?? {};
            const previousProducts = Array.isArray(safePreviousDetails?.products)
                ? safePreviousDetails.products
                : [];

            // Update only children which still inherit from this configured parent
            const nextProducts = previousProducts.map((productItem) => {

                // Keep unrelated configured products unchanged
                if (
                    String(productItem?.id || productItem?.client_configured_product_id || "") !==
                    safeConfiguredProductId
                ) {
                    return productItem;
                }

                // Update only the effective display value on inheriting children
                const nextChildren = Array.isArray(productItem?.children)
                    ? productItem.children.map((childItem) => {

                        // Explicit child overrides do not follow parent changes
                        if (String(childItem?.auto_mockup_count_inherited || "N").trim().toUpperCase() !== "Y") {
                            return childItem;
                        }

                        // Keep the child marked as inherited while showing the new effective parent value
                        return {
                            ...childItem,
                            auto_mockup_count: value,
                        };

                    })
                    : productItem?.children;

                // Return the parent family with updated inherited child display values
                return {
                    ...productItem,
                    children: nextChildren,
                };

            });

            // Store the updated design/template state
            return {
                ...safePreviousDetails,
                products: nextProducts,
            };

        });

    };

    // This reads the configured product identity for this open popup
    const currentProductConfiguredId = String(currentProduct?.id || currentProduct?.client_configured_product_id || "");

    // Update one parent product header setting
    const handleProductHeaderToggle = (key, value) => {

        // Do not update anything without a configured product
        if (!currentProductConfiguredId) { return; }

        // Update the configured parent product
        updateParentProductField({
            configuredProductId: currentProductConfiguredId,
            key: key,
            value: B2S(value),
        });

    };

    // This gets the current product from the single live design state
    const liveCurrentProduct = useMemo(() => {

        // Ensure products is always an array
        const products = Array.isArray(podDesignDetails?.products) ? podDesignDetails.products : [];

        // Return the exact configured product from live state, falling back only to the supplied product prop
        return products.find((productItem) => {
            return String(productItem?.id || productItem?.client_configured_product_id || "") === currentProductConfiguredId;
        }) || currentProduct;

    }, [podDesignDetails?.products, currentProductConfiguredId, currentProduct]);

    // A canonical product with children is only a non-buyable grouping parent
    const currentProductHasChildren =
        Array.isArray(product?.children) &&
        product.children.length > 0;

    // Only standalone purchasable products should show a warning on the parent row
    const parentPrintQualityStatus =
        currentProductHasChildren
            ? ""
            : getConfiguredProductPrintQualityStatus(liveCurrentProduct);

    // Read the effective automatic mockup count for the configured parent
    let currentParentAutoMockupCount = Number.parseInt(
        liveCurrentProduct?.auto_mockup_count ?? 3,
        10
    );

    if (!Number.isInteger(currentParentAutoMockupCount) || currentParentAutoMockupCount < 0) {
        currentParentAutoMockupCount = 3;
    }

    // Automatic mockups are enabled whenever the effective count is above zero
    const parentAutoMockupsEnabled = currentParentAutoMockupCount > 0;



    // Build a stable value containing only product fields which can affect pricing
    const productPricingSignature = useMemo(() => {

        // Stop when there is no current product
        if (!liveCurrentProduct) {

            // Return an empty signature
            return "";

        }

        // Build the parent pricing data
        const parentPricingData = {

            // Product identity affects which product is priced
            pod_product_sku: liveCurrentProduct?.pod_product_sku || "",

            // Store SKU is required by the mock order
            sku: liveCurrentProduct?.sku || "",

            // Custom prices directly affect the calculation
            prices: liveCurrentProduct?.prices || [],

            // Default colourway can affect the configured order item
            default_colourway: liveCurrentProduct?.default_colourway || "",

            // Active print areas affect fulfilment cost
            roles: Array.isArray(liveCurrentProduct?.roles)
                ? liveCurrentProduct.roles.map((roleItem) => {

                    // Return only pricing-relevant role data
                    return {

                        // Store the print area role
                        role: roleItem?.role || "",

                        // Store whether the print area is active
                        active: roleItem?.active || "Y",

                    };

                })
                : [],

            // Child pricing must also be included
            children: Array.isArray(liveCurrentProduct?.children)
                ? liveCurrentProduct.children.map((childItem) => {

                    // Return only pricing-relevant child fields
                    return {

                        // Store child identity
                        pod_product_sku: childItem?.pod_product_sku || "",

                        // Store child store SKU
                        sku: childItem?.sku || "",

                        // Store child custom pricing
                        prices: childItem?.prices || [],

                        // Store child default colourway
                        default_colourway: childItem?.default_colourway || "",

                        // Store pricing-relevant child roles
                        roles: Array.isArray(childItem?.roles)
                            ? childItem.roles.map((roleItem) => {

                                // Return only fields that can affect pricing
                                return {

                                    // Store the print area role
                                    role: roleItem?.role || "",

                                    // Store whether it is active
                                    active: roleItem?.active || "Y",

                                };

                            })
                            : [],

                    };

                })
                : [],

        };

        // Convert the pricing data into a stable primitive dependency
        return stableStringify(parentPricingData);

    }, [
        liveCurrentProduct,
    ]);






    // Store the last saved configured product family for the popup dirty check
    const [savedProductEditedCheckValue, setSavedProductEditedCheckValue] =
        useState(() => {

            return stableStringify(
                normaliseConfiguredProductForEditedCheck(
                    liveCurrentProduct
                )
            );

        });

    // Store the last saved automatic mockup counts independently for refresh decisions
    const [savedAutoMockupCounts, setSavedAutoMockupCounts] =
        useState(() => {

            return getConfiguredProductAutoMockupCounts(
                liveCurrentProduct
            );

        });

    // Store the last saved effective mockup colourways independently for refresh decisions
    const [savedMockupColourways, setSavedMockupColourways] =
        useState(() => {

            return getConfiguredProductMockupColourways(
                liveCurrentProduct
            );

        });


    // Build the current stable product-family value
    const currentProductEditedCheckValue =
        useMemo(() => {

            return stableStringify(
                normaliseConfiguredProductForEditedCheck(
                    liveCurrentProduct
                )
            );

        }, [liveCurrentProduct]);

    // Build the current automatic mockup count lookup independently from the normal product fields
    const currentAutoMockupCounts =
        useMemo(() => {

            return getConfiguredProductAutoMockupCounts(
                liveCurrentProduct
            );

        }, [liveCurrentProduct]);

    // Check whether any automatic mockup count changed since the last successful save
    const autoMockupCountsHaveChanged =
        !deepEqual(
            savedAutoMockupCounts,
            currentAutoMockupCounts
        );

    // Check whether anything on this product family has changed since the last successful save
    const productHasChanged =
        savedProductEditedCheckValue !==
        currentProductEditedCheckValue ||
        autoMockupCountsHaveChanged;

    // Product roles and placements can save immediately without using the normal Save button
    const productSaveInProgress =
        savingProduct ||
        savingPlacement ||
        Boolean(savingRoleViewSettingsTargetId);

    // Save the normal product fields and translations
    const handleSaveProduct = async () => {

        // Do nothing while another product save is running
        if (productSaveInProgress) { return null; }

        // Nothing needs saving, but still return the current saved family to callers such as Refresh Mockups
        if (!productHasChanged) {
            return {
                savedProductFamily: liveCurrentProduct,
                changedAutoMockupIds: [],
                changedMockupColourwayIds: [],
            };
        }

        // Remember which configured products changed their automatic mockup count
        const changedAutoMockupIds =
            getChangedAutoMockupProductIds(
                savedAutoMockupCounts,
                liveCurrentProduct
            );

        // Remember which configured products changed their effective mockup colourways
        const changedMockupColourwayIds =
            getChangedMockupColourwayProductIds(
                savedMockupColourways,
                liveCurrentProduct
            );

        // Block repeated footer actions
        setSavingProduct(true);

        try {

            // Save parent and child translations through their dedicated endpoint
            const translationProducts = [
                liveCurrentProduct,
            ];

            if (Array.isArray(liveCurrentProduct?.children)) {

                translationProducts.push(
                    ...liveCurrentProduct.children
                );

            }

            for (const translationProduct of translationProducts) {

                // Skip products without translation overrides
                if (
                    !Array.isArray(translationProduct?.translations) ||
                    translationProduct.translations.length === 0
                ) {
                    continue;
                }

                // Existing popup products must have a configured id
                if (!translationProduct?.id) {

                    throw new Error(
                        "Could not save product translations because the configured product id is missing."
                    );

                }

                // Save the complete translation override array
                const translationsResponse =
                    await updatePodDesignProductTranslations(
                        token,
                        designOrTemplate,
                        podDesignDetails.id,
                        translationProduct.id,
                        translationProduct.translations
                    );

                // Stop immediately when one translation save failed
                requireSuccessfulProductResponse(
                    translationsResponse,
                    "Could not save product translations"
                );

            }

            // Save the parent and child product fields as one family
            const productResponse =
                await updatePodDesignProducts(
                    token,
                    designOrTemplate,
                    podDesignDetails.id,
                    [
                        buildConfiguredProductFamilySavePayload(
                            liveCurrentProduct
                        ),
                    ]
                );

            // Stop immediately when the normal product save failed
            requireSuccessfulProductResponse(
                productResponse,
                "Could not save the product"
            );

            // Get the freshly saved family returned by the product endpoint
            const savedProductFamily =
                getConfiguredProductFamilyById(
                    productResponse.results,
                    currentProductConfiguredId
                );

            // The product endpoint must return the authoritative saved family
            if (!savedProductFamily) {

                throw new Error(
                    "The product was saved but the saved product data was not returned."
                );

            }

            // Commit the authoritative saved family to the design state
            onProductFamilyCommitted?.(
                savedProductFamily
            );

            // Make the successfully saved family the new dirty-check baseline
            setSavedProductEditedCheckValue(
                stableStringify(
                    normaliseConfiguredProductForEditedCheck(
                        savedProductFamily
                    )
                )
            );

            // Store the newly-saved automatic mockup counts
            setSavedAutoMockupCounts(
                getConfiguredProductAutoMockupCounts(
                    savedProductFamily
                )
            );

            // Store the newly-saved effective mockup colourways
            setSavedMockupColourways(
                getConfiguredProductMockupColourways(
                    savedProductFamily
                )
            );

            // The product is saved, but keep the editor open
            setSavingProduct(false);

            // Return the authoritative saved family and any products whose mockup configuration changed
            return {
                savedProductFamily: savedProductFamily,
                changedAutoMockupIds: changedAutoMockupIds,
                changedMockupColourwayIds: changedMockupColourwayIds,
            };

        } catch (error) {

            // Keep the popup open and report the failure
            toastError({
                title: "Error",
                message:
                    error?.message ||
                    "Could not save the product.",
                autoCloseMs: 6000,
            });

            setSavingProduct(false);

            return null;

        }

    };



    // Close the product editor while preserving anything already saved automatically
    const handleCloseProduct = async () => {

        // Do nothing while another save or close request is running
        if (productSaveInProgress || closingProduct) { return; }

        // Reload the authoritative saved family before closing
        const closeWithSavedProduct = async () => {

            // Block repeated footer and close actions
            setClosingProduct(true);

            try {

                // Reload the complete configured hierarchy
                const productsResponse =
                    await getPodDesignProducts(
                        token,
                        designOrTemplate,
                        podDesignDetails.id
                    );

                // Stop immediately when the reload failed
                requireSuccessfulProductResponse(
                    productsResponse,
                    "Could not reload the product"
                );

                // Fish the exact authoritative parent family out of the response
                const refreshedProductFamily =
                    getConfiguredProductFamilyById(
                        productsResponse.results,
                        currentProductConfiguredId
                    );

                // Keep the popup open when the product disappeared
                if (!refreshedProductFamily) {

                    throw new Error(
                        "The saved product could not be found after reloading it."
                    );

                }

                // Replace local product data with the authoritative saved family
                onProductFamilyCommitted?.(
                    refreshedProductFamily
                );

                // Clear the loading state before unmounting the popup
                setClosingProduct(false);

                // Close after the saved state has been restored
                onClose?.();

            } catch (error) {

                // Keep the popup open and report the reload failure
                toastError({
                    title: "Error",
                    message:
                        error?.message ||
                        "Could not reload the product.",
                    autoCloseMs: 6000,
                });

                setClosingProduct(false);

            }

        };


        // Close immediately when no deferred fields are waiting to be saved
        if (!productHasChanged) {

            await closeWithSavedProduct();

            return;

        }

        // Confirm before discarding only the deferred unsaved fields
        DisplayConfirmationDialog(
            "<h3>Discard unsaved changes?</h3><br/>Changes that have already been saved automatically will be kept.<br/><br/>Any remaining unsaved changes will be discarded.",
            "Discard changes",
            "Keep editing",
            async function (isConfirmed) {

                // Keep editing when cancelled
                if (!isConfirmed) { return; }

                // Reload the saved product and close
                await closeWithSavedProduct();

            }
        );

    };



    // Get the configured product image rows



    // Get the configured product image rows
    const safeMockups = useMemo(() => {

        // Return configured image rows when present
        if (Array.isArray(liveCurrentProduct?.images)) {

            return liveCurrentProduct.images;

        }

        // Return a stable empty array when there are no image rows
        return [];

    }, [liveCurrentProduct?.images]);


    // Get the configured parent or active child currently displayed in this popup
    const activeConfiguredImageProduct = useMemo(() => {

        // Use the parent while the parent product is active
        if (!activeChildSku || activeChildSku === "parent") { return liveCurrentProduct; }

        // Read configured children safely
        const configuredChildren = Array.isArray(liveCurrentProduct?.children) ? liveCurrentProduct.children : [];

        // Return the configured child matching the active canonical child sku
        return configuredChildren.find((childItem) => {
            return String(childItem?.pod_product_sku || childItem?.sku || "") === String(activeChildSku);
        }) || null;

    }, [liveCurrentProduct, activeChildSku]);


    // Start or stop polling whenever the active configured product gains or loses pending mockup rows
    useEffect(() => {

        // Read the active configured product id
        const activeConfiguredProductId = String(activeConfiguredImageProduct?.id || "");

        // Unsaved products cannot have background image rows yet
        if (!/^\d+$/.test(activeConfiguredProductId)) {
            setProductImagesPollTargetId("");
            return;
        }

        // Poll only while the active configured product has missing image ids
        if (configuredProductHasPendingMockups(activeConfiguredImageProduct)) {
            setProductImagesPollTargetId(activeConfiguredProductId);
            return;
        }

        // Stop polling when the active configured product has no pending rows
        setProductImagesPollTargetId("");

    }, [activeConfiguredImageProduct]);


    // Poll the active configured product image rows until there are no missing image ids
    useEffect(() => {

        // Stop when there is no active polling target
        if (!productImagesPollTargetId) { return; }

        // Read the saved design/template id
        const savedDesignOrTemplateId = String(podDesignDetails?.id || "");

        // Polling is only possible for an already-saved design/template
        if (!/^\d+$/.test(savedDesignOrTemplateId)) { return; }

        // Track cleanup and the next timer
        let pollingCancelled = false;
        let pollTimer = null;

        // Poll this configured product's image rows
        const pollProductImages = async () => {

            // Get only the latest image rows for this configured product
            const response = await getPodDesignProductImages(
                token,
                designOrTemplate,
                savedDesignOrTemplateId,
                productImagesPollTargetId
            );

            // Stop after unmount or target change
            if (pollingCancelled) { return; }

            // Stop polling on an API error rather than spinning forever
            if (!response?.success) {

                setProductImagesPollTargetId("");

                toastError({
                    title: "Error",
                    message: "Could not refresh the generated product images: " + (response?.error_message || "Unknown error"),
                    autoCloseMs: 6000,
                });

                return;

            }

            // Read the latest image rows returned for this configured product
            const refreshedImages = Array.isArray(response?.results?.images)
                ? response.results.images
                : [];

            // Update the live portal state only when the poll returned different image rows
            setCurrentProducts((previousDetails) => {

                // Find the configured parent or child currently stored in live state
                const existingConfiguredProduct = getConfiguredProductById(
                    previousDetails?.products,
                    productImagesPollTargetId
                );

                // Start with an empty existing image array
                let existingImages = [];

                // Use the currently stored image rows when available
                if (Array.isArray(existingConfiguredProduct?.images)) {
                    existingImages = existingConfiguredProduct.images;
                }

                // Do not create a new state object when this poll returned exactly the same rows
                if (deepEqual(existingImages, refreshedImages)) {
                    return previousDetails;
                }

                // Replace the image rows after a genuine change
                return {
                    ...previousDetails,
                    products: replaceConfiguredProductImages(
                        previousDetails?.products,
                        productImagesPollTargetId,
                        refreshedImages
                    ),
                };

            });

            // Continue polling while Reaper still has outstanding rows for this product
            if (refreshedImages.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id)) {

                pollTimer = window.setTimeout(pollProductImages, 1000);
                return;

            }

            // All rows completed or failed and were removed
            setProductImagesPollTargetId("");

        };

        // Wait one second before the first poll
        pollTimer = window.setTimeout(pollProductImages, 1000);

        // Cancel timers when the target or component changes
        return () => {

            pollingCancelled = true;

            if (pollTimer) {
                window.clearTimeout(pollTimer);
            }

        };

    }, [
        productImagesPollTargetId,
        podDesignDetails?.id,
        token,
        designOrTemplate,
        setCurrentProducts,
        toastError,
    ]);


    // Save one configured product role and update any affected image rows
    const handleRoleViewSettingsChange = useCallback(async ({
        configuredProductId = "",
        canonicalProduct = null,
        targetRole = "",
        nextViewSettings = null,
        nextActive = null,
        regenerateMockupImages = false,
    }) => {

        // Normalise the exact configured parent or child id
        const safeConfiguredProductId = String(configuredProductId || "");

        // Normalise the role key
        const safeTargetRole = String(targetRole || "");

        // Stop when the target cannot be identified
        if (!safeConfiguredProductId || !safeTargetRole) { return; }

        // Prevent a second role settings request while one is already being saved
        if (savingRoleViewSettingsTargetId) { return; }

        // Find the current configured parent or child
        const currentConfiguredProduct = getConfiguredProductById(
            podDesignDetails?.products,
            safeConfiguredProductId
        );

        // Stop when the configured product cannot be found
        if (!currentConfiguredProduct) { return; }

        // Read current roles safely
        const currentRoles = Array.isArray(currentConfiguredProduct?.roles)
            ? currentConfiguredProduct.roles
            : [];

        // Check whether this configured role already exists
        const roleExists = currentRoles.some((roleItem) => {
            return String(roleItem?.role || "") === safeTargetRole;
        });

        // Build one complete updated role row
        const buildUpdatedRole = (roleItem) => {

            // Start with the current role data
            const nextRole = {
                ...roleItem,
                role: safeTargetRole,
            };

            // Replace view settings when supplied
            if (nextViewSettings !== null) {
                nextRole.view_settings = normaliseRoleViewSettings(nextViewSettings);
            }

            // Replace active state when supplied
            if (nextActive !== null) {
                nextRole.active = String(nextActive);
            }

            // Return the complete updated role
            return nextRole;

        };

        // Build the complete next role array
        const nextRoles = roleExists
            ? currentRoles.map((roleItem) => {

                // Keep unrelated roles unchanged
                if (String(roleItem?.role || "") !== safeTargetRole) { return roleItem; }

                // Replace only the requested role values
                return buildUpdatedRole(roleItem);

            })
            : [
                ...currentRoles,
                buildUpdatedRole({
                    active: "Y",
                    view_settings: normaliseRoleViewSettings(null),
                }),
            ];

        // Read the current selected image rows safely
        const currentImages = Array.isArray(currentConfiguredProduct?.images)
            ? currentConfiguredProduct.images
            : [];

        // Start with the currently selected image rows
        let nextImages = currentImages;

        // Remove exact mockup configurations when view compatibility has changed
        if (nextViewSettings !== null) {
            nextImages = removeIncompatibleMockupImages(
                nextImages,
                canonicalProduct,
                nextRoles
            );
        }

        // Queue every remaining selected mockup when the rendered output has changed
        if (regenerateMockupImages) {
            nextImages = buildPendingMockupImageRows(nextImages);
        }

        // Stop polling this product while its role and image rows are being replaced
        setProductImagesPollTargetId((currentTargetId) => {
            return currentTargetId === safeConfiguredProductId ? "" : currentTargetId;
        });

        // Disable the role view controls until both replacement calls have completed
        setSavingRoleViewSettingsTargetId(safeConfiguredProductId);

        // Check whether this is an existing saved configured product
        const canSaveImmediately =
            /^\d+$/.test(safeConfiguredProductId) &&
            /^\d+$/.test(String(podDesignDetails?.id || ""));

        // Existing configured products must save roles before exposing their replacement image rows
        if (canSaveImmediately) {

            try {

                // Save the complete updated role set first
                const rolesResponse = await updatePodDesignProductRoles(
                    token,
                    designOrTemplate,
                    podDesignDetails.id,
                    safeConfiguredProductId,
                    buildConfiguredProductRolesPayload(nextRoles)
                );

                // Stop if the role rows could not be saved
                if (!rolesResponse?.success) {
                    throw new Error(rolesResponse?.error_message || "Unknown role update error");
                }

                // Save the compatible image rows after the role transaction has completed
                const imagesResponse = await updatePodDesignProductImages(
                    token,
                    designOrTemplate,
                    podDesignDetails.id,
                    safeConfiguredProductId,
                    buildProductImagesApiPayload(nextImages)
                );

                // Restore the previous role rows if the image replacement failed
                if (!imagesResponse?.success) {

                    // Put the previous role set back so the database does not retain a half-applied edit
                    await updatePodDesignProductRoles(
                        token,
                        designOrTemplate,
                        podDesignDetails.id,
                        safeConfiguredProductId,
                        buildConfiguredProductRolesPayload(currentRoles)
                    );

                    // Report the original image replacement failure
                    throw new Error(imagesResponse?.error_message || "Unknown image update error");

                }

            } catch (error) {

                // Re-enable the controls after a failed save
                setSavingRoleViewSettingsTargetId("");

                // Resume polling if this product was already waiting for mockups
                if (currentImages.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id)) {
                    setProductImagesPollTargetId(safeConfiguredProductId);
                }

                // Show the save failure
                toastError({
                    title: "Error",
                    message: "Could not save the product print area: " + (error?.message || "Unknown error"),
                    autoCloseMs: 6000,
                });

                return;

            }

        }

        // Update the exact configured parent or child after the replacement calls have succeeded
        setCurrentProducts((previousDetails) => ({
            ...previousDetails,
            products: replaceConfiguredProductRolesAndImages(
                previousDetails?.products,
                safeConfiguredProductId,
                nextRoles,
                nextImages
            ),
        }));

        // Re-enable the role view controls
        setSavingRoleViewSettingsTargetId("");

        // Poll only when the compatible selection includes pending background jobs
        if (nextImages.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id)) {
            setProductImagesPollTargetId(safeConfiguredProductId);
        }

    }, [
        savingRoleViewSettingsTargetId,
        podDesignDetails?.products,
        podDesignDetails?.id,
        token,
        designOrTemplate,
        setCurrentProducts,
        toastError,
    ]);


    // SHOULD PROBABLY PUT SOMETHING IN HERE FOR SPLITTING MOCK UP IMAGES PER ROLE

    

    // Save a placement and queue every mockup image affected by that placement
    const handleSavePlacement = useCallback(async (saveResult) => {

        // A placement editor initialisation with no edit has no target to save
        if (!saveResult?.target) { return true; }

        // Read the fresh product snapshot produced by saveTransformDataToProductRole
        const nextProducts = Array.isArray(saveResult?.nextProducts)
            ? saveResult.nextProducts
            : [];

        // Stop when there is no usable product snapshot
        if (nextProducts.length === 0) {

            toastError({
                title: "Error",
                message: "Could not save the placement because the updated product data was unavailable.",
                autoCloseMs: 6000,
            });

            return false;

        }

        // Read the configured parent product id
        const parentConfiguredProductId = String(saveResult?.target?.configuredProductId || "");

        // Read the edited role key
        const targetRole = String(saveResult?.target?.role || "");

        // Stop when the placement target cannot be identified
        if (!parentConfiguredProductId || !targetRole) {

            toastError({
                title: "Error",
                message: "Could not identify the configured product placement being saved.",
                autoCloseMs: 6000,
            });

            return false;

        }

        // Find the configured parent product
        const configuredParentProduct = getConfiguredProductById(
            nextProducts,
            parentConfiguredProductId
        );

        // Stop when the configured parent product cannot be found
        if (!configuredParentProduct) {

            toastError({
                title: "Error",
                message: "Could not find the configured product being edited.",
                autoCloseMs: 6000,
            });

            return false;

        }

        // Start with the configured parent as the directly edited product
        let editedConfiguredProduct = configuredParentProduct;

        // Find the configured child when the placement belongs to a child
        if (saveResult?.target?.isChild) {

            // Read configured children safely
            const configuredChildren = Array.isArray(configuredParentProduct?.children)
                ? configuredParentProduct.children
                : [];

            // Find the exact child from the saved transform target
            editedConfiguredProduct = configuredChildren.find((childItem) => {
                return String(childItem?.pod_product_sku || childItem?.sku || "") === String(saveResult?.target?.childSku || "");
            }) || null;

        }

        // Stop when the directly edited parent or child cannot be found
        if (!editedConfiguredProduct) {

            toastError({
                title: "Error",
                message: "Could not find the configured parent or child placement being edited.",
                autoCloseMs: 6000,
            });

            return false;

        }

        // Read the exact configured id of the directly edited parent or child
        const editedConfiguredProductId = String(
            editedConfiguredProduct?.id ||
            editedConfiguredProduct?.client_configured_product_id ||
            ""
        );

        // Stop when the directly edited configured product has no identity
        if (!editedConfiguredProductId) {

            toastError({
                title: "Error",
                message: "Could not identify the configured parent or child product.",
                autoCloseMs: 6000,
            });

            return false;

        }

        // Start with the directly edited configured product
        const affectedConfiguredProducts = [
            editedConfiguredProduct,
        ];

        // A parent placement also affects children which inherit this role placement
        if (!saveResult?.target?.isChild) {

            // Read configured children safely
            const configuredChildren = Array.isArray(configuredParentProduct?.children)
                ? configuredParentProduct.children
                : [];

            // Add children which have not locked this role with their own edited placement
            configuredChildren.forEach((childItem) => {

                // Read child roles safely
                const childRoles = Array.isArray(childItem?.roles)
                    ? childItem.roles
                    : [];

                // Find this role on the child
                const matchingChildRole = childRoles.find((roleItem) => {
                    return String(roleItem?.role || "") === targetRole;
                }) || null;

                // Children with their own edited placement are not affected by the parent change
                if (String(matchingChildRole?.edited || "N").toUpperCase() === "Y") {
                    return;
                }

                // Add the inheriting child to the regeneration list
                affectedConfiguredProducts.push(childItem);

            });

        }

        // Build the pending image arrays for every affected configured product
        const affectedImageTargets = affectedConfiguredProducts.map((configuredProduct) => {

            // Read the exact configured product id
            const configuredProductId = String(
                configuredProduct?.id ||
                configuredProduct?.client_configured_product_id ||
                ""
            );

            // Read the selected image rows safely
            const currentImages = Array.isArray(configuredProduct?.images)
                ? configuredProduct.images
                : [];

            // Return this configured product and its pending replacement rows
            return {
                configuredProductId: configuredProductId,
                images: buildPendingMockupImageRows(currentImages),
            };

        }).filter((targetItem) => targetItem.configuredProductId);

        // Stop the current poll while role and image rows are being replaced
        setProductImagesPollTargetId("");

        // Disable the placement popup controls
        setSavingPlacement(true);

        // Check whether this design/template and configured product already exist in the database
        const canSaveImmediately =
            /^\d+$/.test(String(podDesignDetails?.id || "")) &&
            /^\d+$/.test(editedConfiguredProductId);

        try {

            // Existing configured products must save the role before exposing background jobs
            if (canSaveImmediately) {

                // Read the complete updated role set from the directly edited product
                const editedRoles = Array.isArray(editedConfiguredProduct?.roles)
                    ? editedConfiguredProduct.roles
                    : [];

                // Save the complete role array first
                const rolesResponse = await updatePodDesignProductRoles(
                    token,
                    designOrTemplate,
                    podDesignDetails.id,
                    editedConfiguredProductId,
                    buildConfiguredProductRolesPayload(editedRoles)
                );

                // Stop if the configured role rows could not be saved
                if (!rolesResponse?.success) {
                    throw new Error(rolesResponse?.error_message || "Unknown role update error");
                }

                // Replace the image rows for every affected existing parent or child
                for (const affectedTarget of affectedImageTargets) {

                    // Unsaved temporary configured products are handled by the main design save
                    if (!/^\d+$/.test(affectedTarget.configuredProductId)) {
                        continue;
                    }

                    // Skip products which have no selected mockup rows
                    const hasMockupRows = affectedTarget.images.some((imageRow) => {
                        return Boolean(imageRow?.mockup_id);
                    });

                    if (!hasMockupRows) {
                        continue;
                    }

                    // Save completed manual images and pending mockup jobs
                    const imagesResponse = await updatePodDesignProductImages(
                        token,
                        designOrTemplate,
                        podDesignDetails.id,
                        affectedTarget.configuredProductId,
                        buildProductImagesApiPayload(affectedTarget.images)
                    );

                    // Stop if any affected image set could not be replaced
                    if (!imagesResponse?.success) {
                        throw new Error(imagesResponse?.error_message || "Unknown image update error");
                    }

                }

            }

            // Apply the pending image rows to the fresh placement product snapshot
            let nextProductsWithPendingImages = nextProducts;

            affectedImageTargets.forEach((affectedTarget) => {

                // Replace only this configured parent or child image array
                nextProductsWithPendingImages = replaceConfiguredProductImages(
                    nextProductsWithPendingImages,
                    affectedTarget.configuredProductId,
                    affectedTarget.images
                );

            });

            // Start with the locally updated hierarchy for unsaved products or refresh failures
            let refreshedProducts = nextProductsWithPendingImages;

            // Refresh saved products so API-derived print quality reflects the new placement
            if (canSaveImmediately) {

                try {

                    // Get the authoritative configured product hierarchy
                    const productsResponse = await getPodDesignProducts(
                        token,
                        designOrTemplate,
                        podDesignDetails.id
                    );

                    // Use the refreshed products when the API returned them successfully
                    if (productsResponse?.success && Array.isArray(productsResponse?.results)) {

                        refreshedProducts = productsResponse.results;

                    } else {

                        // The placement is saved but its derived print quality could not be refreshed
                        toastError({
                            title: "Error",
                            message: "The placement was saved but the updated print quality could not be refreshed.",
                            autoCloseMs: 6000,
                        });

                    }

                } catch (error) {

                    // The placement is saved but its derived print quality could not be refreshed
                    toastError({
                        title: "Error",
                        message: "The placement was saved but the updated print quality could not be refreshed.",
                        autoCloseMs: 6000,
                    });

                }

            }

            // Store the updated placement and authoritative print quality together
            setCurrentProducts((previousDetails) => ({
                ...previousDetails,
                products: refreshedProducts,
            }));

            // Find the directly edited product's pending image rows
            const editedImageTarget = affectedImageTargets.find((affectedTarget) => {
                return affectedTarget.configuredProductId === editedConfiguredProductId;
            }) || null;

            // Poll the product currently being edited while its mockups are processed
            if (
                /^\d+$/.test(editedConfiguredProductId) &&
                editedImageTarget?.images?.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id)
            ) {
                setProductImagesPollTargetId(editedConfiguredProductId);
            }

            // Re-enable the placement popup
            setSavingPlacement(false);

            // Tell the popup that saving completed successfully
            return true;

        } catch (error) {

            // Re-enable the placement popup
            setSavingPlacement(false);

            // Show the placement or queue failure
            toastError({
                title: "Error",
                message: "Could not save the product placement: " + (error?.message || "Unknown error"),
                autoCloseMs: 6000,
            });

            // Keep the placement popup open
            return false;

        }

    }, [
        podDesignDetails?.id,
        token,
        designOrTemplate,
        setCurrentProducts,
        toastError,
    ]);

    // Reset one edited print-area placement and save the change immediately
    const handleResetPrintAreaPlacement = useCallback(({
        product: targetProduct = null,
        child: targetChild = null,
        isChild = false,
        role = "",
        roleName = "",
        currentRole = null,
    }) => {

        // Do not start another placement save while one is already running
        if (savingPlacement) { return; }

        // Read the exact role key
        const safeRole = String(role || "");

        // Read the parent product sku
        const productSku = String(targetProduct?.sku || "");

        // Stop if the placement target cannot be identified
        if (!safeRole || !productSku || !currentRole) {

            toastError({
                title: "Error",
                message: "Could not identify the print area placement to reset.",
                autoCloseMs: 6000,
            });

            return;

        }

        // Start without a child sku
        let childSku = "";

        // Read the child sku when resetting a child placement
        if (isChild) {
            childSku = String(targetChild?.sku || "");
        }

        // Read a friendly print area name
        let safeRoleName = String(roleName || "").trim();

        // Use the role key if there is no friendly name
        if (!safeRoleName) {
            safeRoleName = safeRole;
        }

        // Build the parent confirmation message
        let confirmationMessage = `<h3>Reset ${safeRoleName}?</h3><br/>This will clear the edited placement for this print area and make it inherit from the design again.<br/><br/>Any child products which inherit this print area will have their mockups regenerated.`;

        // Build the child confirmation message
        if (isChild) {
            confirmationMessage = `<h3>Reset ${safeRoleName}?</h3><br/>This will clear the edited placement for this child print area and make it inherit from the parent product again.`;
        }

        // Ask before clearing the placement override
        DisplayConfirmationDialog(
            confirmationMessage,
            "Reset Print Area",
            "Cancel",
            async function (is_confirmed) {

                // Leave the placement unchanged when cancelled
                if (!is_confirmed) { return; }

                // Build the same reset payload used by the placement editor Reset action
                const resetTransform = {
                    changeSource: "reset_to_default",
                    product_sku: productSku,
                    child_sku: childSku,
                    role: safeRole,
                    active: String(currentRole?.active || "Y"),
                    edited: "N",
                    edited_background: "N",
                    edited_position: "N",
                    background_colour: null,
                    position: null,
                };

                // Remove the placement override from the live configured product
                const saveResult = saveTransformDataToProductRole(resetTransform);

                // Save the role and regenerate every affected mockup
                const saveSucceeded = await handleSavePlacement(saveResult);

                // Stop when the API save failed
                if (!saveSucceeded) { return; }

                // Clear any stale transform left from a previous placement editor session
                setLatestTransform(null);

            }
        );

    }, [
        savingPlacement,
        toastError,
        saveTransformDataToProductRole,
        handleSavePlacement,
        setLatestTransform,
    ]);


    // Refresh one configured product's generated mockups through the API
    const handleRegenerateConfiguredProductMockups = useCallback(async ({
        configuredProductId = "",
        reselect = false
    }) => {

        // Read the exact configured product id
        const safeConfiguredProductId = String(configuredProductId || "");

        // Stop when this is not an existing saved configured product
        if (!/^\d+$/.test(safeConfiguredProductId)) { return; }

        // Ask the API to rebuild only the generated mockup rows
        const imageResponse = await refreshPodDesignProductMockups(
            token,
            designOrTemplate,
            podDesignDetails?.id,
            safeConfiguredProductId,
            reselect
        );

        // Stop on error
        if (!imageResponse?.success) {

            toastError({
                title: "Error",
                message: "Could not queue the product mockups: " + (imageResponse?.error_message || "Unknown error"),
                autoCloseMs: 6000,
            });

            return;

        }

        // Read the complete refreshed image list returned by the API
        const refreshedImages = Array.isArray(imageResponse?.results?.images)
            ? imageResponse.results.images
            : [];

        // Update only the exact configured parent or child locally
        setCurrentProducts((previousDetails) => ({
            ...previousDetails,
            products: replaceConfiguredProductImages(
                previousDetails?.products,
                safeConfiguredProductId,
                refreshedImages
            ),
        }));

        // Poll while generated mockups are waiting for Reaper
        if (
            refreshedImages.some((imageRow) => {
                return imageRow?.mockup_id && !imageRow?.image_id;
            })
        ) {
            setProductImagesPollTargetId(safeConfiguredProductId);
        }

    }, [
        token,
        designOrTemplate,
        podDesignDetails?.id,
        setCurrentProducts,
        toastError,
    ]);

    // Regenerate configured products whose automatic count or effective mockup colourways changed
    const refreshChangedMockupsAfterSave = async (saveResult) => {

        // Track products already refreshed so an explicit Refresh click does not do them twice
        const refreshedConfiguredProductIds = [];

        // Nothing to regenerate without a successful save result
        if (!saveResult) { return refreshedConfiguredProductIds; }

        // Designs controlled by a template do not own their local product mockups
        if (
            designOrTemplate === "design" &&
            designUsesTemplates(podDesignDetails)
        ) {
            return refreshedConfiguredProductIds;
        }

        // Templates only need their image candidate rows rebuilt for colourway changes
        const changedConfiguredProductIds =
            designOrTemplate === "template"
                ? [
                    ...(saveResult.changedMockupColourwayIds || []),
                ]
                : [
                    ...(saveResult.changedAutoMockupIds || []),
                    ...(saveResult.changedMockupColourwayIds || []),
                ];

        // Track design products whose automatic count changed so they need a fresh selection
        const reselectConfiguredProductIds = new Set(
            designOrTemplate === "design"
                ? (saveResult.changedAutoMockupIds || []).map((configuredProductId) => String(configuredProductId))
                : []
        );

        // A product can appear in both lists when count and colourway changed together
        const uniqueConfiguredProductIds =
            Array.from(
                new Set(
                    changedConfiguredProductIds.map(
                        (configuredProductId) =>
                            String(configuredProductId)
                    )
                )
            );

        // Refresh every affected configured parent or child once
        for (
            const configuredProductId
            of uniqueConfiguredProductIds
        ) {

            // Resolve the exact freshly-saved configured product
            const configuredProduct =
                getConfiguredProductById(
                    [saveResult.savedProductFamily],
                    configuredProductId
                );

            // Skip products which disappeared from the saved family
            if (!configuredProduct) { continue; }

            // Rebuild its generated mockups using the freshly-saved configuration
            await handleRegenerateConfiguredProductMockups({
                configuredProductId: configuredProductId,
                reselect: reselectConfiguredProductIds.has(String(configuredProductId)),
            });

            // Remember that this product was already refreshed
            refreshedConfiguredProductIds.push(
                String(configuredProductId)
            );

        }

        return refreshedConfiguredProductIds;

    };


    // Save deferred product changes and regenerate mockups affected by count or colourway changes
    const handleSaveProductAndRefreshMockups = async () => {

        // Save the current product family first
        const saveResult = await handleSaveProduct();

        // Stop when the save failed
        if (!saveResult) { return; }

        // Refresh configured products whose automatic count or effective colourways changed
        await refreshChangedMockupsAfterSave(
            saveResult
        );

    };


    // Save any dirty product fields before explicitly refreshing one configured product
    const handleRefreshProductMockups = async ({
        configuredProductId = ""
    }) => {

        // Normalise the requested configured product
        const safeConfiguredProductId =
            String(configuredProductId || "");

        // Refresh only existing saved configured products
        if (!/^\d+$/.test(safeConfiguredProductId)) { return; }

        // Do nothing while another save/close operation is underway
        if (productSaveInProgress || closingProduct) { return; }

        // Save any deferred fields first, including Auto Mockups
        const saveResult = await handleSaveProduct();

        // Stop when the save failed
        if (!saveResult) { return; }

        // A changed count or effective colourway may already have caused this product to regenerate
        const refreshedConfiguredProductIds =
            await refreshChangedMockupsAfterSave(
                saveResult
            );

        // Do not immediately reshuffle the same product for a second time
        if (
            refreshedConfiguredProductIds.includes(
                safeConfiguredProductId
            )
        ) {
            return;
        }

        // Explicit Refresh always selects a fresh automatic mockup set
        await handleRegenerateConfiguredProductMockups({
            configuredProductId: safeConfiguredProductId,
            reselect: true,
        });

    };

    // Save selected image rows immediately so Reaper can process pending mockups
    const handleSaveProductImages = async (selectedImages, { manualSelectionChanged = false } = {}) => {

        // Make sure we have a popup target
        if (!productImagesPopupTarget) { return; }

        // Ordinary design image edits imply manual mode; template image edits only change the automatic candidate pool
        const switchToManualSelection =
            manualSelectionChanged && designOrTemplate === "design";

        // Read the configured image target id
        const popupConfiguredProductId = String(productImagesPopupTarget.configuredProductId || "");

        // Stop when the target cannot be identified
        if (!popupConfiguredProductId) { return; }

        // Build display rows while preserving pending exact mockup selections
        const displayImages = Array.isArray(selectedImages)
            ? selectedImages.map((imageRow, imageIndex) => {

                // Build a clean local image row
                const displayImage = {
                    ordering: imageIndex + 1,
                    source: imageRow?.source === "S" ? "S" : "U",
                };

                // Preserve an existing image id
                if (imageRow?.image_id) {
                    displayImage.image_id = Number(imageRow.image_id);
                }

                // Preserve the exact mockup configuration, including pending rows without an image id
                if (imageRow?.mockup_id) {
                    displayImage.mockup_id = Number(imageRow.mockup_id);
                    displayImage.aspect = String(imageRow?.aspect || "").trim().toUpperCase();
                    displayImage.rotation = Number(imageRow.rotation);
                }

                // Preserve current display URLs when present
                if (imageRow?.image_url) {
                    displayImage.image_url = imageRow.image_url;
                }

                if (imageRow?.thumbnail_url) {
                    displayImage.thumbnail_url = imageRow.thumbnail_url;
                }

                // Return the local image row
                return displayImage;

            }).filter((imageRow) => imageRow.image_id || imageRow.mockup_id)
            : [];

        // Existing configured products can create their image queue rows immediately
        if (
            /^\d+$/.test(popupConfiguredProductId) &&
            /^\d+$/.test(String(podDesignDetails?.id || ""))
        ) {

            // Save the complete current configured roles before exposing background image jobs
            const rolesResponse = await updatePodDesignProductRoles(
                token,
                designOrTemplate,
                podDesignDetails.id,
                popupConfiguredProductId,
                buildConfiguredProductRolesPayload(productImagesPopupTarget.configuredRoles)
            );

            // Stop if the configured roles could not be saved
            if (!rolesResponse?.success) {
                toastError({
                    title: "Error",
                    message: "Could not save the product print areas: " + (rolesResponse?.error_message || "Unknown error"),
                    autoCloseMs: 6000,
                });

                return;
            }

            // Build the normal image replacement payload
            const imagePayload =
                buildProductImagesApiPayload(
                    displayImages
                );

            // Ordinary design image edits also switch this configured product into manual image-selection mode
            let imageRequestPayload = imagePayload;

            if (switchToManualSelection) {
                imageRequestPayload = {
                    images: imagePayload,
                    manual_selection: true,
                };
            }

            // Save completed images and pending mockup jobs through the replacement endpoint
            const imagesResponse = await updatePodDesignProductImages(
                token,
                designOrTemplate,
                podDesignDetails.id,
                popupConfiguredProductId,
                imageRequestPayload
            );

            // Stop if the image rows could not be saved
            if (!imagesResponse?.success) {
                toastError({
                    title: "Error",
                    message: "Could not save the product images: " + (imagesResponse?.error_message || "Unknown error"),
                    autoCloseMs: 6000,
                });

                return;
            }

        }

        // Update only the exact parent or child target in the live design state
        setCurrentProducts((previousDetails) => {

            // Store the saved image rows first
            let nextProducts =
                replaceConfiguredProductImages(
                    previousDetails?.products,
                    popupConfiguredProductId,
                    displayImages
                );

            // An ordinary design image edit switches this configured product to manual selection mode
            if (switchToManualSelection) {
                nextProducts =
                    replaceConfiguredProductAutoMockupCount(
                        nextProducts,
                        popupConfiguredProductId,
                        0
                    );
            }

            return {
                ...previousDetails,
                products: nextProducts,
            };

        });

        // The image endpoint saved ordinary design manual mode immediately, so update the saved effective-count baseline
        if (switchToManualSelection) {

            setSavedAutoMockupCounts((previousCounts) => {

                // Start with the existing saved state so unrelated unsaved overrides remain dirty
                const nextCounts = {
                    ...previousCounts,
                    [popupConfiguredProductId]: {
                        effective: 0,
                        inherited: "N",
                    },
                };

                // Check whether the manually-edited target is this configured parent
                const parentConfiguredProductId =
                    String(
                        liveCurrentProduct?.id ||
                        liveCurrentProduct?.client_configured_product_id ||
                        ""
                    );

                if (parentConfiguredProductId === popupConfiguredProductId) {

                    // The parent's new zero value also becomes the effective value of inheriting children
                    const childProducts =
                        Array.isArray(liveCurrentProduct?.children)
                            ? liveCurrentProduct.children
                            : [];

                    childProducts.forEach((childItem) => {

                        // Explicit child overrides do not follow the parent
                        if (String(childItem?.auto_mockup_count_inherited || "N").trim().toUpperCase() !== "Y") {
                            return;
                        }

                        // Read the configured child identity
                        const childConfiguredProductId =
                            String(
                                childItem?.id ||
                                childItem?.client_configured_product_id ||
                                ""
                            );

                        if (!childConfiguredProductId) { return; }

                        // The child remains inherited, but its saved effective value is now zero
                        nextCounts[childConfiguredProductId] = {
                            effective: 0,
                            inherited: "Y",
                        };

                    });

                }

                return nextCounts;

            });

        }

        // Poll saved configured products while Reaper is filling missing image ids
        if (
            /^\d+$/.test(popupConfiguredProductId) &&
            displayImages.some((imageRow) => imageRow?.mockup_id && !imageRow?.image_id)
        ) {
            setProductImagesPollTargetId(popupConfiguredProductId);
        }

        // Close Manage Images after the queue rows have been saved
        setProductImagesPopupTarget(null);

    };



    // This picks the sku no matter which shape the product uses
    const sku = product?.sku ?? product?.pod_product_sku;

    // This stores the configured product identity for exact parent/child updates
    const configuredProductId = String(currentProduct?.id || currentProduct?.client_configured_product_id || "");

    // Helper data for placeholder #x.y# replacement
    const placeholderData = {
        design: {
            title: formValues.translations?.find((translation) => translation.language === chosenLanguage)?.title ?? "",
        },
    };

    // Create a ref for the div
    const childRowRef = useRef(null);

    // Focus and scroll the active child row after render
    useEffect(() => {

        // Guard against missing element
        if (!childRowRef.current) {

            return;

        }

        // Scroll the row into view
        childRowRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
        });

        // Focus the row
        childRowRef.current.focus();

    }, [activeChildSku]);



    // Get the current selected store connection
    const selectedStoreConnection = connectionsData?.find((connection) => connection.id === formValues.store_id) || null;



    // Get the store alias so we can click to view the product on the store
    const storeAlias = selectedStoreConnection?.alias;



    // Calculate API-backed inline price display values for this product popup
    useEffect(() => {

        // Stop when the popup is closed
        if (!isOpen) {

            return;

        }

        // Stop when there is no token
        if (!token) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Stop when there is no selected store
        if (!selectedStoreConnection?.id) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Stop when there is no design set id
        if (!podDesignDetails?.design_set_id) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Read reference data safely
        const safeFulfilmentCountries = Array.isArray(fulfilmentCountries)
            ? fulfilmentCountries
            : Array.isArray(fulfilmentCountries?.results)
                ? fulfilmentCountries.results
                : [];

        // Read country reference data safely
        const safeCountries = Array.isArray(countries)
            ? countries
            : Array.isArray(countries?.results)
                ? countries.results
                : [];

        // Read subscription level reference data safely
        const safeSubscriptionLevels = Array.isArray(subscriptionLevels)
            ? subscriptionLevels
            : Array.isArray(subscriptionLevels?.results)
                ? subscriptionLevels.results
                : [];

        // Wait for fulfilment country reference data before making money calculations
        if (safeFulfilmentCountries.length === 0) {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }

        // Wait for core country reference data before resolving store currency
        if (safeCountries.length === 0) {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }
 
        // Wait for the user's subscription level and level reference data
        if (String(userData?.subscription_level || "").trim() === "" || safeSubscriptionLevels.length === 0) {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }

        // Wait for the store home country before resolving the mock order destination
        if (String(storeHomeCountry || "").trim() === "") {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }

        // Get available shipping countries for this store
        const availableShippingCountries = getAvailableShippingCountries(selectedStoreConnection);

        // Wait for shipping country configuration
        if (availableShippingCountries.length === 0) {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }

        // Get the default store country name
        const defaultStoreCountryName = safeFulfilmentCountries.find((country) => {
            return String(country?.code || "").toUpperCase() === String(storeHomeCountry || "").toUpperCase();
        })?.name || "";

        // Stop when the store home country cannot be resolved
        if (!defaultStoreCountryName) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Start with no selected shipping country
        let selectedShippingCountry = "";

        // Prefer the store home country when it is available
        if (availableShippingCountries.includes(defaultStoreCountryName)) {

            selectedShippingCountry = defaultStoreCountryName;

        }

        // Fall back to the first available shipping country only after shipping configuration is loaded
        if (!selectedShippingCountry) {

            selectedShippingCountry = availableShippingCountries[0] || "";

        }

        // Stop when no mock shipping country can be resolved
        if (!selectedShippingCountry) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Get available shipping methods for this country
        const availableShippingMethods = getShippingMethodsForCountry({
            selectedStoreConnection,
            selectedShippingCountry,
        });

        // Wait for shipping methods to be available
        if (availableShippingMethods.length === 0) {

            setProductPricePreviewStatus("loading");
            setProductPricePreviewValues({});
            return;

        }

        // Pick the first available method
        const selectedShippingMethod = availableShippingMethods[0] || null;

        // Stop when no mock shipping method can be resolved
        if (!selectedShippingMethod?.code) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Read the store currency setting
        const currenciesSetting = selectedStoreConnection?.settings?.find((setting) => {
            return setting.type === "Pricing" && setting.code === "currencies";
        });

        // Build selected currency list
        const selectedCurrencyCodes = String(currenciesSetting?.value || "")
            .split(",")
            .map((currencyCode) => currencyCode.trim())
            .filter(Boolean);

        // Resolve the store default currency from loaded country reference data
        let selectedCurrencyCode = getStoreDefaultCurrency({
            storeHomeCountry,
            coreCountries: safeCountries,
        });

        // Use a store-enabled currency only
        if (selectedCurrencyCodes.length > 0 && !selectedCurrencyCodes.includes(selectedCurrencyCode)) {

            selectedCurrencyCode = selectedCurrencyCodes[0];

        }

        // Stop when no store currency can be resolved from loaded reference data
        if (!selectedCurrencyCode) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Read whether this canonical product has children
        const productHasChildren = Array.isArray(product?.children) && product.children.length > 0;

        // Build the mock venue order id
        const venueOrderId = Date.now();

        // Build the rows we want to price
        const pricePreviewRows = [];

        // Build child rows when this is a parent/child product
        if (productHasChildren) {

            // Read configured children safely
            const configuredChildren = Array.isArray(liveCurrentProduct?.children) ? liveCurrentProduct.children : [];

            configuredChildren.forEach((configuredChild, index) => {

                // Resolve the canonical child product
                const canonicalChild = product.children.find((childItem) => {
                    return String(childItem?.sku || "") === String(configuredChild?.pod_product_sku || configuredChild?.sku || "");
                }) || null;

                // Resolve the store sku and POD product sku
                const storeSku = String(configuredChild?.sku || "").trim();
                const podProductSku = String(configuredChild?.pod_product_sku || canonicalChild?.sku || "").trim();

                // Skip rows that cannot be sent to the mock order endpoint
                if (!storeSku || !podProductSku) {

                    return;

                }

                // Resolve VAT from the canonical child home country price row
                const childHomeCountryPricing = product?.homeCountryPricing?.children?.find((childPriceRow) => {
                    return String(childPriceRow?.sku || "") === String(podProductSku);
                });

                // Read the saved price config for this child
                const priceConfig = getPriceConfigFromProductPrices({
                    productPrices: configuredChild?.prices || [],
                    fallbackPriceConfig: {
                        mode: "D",
                        value: null,
                    },
                    resolvedVatRate: getResolvedVatRateFromPriceRows(childHomeCountryPricing?.prices || []),
                });

                // Build venue order item id
                const venueOrderItemId = venueOrderId + index + 1;

                // Store the row for the mock order
                pricePreviewRows.push({
                    key: getProductPricePreviewKey({
                        storeSku,
                        podProductSku,
                    }),
                    venueOrderItemId,
                    orderItem: buildEarningsPreviewOrderItem({
                        currencyCode: selectedCurrencyCode,
                        storeSku,
                        podProductSku,
                        designSetId: podDesignDetails.design_set_id,
                        priceConfig,
                        colourwayCode: configuredChild?.default_colourway || "",
                        venueOrderItemId,
                    }),
                });

            });

        } else {

            // Resolve the standalone store sku and POD product sku
            const storeSku = String(liveCurrentProduct?.sku || "").trim();
            const podProductSku = String(liveCurrentProduct?.pod_product_sku || product?.sku || "").trim();

            // Add the standalone row when it can be sent to the mock order endpoint
            if (storeSku && podProductSku) {

                // Read the saved price config for this product
                const priceConfig = getPriceConfigFromProductPrices({
                    productPrices: liveCurrentProduct?.prices || [],
                    fallbackPriceConfig: {
                        mode: "D",
                        value: null,
                    },
                    resolvedVatRate: getResolvedVatRateFromPriceRows(product?.homeCountryPricing?.prices || []),
                });

                // Build venue order item id
                const venueOrderItemId = venueOrderId + 1;

                // Store the row for the mock order
                pricePreviewRows.push({
                    key: getProductPricePreviewKey({
                        storeSku,
                        podProductSku,
                    }),
                    venueOrderItemId,
                    orderItem: buildEarningsPreviewOrderItem({
                        currencyCode: selectedCurrencyCode,
                        storeSku,
                        podProductSku,
                        designSetId: podDesignDetails.design_set_id,
                        priceConfig,
                        colourwayCode: liveCurrentProduct?.default_colourway || "",
                        venueOrderItemId,
                    }),
                });

            }

        }

        // Stop when there are no rows to price
        if (pricePreviewRows.length === 0) {

            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});
            return;

        }

        // Track whether these requests are still active
        let requestIsActive = true;

        // Mark display values as loading
        setProductPricePreviewStatus("loading");
        setProductPricePreviewValues({});

        // Build one API request per price row so order-level product earning belongs to exactly one SKU
        const previewRequests = pricePreviewRows.map((pricePreviewRow, index) => {

            // Build a unique mock order id for this row
            const rowVenueOrderId = venueOrderId + ((index + 1) * 1000);

            // Build the one-line mock order payload
            const rowOrderPayload = {
                connid: selectedStoreConnection.id,
                venueorderid: rowVenueOrderId,
                deliverycountry: selectedShippingCountry,
                billingcountry: selectedShippingCountry,
                venueshippingmethod: selectedShippingMethod.code,
                currency: selectedCurrencyCode,
                status: "mock",
                orderitems: [
                    pricePreviewRow.orderItem,
                ],
            };

            // Call the API for this one row
            return calculateOrderEarnings(token, rowOrderPayload).then((response) => {

                // Return the row and its response together
                return {
                    pricePreviewRow,
                    response,
                };

            }).catch((error) => {

                // Return the row with a failed response
                return {
                    pricePreviewRow,
                    response: null,
                    error,
                };

            });

        });

        // Call the API once per row
        Promise.all(previewRequests).then((previewResponses) => {

            // Stop when this response is stale
            if (!requestIsActive) {

                return;

            }

            // Build the next display values
            const nextPreviewValues = {};

            // Map each one-line mock response back to the product or child row
            previewResponses.forEach((previewResponse) => {

                // Read this row
                const pricePreviewRow = previewResponse.pricePreviewRow;

                // Read this response
                const response = previewResponse.response;

                // Skip failed responses
                if (!response?.success) {

                    return;

                }

                // Read response order items safely
                const responseOrderItems = Array.isArray(response?.results?.orderitems) ? response.results.orderitems : [];

                // Find the matching API order item
                let matchedOrderItem = responseOrderItems.find((orderItem) => {
                    return String(orderItem?.venueorderitemid || "") === String(pricePreviewRow.venueOrderItemId || "");
                }) || null;

                // Fall back to sku matching
                if (!matchedOrderItem) {

                    matchedOrderItem = responseOrderItems.find((orderItem) => {
                        return String(orderItem?.sku || "") === String(pricePreviewRow?.orderItem?.sku || "");
                    }) || null;

                }

                // Stop when the response did not include this order item
                if (!matchedOrderItem) {

                    return;

                }

                // Store display values for this product row
                nextPreviewValues[pricePreviewRow.key] = getMockOrderItemDisplayValues({
                    orderItem: matchedOrderItem,
                    orderResults: response.results,
                    currentPlanLevel: userData.subscription_level,
                    subscriptionLevels: safeSubscriptionLevels,
                    currencyCode: selectedCurrencyCode,
                });

            });

            // Store the display values
            setProductPricePreviewValues(nextPreviewValues);
            setProductPricePreviewStatus(Object.keys(nextPreviewValues).length > 0 ? "ready" : "unavailable");

        }).catch(() => {

            // Stop when this response is stale
            if (!requestIsActive) {

                return;

            }

            // Clear values on failure
            setProductPricePreviewStatus("unavailable");
            setProductPricePreviewValues({});

        });

        // Prevent stale responses updating state
        return () => {

            requestIsActive = false;

        };

    }, [isOpen, 
        token, 
        selectedStoreConnection, 
        podDesignDetails.design_set_id, 
        fulfilmentCountries, 
        storeHomeCountry, 
        countries, 
        product, 
        productPricingSignature, 
        userData.subscription_level, 
        subscriptionLevels, 
        liveCurrentProduct.children, 
        liveCurrentProduct?.sku, 
        liveCurrentProduct?.pod_product_sku, 
        liveCurrentProduct?.prices, 
        liveCurrentProduct?.default_colourway
    ]);


    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;

    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--large-inner">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            {productTitleWithoutPlaceholders}

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            {currentProduct?.sku ? `SKU: ${currentProduct.sku}` : ""}

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* If this is a saved design and there is a current product sku then show button to open the product on the store */}
                        {!isMobile ?
                        
                            currentProduct?.sku && currentProduct?.sku !== "" ?

                                <OpenStoreButton alias={`${storeAlias}/product/${currentProduct.sku}`} buttonTitle={"Open Product on Store"} disabled={false} />

                                :

                                <span className="warning-text">Save to view product on store.</span>

                                : ""

                        }

                        {/* This controls marketplace publication for the configured product family */}
                        <ToggleSwitch
                            title={currentProduct?.marketplace === "Y" ? "Remove Product From Marketplace" : "Publish Product To Marketplace"}
                            value={currentProduct?.marketplace === "Y"}
                            label="Marketplace"
                            disabled={designUsesTemplates(podDesignDetails)}
                            onChange={(event) => {

                                handleProductHeaderToggle("marketplace", event.target.value);

                            }}
                        />

                        {/* This controls whether the configured product family appears in the normal store */}
                        <ToggleSwitch
                            title={currentProduct?.store === "N" ? "Show Product On Store" : "Hide Product From Store"}
                            value={currentProduct?.store !== "N"}
                            label="Store"
                            disabled={designUsesTemplates(podDesignDetails)}
                            onChange={(event) => {

                                handleProductHeaderToggle("store", event.target.value);

                            }}
                        />

                        {/* This controls whether the configured product family is active */}
                        <ToggleSwitch
                            title={currentProduct?.active === "Y" ? "Disable Product" : "Enable Product"}
                            value={currentProduct?.active === "Y"}
                            label="Active"
                            disabled={designUsesTemplates(podDesignDetails)}
                            onChange={(event) => {

                                handleProductHeaderToggle("active", event.target.value);

                            }}
                        />

                        {/* This is the close button */}
                        <button
                            type="button"
                            className="popup__close"
                            onClick={handleCloseProduct}
                            disabled={productSaveInProgress || closingProduct}
                            title="Close"
                        >
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content">

                    <div className="pod-design__product-details">

                        <div className="productModal__body">

                            {product.children?.length > 0 && 
                            
                                <div className="productModal__parent-child-list">

                                    <div className="card">

                                        <div className="card__header">

                                            <h3 className="card__title">
                                                Products
                                            </h3>

                                        </div>

                                        <div className="productModal__parent-child-list--header">
                                            Parent:
                                        </div>

                                        <div 
                                            className={`productModal__parent-child-list--item ${activeChildSku === "parent" && "productModal__parent-child-list--item-active"} productModal__parent-child-list--parent`} 
                                            onClick={() => {
                                                setViewChildrenQuickEditTable(false);
                                                setActiveChildSku("parent"); 
                                            }}
                                        >

                                            {/* Keep warning space reserved for consistent parent/child alignment */}
                                            <div
                                                className="productModal__parent-child-list--quality-slot"
                                                onClick={(event) => event.stopPropagation()}
                                            >

                                                {parentPrintQualityStatus &&

                                                    <InfoButton
                                                        buttonIcon={
                                                            <WarningIcon
                                                                className={`printQualityWarningIcon printQualityWarningIcon--${parentPrintQualityStatus}`}
                                                            />
                                                        }
                                                        shortLabel="Print quality warning"
                                                        description={
                                                            parentPrintQualityStatus === "fail"
                                                                ? "One or more print areas on this product are below the minimum image resolution."
                                                                : "One or more print areas on this product are below the recommended image resolution."
                                                        }
                                                    />

                                                }

                                            </div>

                                            <div className="productModal__parent-child-list--item-label">

                                                {/* Parent: {liveCurrentProduct?.pod_product_sku || product.sku} */}
                                                {productTitleWithoutPlaceholders}

                                            </div>
                                            
                                        </div>

                                        <div className="productModal__parent-child-list--children">

                                            <div className="productModal__parent-child-list--header">
                                                Children:
                                            </div>

                                            {product.children.map((child) => {

                                                // Find the configured version of this canonical child
                                                const configuredChildren =
                                                    Array.isArray(liveCurrentProduct?.children)
                                                        ? liveCurrentProduct.children
                                                        : [];

                                                const configuredChild =
                                                    configuredChildren.find((configuredChildItem) => {
                                                        return String(configuredChildItem?.pod_product_sku || "") === String(child?.sku || "");
                                                    }) || null;

                                                // Calculate this child's own PPI state rather than the family-wide state
                                                const childPrintQualityStatus =
                                                    getConfiguredProductPrintQualityStatus(configuredChild);

                                                return (

                                                    <div 
                                                        key={child.sku} 
                                                        className={`
                                                            productModal__parent-child-list--item ${activeChildSku === child.sku && "productModal__parent-child-list--item-active"} 
                                                            productModal__parent-child-list--child
                                                            `
                                                        } 
                                                        style={{opacity: child.Active === "N" ? "0.7" : "1" }}
                                                        onClick={() => {
                                                            setViewChildrenQuickEditTable(false);
                                                            setActiveChildSku(child.sku); 
                                                        }}
                                                    >

                                                        {/* Always reserve warning space so every variant label remains aligned */}
                                                        <div
                                                            className="productModal__parent-child-list--quality-slot"
                                                            onClick={(event) => event.stopPropagation()}
                                                        >

                                                            {childPrintQualityStatus &&

                                                                <InfoButton
                                                                    buttonIcon={
                                                                        <WarningIcon
                                                                            className={`printQualityWarningIcon printQualityWarningIcon--${childPrintQualityStatus}`}
                                                                        />
                                                                    }
                                                                    shortLabel="Print quality warning"
                                                                    description={
                                                                        childPrintQualityStatus === "fail"
                                                                            ? "One or more print areas on this variant are below the minimum image resolution."
                                                                            : "One or more print areas on this variant are below the recommended image resolution."
                                                                    }
                                                                />

                                                            }

                                                        </div>

                                                        <div className="productModal__parent-child-list--item-label">

                                                            {child.venues[0]?.attributes?.map((attribute) => {

                                                                return (

                                                                    <div key={`${attribute.name}-${attribute.label}`}>
                                                                        {attribute.name}: {attribute.value}
                                                                    </div>

                                                                );

                                                            })}

                                                        </div>

                                                        {child.Active === "N" && 

                                                            <span>❌</span>

                                                        }
                                                                                                                
                                                    </div>

                                                );

                                            })}

                                        </div>

                                        {/* Open the combined child-product editor. */}
                                        <div className="productModal__view-all-children">

                                            <CreateButton
                                                title="View All Children"
                                                size="s"
                                                fullWidth={true}
                                                onClick={() => {

                                                    setActiveChildSku(null);
                                                    setViewChildrenQuickEditTable(true);

                                                }}
                                            />

                                        </div>

                                    </div>

                                </div>
                            
                            }






                            {/* (!activeChildSku && activeChildSku === "parent") || product.children?.length === 0 ) && */}
                            {(activeChildSku === "parent" || product.children?.length === 0 || !product.children) &&

                                <div className="productModal__parent-product productModal__leftWrap">
                                
                                    <div className="productModal__left">

                                        <div className="card productModal__imagesPanel">

                                            <div className="card__header">

                                                <h3 className="card__title">Images</h3>

                                                <span className="hintText">Live views and product images</span>

                                            </div>

                                            <div className="card__content productModal__imagesPanelContent">

                                                {/* Image carousel of mock up images */}
                                                <ProductDetailsImageCarousel
                                                    images={safeMockups}
                                                    numberOfPlaceHolders={0}
                                                    parentProduct={product}
                                                    parentOrChildProduct={liveCurrentProduct}
                                                    designRoles={podDesignDetails?.roles}
                                                />

                                                <CreateButton
                                                    type="button"
                                                    title="Manage Images"
                                                    size="s"
                                                    fullWidth={true}
                                                    disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                                    onClick={() => {

                                                        // Open the product images popup for the parent product
                                                        setProductImagesPopupTarget({
                                                            configuredProductId: liveCurrentProduct?.id || liveCurrentProduct?.client_configured_product_id || "",
                                                            title: "Manage Product Images",
                                                            canonicalProduct: product,
                                                            configuredRoles: Array.isArray(liveCurrentProduct?.roles) ? liveCurrentProduct.roles : [],
                                                            images: Array.isArray(liveCurrentProduct?.images) ? liveCurrentProduct.images : [],
                                                        });

                                                    }}
                                                />

                                                <div className="productModal__imageControls">

                                                    <div className="productModal__autoMockups">

                                                        <label>Auto Mockups</label>

                                                        <ToggleSwitch
                                                            name={"auto_mockups_" + String(liveCurrentProduct?.id || liveCurrentProduct?.client_configured_product_id || "parent")}
                                                            value={parentAutoMockupsEnabled}
                                                            onChange={(event) => {

                                                                // Turning Auto Mockups off stores zero; turning it back on restores the normal default count
                                                                handleAutoMockupCountChange({
                                                                    configuredProductId: liveCurrentProduct?.id || liveCurrentProduct?.client_configured_product_id || "",
                                                                    value: event.target.value ? 3 : 0,
                                                                });

                                                            }}
                                                            label=""
                                                            disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                                            title={parentAutoMockupsEnabled ? "Turn off automatic mockups" : "Turn on automatic mockups"}
                                                            size="s"
                                                        />

                                                        {parentAutoMockupsEnabled &&

                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                value={currentParentAutoMockupCount}
                                                                onChange={(event) => {

                                                                    // Store the automatic mockup count on this configured parent
                                                                    handleAutoMockupCountChange({
                                                                        configuredProductId: liveCurrentProduct?.id || liveCurrentProduct?.client_configured_product_id || "",
                                                                        value: event.target.value,
                                                                    });

                                                                }}
                                                                disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                                            />

                                                        }

                                                        <span className="hintText">
                                                            {parentAutoMockupsEnabled
                                                                ? "per rotation"
                                                                : "Uses all selected images"}
                                                        </span>

                                                    </div>

                                                    <CreateButton
                                                        type="button"
                                                        title="Refresh"
                                                        tooltip="Saves any Auto Mockups changes, then selects and regenerates a fresh random set."
                                                        size="s"
                                                        disabled={
                                                            designUsesTemplates(podDesignDetails)
                                                            || productSaveInProgress
                                                            || closingProduct
                                                            || !/^\d+$/.test(String(liveCurrentProduct?.id || ""))
                                                        }
                                                        onClick={() => {

                                                            // Select a fresh automatic mockup set and queue it for generation
                                                            handleRefreshProductMockups({
                                                                configuredProductId: liveCurrentProduct?.id || ""
                                                            });

                                                        }}
                                                    />

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                    <div className="productModal__right">

                                        <ProductDetailsData
                                            currentProduct={liveCurrentProduct}
                                            setCurrentProducts={setCurrentProducts}
                                            product={product}
                                            sku={sku}
                                            configuredProductId={configuredProductId}
                                            chosenLanguage={chosenLanguage}
                                            designRoles={podDesignDetails.roles}
                                            placeholderData={placeholderData}
                                            storeHomeCountry={storeHomeCountry}
                                            podDesignDetails={podDesignDetails}
                                            productPricePreviewValues={productPricePreviewValues}
                                            productPricePreviewStatus={productPricePreviewStatus}
                                        />

                                    </div>

                                    <div className="productModal__printAreas">

                                        {/* Table of roles - can be toggled and edited (image placement editor) */}
                                        <PrintAreasPanel
                                            productRoles={liveCurrentProduct?.roles}
                                            selectedRoles={selectedRoles}
                                            title={"Print Areas"}
                                            printAreaRoles={printAreaRoles}
                                            product={product}
                                            podDesignDetails={podDesignDetails}
                                            currentProduct={liveCurrentProduct}
                                            configuredProductId={configuredProductId}
                                            handleOpenImagePlacementEditor={handleOpenImagePlacementEditor}
                                            onRoleViewSettingsChange={handleRoleViewSettingsChange}
                                            savingRoleViewSettingsTargetId={savingRoleViewSettingsTargetId}
                                            onResetPlacement={handleResetPrintAreaPlacement}
                                            savingPlacement={savingPlacement}
                                        />

                                    </div>

                                </div>

                            }


                            {activeChildSku && activeChildSku !== "parent" &&

                                <div
                                    key={activeChildSku}
                                    className=""
                                >
                                    <ChildDetailsDrawer
                                        child={activeChild}
                                        currentProduct={liveCurrentProduct}
                                        configuredProductId={configuredProductId}
                                        setCurrentProducts={setCurrentProducts}
                                        product={product}
                                        sku={sku}
                                        chosenLanguage={chosenLanguage}
                                        printAreaRoles={printAreaRoles}
                                        selectedRoles={selectedRoles}
                                        podDesignDetails={podDesignDetails}
                                        handleOpenImagePlacementEditor={handleOpenImagePlacementEditor}
                                        onRoleViewSettingsChange={handleRoleViewSettingsChange}
                                        savingRoleViewSettingsTargetId={savingRoleViewSettingsTargetId}
                                        onResetPlacement={handleResetPrintAreaPlacement}
                                        savingPlacement={savingPlacement}
                                        handleRefreshProductMockups={handleRefreshProductMockups}
                                        handleAutoMockupCountChange={handleAutoMockupCountChange}
                                        storeHomeCountry={storeHomeCountry}
                                        placeholderData={placeholderData}
                                        productPricePreviewValues={productPricePreviewValues}
                                        productPricePreviewStatus={productPricePreviewStatus}
                                        setProductImagesPopupTarget={setProductImagesPopupTarget}
                                    />
                                </div>

                            }


                            {viewChildrenQuickEditTable && 
                            
                                //* This is the children table so that can be quickly edited */}
                                <div
                                    
                                >

                                    {/* This is the main children list panel */}
                                    <div className="productModal__rightMain">

                                        {/* This is the children card */}
                                        <div className="card">

                                            {/* This is the children header */}
                                            <div className="card__header card__header--sticky">

                                                {/* This is the header title */}
                                                <div className="childrenHeader">

                                                    {/* This is the title */}
                                                    <h3 className="card__title">Children</h3>

                                                    {/* This is the count */}
                                                    <span className="childrenHeader__count">
                                                        {product.children.length} variants
                                                    </span>

                                                </div>

                                                {/* This is header actions */}
                                                <div className="childrenHeader__actions">

                                                    {/* This is a bulk toggle */}
                                                    <CreateButton
                                                        title="Enable all"
                                                        size="xs"
                                                        plain={true}
                                                        type="button"
                                                        className="btn btn--ghost"
                                                        onClick={() => handleToggleAllChildren(true, product)}
                                                    />

                                                    {/* This is a bulk toggle */}
                                                    <CreateButton
                                                        title="Disable all"
                                                        size="xs"
                                                        plain={true}
                                                        type="button"
                                                        className="btn btn--ghost"
                                                        onClick={() => handleToggleAllChildren(false, product)}
                                                    />

                                                </div>

                                            </div>

                                            {/* This is the children list */}
                                            <div className="childrenList">

                                                {product.children.map((child) => {

                                                    const designProductChild = currentProduct?.children?.find((c) => c.pod_product_sku === child.sku) || [];
                                                    const childTranslations = child.venues?.[0]?.translations?.find((trans) => trans?.language === chosenLanguage);
                                                    const attributes = child.venues?.[0]?.attributes;
                                                    const safeChildRoles = Array.isArray(designProductChild.roles) ? designProductChild.roles : [];
                                                    const designRoles = Array.isArray(podDesignDetails.roles) ? podDesignDetails.roles : [];

                                                    // Get the list of role names for the pricing - this will be an array like ["exterior_front_main", "exterior_back_main"]
                                                    const printAreasToIncludeInPricing = (() => {

                                                        // Build a quick lookup of roles that are explicitly disabled on the current child product
                                                        const inactiveRoleSet = new Set(
                                                            safeChildRoles
                                                                .filter((productRole) => productRole.active === "N")
                                                                .map((productRole) => productRole.role)
                                                        );

                                                        // Use the design roles but exclude anything the current product has explicitly disabled
                                                        return (designRoles || [])
                                                            .filter((designRole) => !inactiveRoleSet.has(designRole.role))
                                                            .map((designRole) => designRole.role);

                                                    })();

                                                    // Helper to replace placeholders #x.y# - this creates a new object with placeholders replaced
                                                    const formattedTranslations = ReplacePlaceholdersInObject(childTranslations, placeholderData);

                                                    // Get the child prices array
                                                    const homeCountryPricingChild = product?.homeCountryPricing?.children?.find((c) => c.sku === child.sku);

                                                    return (

                                                        <div
                                                            key={child.sku}
                                                            ref={child.sku === activeChildSku ? childRowRef : null}
                                                            tabIndex={-1}
                                                        >

                                                            <div
                                                                className="childrenTable childrenTable--row"
                                                            >

                                                                {/* This is the image cell */}
                                                                <div className="childrenTable__col childrenTable__col--thumb">

                                                                    {designRoles.map((roleItem) => {

                                                                        const currentDesignRole = roleItem || null;
                                                                        const currentProductRole = currentProduct?.roles?.find((r) => r?.role === roleItem?.role) || null;
                                                                        const currentProductChildRole = safeChildRoles?.find((r) => r?.role === roleItem?.role) || null;

                                                                        // Only use child placement data when this child role has been edited directly
                                                                        const currentProductChildPreviewRole = roleHasEditedPlacement(currentProductChildRole) ? currentProductChildRole : null;

                                                                        // Use child fit/fill only when this child role has been edited directly, otherwise inherit from the parent
                                                                        const currentProductPreviewRole = currentProductChildPreviewRole || currentProductRole;

                                                                        // Use the child's configured role settings, falling back to the parent role when needed
                                                                        let configuredRoleForView = currentProductRole;

                                                                        if (currentProductChildRole) {
                                                                            configuredRoleForView = currentProductChildRole;
                                                                        }

                                                                        // Get the first live view allowed by this configured child product role
                                                                        const firstConfiguredView = getFirstConfiguredProductPreviewView(
                                                                            child,
                                                                            configuredRoleForView,
                                                                            roleItem?.role
                                                                        );

                                                                        // Check if this product has a matching role
                                                                        const displayRole = hasMatchingRole(child, roleItem);

                                                                        // Return early if there's no matching product role
                                                                        if (!displayRole) {

                                                                            return null;

                                                                        }

                                                                        // Do not substitute an unrelated canonical or custom-product view
                                                                        if (!firstConfiguredView) { return null; }

                                                                        return (

                                                                            <div key={roleItem.role}>

                                                                                <PodProductPreview
                                                                                    thedesign={currentDesignRole}
                                                                                    product={product.custom_product}
                                                                                    child={child?.custom_product}
                                                                                    currentProductRoleData={currentProductRole}
                                                                                    currentProductChildRoleData={currentProductChildPreviewRole}
                                                                                    mockup_height={60}
                                                                                    options={{
                                                                                        display_mode: "product",
                                                                                        product_fit_fill: currentProductPreviewRole?.fit_fill ? currentProductPreviewRole.fit_fill : null,
                                                                                        fit_or_fill: currentProductPreviewRole?.fit_fill ? currentProductPreviewRole.fit_fill : null,
                                                                                        surface_code: firstConfiguredView.surface_code || "default",
                                                                                        print_area_code: firstConfiguredView.print_area_code || "default",
                                                                                        view_style: firstConfiguredView.style || "default",
                                                                                        view_name: firstConfiguredView.name || "",
                                                                                        selected_colourway_code: designProductChild?.default_colourway || "",
                                                                                    }}
                                                                                />

                                                                            </div>

                                                                        );

                                                                    })}

                                                                    {designRoles.length === 0 &&
                                                                        <div style={{ width: "60px", height: "60px" }}>
                                                                            <img
                                                                                src={getCanonicalProductMockups(child)?.[0]?.mockup_thumbnail_url}
                                                                                alt={child.sku}
                                                                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                                                                            />
                                                                        </div>
                                                                    }

                                                                </div>

                                                                {/* This is the name cell */}
                                                                <div className="childrenTable__col childrenTable__col--name">

                                                                    <div className="childName" onClick={(e) => e.stopPropagation()}>

                                                                        <input
                                                                            className="fieldRow__textarea"
                                                                            value={designProductChild.title ? designProductChild.title : formattedTranslations?.title || ""}
                                                                            placeholder="Title"
                                                                            onChange={(e) => {

                                                                                updateChildProductField({
                                                                                    configuredProductId: configuredProductId,
                                                                                    childSku: child.sku,
                                                                                    key: "title",
                                                                                    value: e.target.value,
                                                                                });

                                                                            }}
                                                                            disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                                                        />

                                                                    </div>

                                                                </div>

                                                                {/* This is the tags cell */}
                                                                <div className="childrenTable__col childrenTable__col--tags">

                                                                    <div className="pillRow">

                                                                        {attributes?.map((attribute) =>

                                                                            <Pill key={`${attribute.name}-${attribute.value}`}>
                                                                                {attribute.name}: {attribute.value}
                                                                            </Pill>

                                                                        )}

                                                                    </div>

                                                                </div>

                                                                {/* This is the price cell */}
                                                                <div className="childrenTable__col childrenTable__col--price" onClick={(e) => e.stopPropagation()}>

                                                                    <ProductPriceEditor
                                                                        sku={sku}
                                                                        childSku={child.sku}
                                                                        storeSku={designProductChild?.sku || ""}
                                                                        currentPlanLevel={userData.subscription_level}
                                                                        subscriptionLevels={subscriptionLevels}
                                                                        defaultPrices={homeCountryPricingChild?.prices || []}
                                                                        //prices={child.venues[0]?.prices}
                                                                        productPrices={designProductChild.prices || []}
                                                                        coreCurrencies={currencies}
                                                                        storeHomeCountry={storeHomeCountry}
                                                                        //initialSalePrice={(designProductChild?.price && designProductChild.price !== "0.00") ? designProductChild?.price : child.venues[0]?.Price}
                                                                        selectedPrintAreas={printAreasToIncludeInPricing}
                                                                        vatRate={child.venues[0]?.VATrate}
                                                                        shippingDestinations={fulfilmentCountries}
                                                                        initialUserTakesPayment={userData.paypal_merchant_id ? true : false}
                                                                        rateCard={rateCard}
                                                                        onPriceConfigChange={(nextSalePrice) => {

                                                                            // This updates price
                                                                            updateChildProductField({
                                                                                configuredProductId: configuredProductId,
                                                                                childSku: child.sku,
                                                                                key: "prices",
                                                                                value: nextSalePrice,
                                                                            });

                                                                        }}
                                                                        displayInfo={false}
                                                                        disablePriceEditor={designUsesTemplates(podDesignDetails) ? true : false}
                                                                        podDesignDetails={podDesignDetails}
                                                                        defaultColourwayCode={designProductChild?.default_colourway || ""}
                                                                        apiDisplayValues={
                                                                            productPricePreviewValues[
                                                                                getProductPricePreviewKey({
                                                                                    storeSku: designProductChild?.sku || "",
                                                                                    podProductSku: designProductChild?.pod_product_sku || child.sku,
                                                                                })
                                                                            ] || null
                                                                        }
                                                                        apiDisplayStatus={productPricePreviewStatus}
                                                                    />

                                                                </div>

                                                                {/* This is the active cell */}
                                                                <div className="childrenTable__col childrenTable__col--active">

                                                                    <ToggleSwitch
                                                                        value={designProductChild.active ? S2B(designProductChild.active) : false}
                                                                        size="s"
                                                                        label=""
                                                                        disabled={designUsesTemplates(podDesignDetails) ? true : false}
                                                                        onChange={(e) => {

                                                                            const activeValue = B2S(e.target.value);

                                                                            updateChildProductField({
                                                                                configuredProductId: configuredProductId,
                                                                                childSku: child.sku,
                                                                                key: "active",
                                                                                value: activeValue,
                                                                            });

                                                                        }}
                                                                    />

                                                                </div>

                                                            </div>

                                                        </div>

                                                    );

                                                })}

                                            </div>

                                            

                                            {/* This is a footer */}
                                            <div className="card__footer card__footer--split">

                                                <div className="footerLeft">

                                                    <span className="hintText">
                                                        Tip: edit titles and prices inline, then click on the child in the list for more options.
                                                    </span>

                                                </div>

                                                <div className="footerRight"></div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            }

                        </div>

                    </div>

                </div>

                {/* Footer actions */}
                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                    <div></div>

                    <div className="horizontal-align-content-center">

                        {(productHasChanged || savingProduct) &&

                            <CreateButton
                                type="button"
                                title={savingProduct ? "Saving..." : "Save changes"}
                                size="m"
                                onClick={handleSaveProductAndRefreshMockups}
                                disabled={productSaveInProgress || closingProduct}   
                            />

                        }

                        <CreateButton
                            type="button"
                            title={closingProduct ? "Closing..." : "Close"}
                            isCancel={true}
                            invert
                            size="m"
                            onClick={handleCloseProduct}
                            disabled={productSaveInProgress || closingProduct}
                        />

                    </div>

                </div>

            </div>

            {productImagesPopupTarget &&

                <ProductImagesPopup
                    isOpen={true}
                    title={productImagesPopupTarget.title}
                    canonicalProduct={productImagesPopupTarget.canonicalProduct}
                    configuredRoles={productImagesPopupTarget.configuredRoles}
                    designRoles={podDesignDetails?.roles}
                    images={productImagesPopupTarget.images}
                    disabled={designUsesTemplates(podDesignDetails) ? true : false}
                    onClose={() => setProductImagesPopupTarget(null)}
                    onSave={handleSaveProductImages}
                />

            }

            {/* This renders the image placement popup as a global overlay (not relative to the drawer) */}
            {placementEditorTarget &&

                <ImagePlacementPopup
                    title={placementEditorTarget.title}
                    subTitle={placementEditorTarget.subTitle}
                    onClose={() => {

                        // This closes the editor
                        setPlacementEditorTarget(null);

                    }}
                    onSave={async (saveResult) => {

                        // Save the configured role and create replacement background jobs
                        const saveSucceeded = await handleSavePlacement(saveResult);

                        // Keep the placement editor open when saving failed
                        if (!saveSucceeded) { return; }

                        // Close the editor after the role and queue rows have been saved
                        setPlacementEditorTarget(null);

                    }}
                    product={placementEditorTarget.product}
                    child={placementEditorTarget.child}
                    isChild={placementEditorTarget.isChild}
                    role={placementEditorTarget.role}
                    currentProductRole={placementEditorTarget.currentProductRole}
                    latestTransform={latestTransform}
                    setLatestTransform={setLatestTransform}

                    currentProduct={liveCurrentProduct}
                    podDesignDetails={podDesignDetails}
                    saveTransformDataToProductRole={saveTransformDataToProductRole}
                    saving={savingPlacement}
                />

            }

        </div>


    )

}; export default ProductDetailsPopUp;
